/** biome-ignore-all lint/suspicious/noShadowRestrictedNames: no need to use shadow restricted names */
/** biome-ignore-all lint/suspicious/useIterableCallbackReturn: pre-existing `.map()` callsites used for side effects; refactor to `.forEach()` is out of FAC-4861 scope. */
import camelcaseKeys from "camelcase-keys";
import Decimal from "decimal.js-light";
import { fromJS } from "immutable";
import { flattenDeep, isNaN, orderBy } from "lodash";
import { evaluate, round as mathjsRound } from "mathjs";
import { nanoid } from "nanoid";
import snakecaseKeys from "snakecase-keys";

import { cancelOnGoinApiRequest } from "@/api/common";
import { getAccountingItemLabel } from "@/utils/accounting";
import { convertMmToFeet } from "@/utils/measurement/measurement-conversion";
import {
  getCustomFormulaQuantity,
  getLinealFeetQuantityInFeet,
  getLinealFeetQuantityInMm,
  getLinealMetresQuantityInMetres,
  getLinealMetresQuantityInMm,
  getQuantityByPricingStrategy,
  getSquareFeetQuantityInSquareFeet,
  getSquareFeetQuantityInSquareMeter,
  getSquareMetresQuantityInSquareMetres,
  getSumLengthsByPricingStrategy,
} from "@/utils/measurement/measurement-for-price-computation";

import * as additionalProductsApi from "../../api/additional-products";
import * as priceLevelApi from "../../apiv2/priceLevels";
import * as productKitsApi from "../../apiv2/productKits";
import * as productsApi from "../../apiv2/products";
import { isNew, isNumeric, isPositiveNumber } from "../../utils/common";
import {
  NOTES_TYPES_TO_INT,
  PRICING_STRATEGY,
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_FEET,
  PRICING_STRATEGY_LINEAL_FEET_LABEL,
  PRICING_STRATEGY_LINEAL_METRES,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_FEET,
  PRICING_STRATEGY_SQUARE_FEET_LABEL,
  PRICING_STRATEGY_SQUARE_METRES,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRICING_STRATEGY_STRING_TO_INT,
  PRODUCT_TYPE_ADDITIONAL_PRODUCT,
  PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
  PRODUCT_TYPE_LABOUR_ITEM,
  PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
  PRODUCT_TYPE_NOTES_ITEM,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
  PRODUCT_TYPE_ON_THE_FLY_PRODUCT,
  PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_LABEL,
  PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
  PRODUCT_TYPES,
} from "../../utils/constants";
import { handleError } from "../utils/error";

import ReduxModule from "./abstract/ReduxModule";
import alerts from "./alerts";
import { createFormData } from "./helpers/common";
import {
  getMinLengthLinealFeet,
  getMinLengthProducts,
  sortByMaterialName,
} from "./helpers/product";
import { withMutations } from "./helpers/reducerWrappers";
import orders from "./orders";

Decimal.config({ rounding: Decimal.ROUND_HALF_UP });

const toStateValue = (value) => (value === null || value === undefined ? null : `${value}`);

// FAC-5897: a labour line item's "actual" means hours actually worked. A brand new labour row
// has had nobody work on it yet, so its actual starts at zero and only moves when someone logs
// time against the line or types an actual by hand. Every other product type starts with its
// actual equal to its estimate, because a material is committed the moment it is quoted.
//
// Kept as a string: it is read back through Decimal(), which throws on null, and it has to
// survive snakecaseKeys on its way to the API.
const LABOUR_INITIAL_ACTUAL_QUANTITY = "0";

const initialActualQuantity = (productType, quantity) =>
  productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL ? LABOUR_INITIAL_ACTUAL_QUANTITY : quantity;

// FAC-5897: duplicating a row copies every field off the source, including the two that record
// how the source's actual was arrived at. Nobody has worked on the copy, so those reset too --
// otherwise the row would claim tracked hours it never had, and break the invariant that
// actualQuantity = totalTrackedTime + customActualQuantity.
const initialLabourTracking = (productType) =>
  productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL
    ? { totalTrackedTime: "0.0000", customActualQuantity: "0.0000" }
    : {};

// FAC-5896: an estimated-quantity edit must not destroy an actual quantity the user entered.
// An actual that still equals the estimate has never been set by anyone — it is only tracking
// the estimate (a new line item starts with both equal), and cost/margin are computed from the
// actual, so it must keep following. Once the user moves the actual away from the estimate,
// their number is real data and the estimate must leave it alone.
const isActualTrackingEstimate = (quantity, actualQuantity) => {
  if (actualQuantity === null || actualQuantity === undefined || actualQuantity === "") { return true; }
  if (quantity === null || quantity === undefined || quantity === "") { return false; }
  try {
    return Decimal(actualQuantity).equals(Decimal(quantity));
  } catch (_) {
    return false;
  }
};

const ACTIONS = {
  ADD_ADDITIONAL_PRODUCT: "Add additional product",
  ADD_ON_THE_FLY_PRODUCT_KIT: "Add on the fly product kit",
  ADD_ON_THE_FLY_SUB_KIT: "Add on the fly sub kit",
  ADD_PRODUCT_KIT_AS_SUB_KIT: "Add product kit as sub kit",
  REMOVE_SUB_KITS: "Remove sub kits",
  ADD_ON_THE_FLY_PRODUCT_ITEM: "Add on the fly product item",
  ADD_NOTES_LINE_ITEM: "Add notes line item",
  ADD_LABOUR_LINE_ITEM: "Add labour line item",
  EDIT_LABOUR_LINE_ITEM: "Edit Labour line item",
  ADD_PRODUCT_PRICE_LEVEL: "Add product price level",
  CHANGE_PRICE_VALUE: "Change product price value",
  CHANGE_PRODUCT_QUANTITY: "Change product quantity",
  CHANGE_PRODUCT_ACTUAL_QUANTITY: "Change product actual quantity",
  CHANGE_PRODUCT_ACTUAL_QUANTITY_INVENTORY: "Change product actual quantity inventory",
  CHANGE_PRODUCT_KIT_QUANTITY: "Change product kit quantity",
  CHANGE_SUB_KIT_QUANTITY: "Change sub kit quantity",
  CHANGE_PRODUCT_KIT_ACTUAL_QUANTITY: "Change product kit actual quantity",
  CHANGE_PRODUCT_KIT_ACTUAL_QUANTITY_INVENTORY: "Change product kit actual quantity inventory",
  CHANGE_SUB_KIT_ACTUAL_QUANTITY: "Change sub kit actual quantity",
  CHANGE_SUB_KIT_ACTUAL_QUANTITY_INVENTORY: "Change sub kit actual quantity inventory",
  CHANGE_PRODUCT_IS_TAX_FREE: "Change product is tax free",
  CHANGE_PRODUCT_KIT_ITEM_IS_TAX_FREE: "Change product kit item is tax free",
  CHANGE_SUB_KIT_ITEM_IS_TAX_FREE: "Change sub kit item is tax free",
  CHANGE_PRODUCT_COMPLETED_STATUS: "Change product completed status",
  CHANGE_PRODUCT_KIT_ITEM_COMPLETED_STATUS: "Change product kit item completed status",
  CHANGE_SUB_KIT_ITEM_COMPLETED_STATUS: "Change sub kit item completed status",
  CHANGE_SUB_KIT_COMPLETED_STATUS: "Change sub kit completed status",
  CHANGE_PRODUCT_KIT_DESCRIPTION: "Change product kit description",
  CHANGE_PRODUCT_KIT_ITEM_QUANTITY: "Change product kit item quantity",
  CHANGE_SUB_KIT_ITEM_QUANTITY: "Change sub kit item quantity",
  CHANGE_PRODUCT_KIT_ITEM_ACTUAL_QUANTITY: "Change product kit item actual quantity",
  CHANGE_PRODUCT_KIT_ITEM_ACTUAL_QUANTITY_INVENTORY:
    "Change product kit item actual quantity inventory",
  CHANGE_SUB_KIT_ITEM_ACTUAL_QUANTITY: "Change sub kit item actual quantity",
  CHANGE_SUB_KIT_ITEM_ACTUAL_QUANTITY_INVENTORY: "Change sub kit item actual quantity inventory",
  CHANGE_PRODUCT_KIT_ITEM_NAME: "Change product kit item name",
  CHANGE_SUB_KIT_ITEM_NAME: "Change sub kit item name",
  CHANGE_PRODUCT_KIT_ITEM_TYPE: "Change product kit item type",
  CHANGE_SUB_KIT_ITEM_TYPE: "Change sub kit item type",
  CHANGE_PRODUCT_KIT_ITEM_PRICE_VALUE: "Change product kit item price value",
  CHANGE_SUB_KIT_ITEM_PRICE_VALUE: "Change sub kit item price value",
  CHANGE_SUB_KIT_PRICE_VALUE: "Change sub kit price value",
  CHANGE_PRODUCT_KIT_ITEM_NOTES: "Change product kit item notes",
  CHANGE_SUB_KIT_ITEM_NOTES: "Change sub kit item notes",
  CHANGE_PRODUCT_KIT_ITEM_DESCRIPTION: "Change product kit item description",
  CHANGE_SUB_KIT_ITEM_DESCRIPTION: "Change sub kit item description",
  CHANGE_PRODUCT_NAME: "Change product name",
  CHANGE_SUB_KIT_TITLE: "Change sub kit title",
  CHANGE_PRODUCT_NOTES: "Change product notes",
  CHANGE_PRODUCT_DESCRIPTION: "Change product description",
  CHANGE_PRODUCT_TYPE: "Change product type",
  CHANGE_ITEM_ORDER_INDEX: "Change item or product kit order index",
  CHANGE_PRODUCT_KIT_ITEM_ORDER_INDEX: "Change product kit item order index",
  CHANGE_SUB_KIT_ITEM_ORDER_INDEX: "Change sub kit item order index",
  CHANGE_SUB_KIT_ORDER_INDEX: "Change sub kit order index",
  SAVE_PRODUCT_TO_CATALOGUE: "Save product to catalogue",
  SAVE_PRODUCT_KIT_TO_CATALOGUE: "Save product kit to catalogue",
  SAVE_KIT_ITEM_TO_CATALOGUE: "Save kit item to catalogue",
  SAVE_SUB_KIT_ITEM_TO_CATALOGUE: "Save sub kit item to catalogue",
  CLEAR_ACTIVE_ORDER: "Clear active order",
  CREATE_NEW_ORDER: "Create new order",
  DELETE_PRODUCT_LENGTH: "Delete product length",
  DELETE_PRODUCT_ACTUAL_LENGTH: "Delete product actual length",
  DELETE_PRODUCT_KIT_ITEM_LENGTH: "Delete product kit item length",
  DELETE_SUB_KIT_ITEM_LENGTH: "Delete sub kit item length",
  DELETE_PRODUCT_KIT_ITEM_ACTUAL_LENGTH: "Delete product kit item actual length",
  DELETE_SUB_KIT_ITEM_ACTUAL_LENGTH: "Delete sub kit item actual length",
  IS_DUPLICATING_PRODUCT: "Is duplicating product",
  DUPLICATE_ADDITIONAL_PRODUCT: "Duplicate product price level",
  DUPLICATE_KIT_ITEM: "Duplicate kit item on order",
  DUPLICATE_KIT: "Duplicate kit",
  DUPLICATE_SUB_KIT_ITEM: "Duplicate sub kit item on order",
  DUPLICATE_PRODUCT: "Duplicate product",
  EDIT_ADDITIONAL_PRODUCT: "Edit additional product",
  GET_CATEGORIES: "Get categories",
  GET_PRODUCT: "Get product",
  OPEN_ORDER: "Open order",
  REMOVE_KIT_ITEM_FROM_ORDER: "Remove kit item from order",
  REMOVE_SUB_KIT_ITEM_FROM_ORDER: "Remove sub kit item from order",
  REMOVE_PRODUCT_FROM_ORDER: "Remove product from order",
  DELETE_SUB_KIT_FROM_ORDER: "Remove sub kit from order",
  RESET_ORDER_PRODUCTS: "Reset order products",
  SAVE_EDIT_ADDITIONAL_PRODUCT: "save edit additional product",
  SEARCH_PRODUCTS: "Get all products",
  SEARCH_PRODUCTS_BY_CATEGORY: "Get all products for a category",
  SET_UID: "Set uid",
  SET_KIT_ITEM_UID: "Set kit item uid",
  SET_UID_OF_CUSTOM_FORMULA: "Set uid of custom formula",
  SET_UID_OF_KIT_ITEM_CUSTOM_FORMULA: "Set uid of kit item custom formula",
  SET_UIDS: "Set uids",
  SET_SEARCH_VALUE: "Set search value",
  SET_ACTIVE_TAB: "Set active tab",
  SET_ACTIVE_PRODUCT: "Set active product",
  SORT_BY_CATEGORY: "Sort by category",
  UPDATE_UID_OF_CUSTOM_FORMULA: "Update uid of custom formula",
  CHANGE_CUSTOM_FORMULA_VALUES: "Update custom formula values",
  CHANGE_ACTUAL_CUSTOM_FORMULA_VALUES: "Update actual custom formula values",
  ADD_CUSTOM_FORMULA_VALUE: "Add custom formula value",
  REMOVE_CUSTOM_FORMULA_VALUE: "Remove custom formula value",
  ADD_ITEM_TO_KIT: "Add item to kit item on order",
  ADD_ON_THE_FLY_ITEM_TO_KIT: "Add on the fly product to kit item on order",
  ADD_NOTES_ITEM_TO_KIT: "Add on the notes to kit item on order",
  ADD_LABOUR_LINE_ITEM_TO_KIT: "Add labour item to kit on order",
  ADD_ITEM_TO_SUB_KIT: "Add item to sub kit item on order",
  ADD_ON_THE_FLY_ITEM_TO_SUB_KIT: "Add on the fly product to sub kit item on order",
  ADD_NOTES_ITEM_TO_SUB_KIT: "Add on the notes to sub kit item on order",
  ADD_LABOUR_LINE_ITEM_TO_SUB_KIT: "Add labour item to sub kit on order",
  SELECT_XERO_TRACKING_CATEGORY: "Select xero tracking category to line item",
  SELECT_XERO_TRACKING_CATEGORY_TO_SUB_KIT: "Select xero tracking category to sub kit",
  SELECT_XERO_TRACKING_CATEGORY_TO_KIT_ITEM: "Select xero tracking category to kit item",
  SELECT_XERO_TRACKING_CATEGORY_TO_SUB_KIT_ITEM: "Select xero tracking category to sub kit item",
  APPLY_XERO_TRACKING_CATEGORY_TO_ALL_LINE_ITEMS: "Apply Xero tracking category to all line items",
  SELECT_MYOB_JOB: "Select myob job to line item",
  SELECT_MYOB_JOB_TO_SUB_KIT: "Select myob job to sub kit",
  SELECT_MYOB_JOB_TO_KIT_ITEM: "Select myob job to kit item",
  SELECT_MYOB_JOB_TO_SUB_KIT_ITEM: "Select myob job to sub kit item",
  APPLY_MYOB_JOB_TO_ALL_LINE_ITEMS: "Apply Myob Job to all line items",
  SELECT_MYOB_ACCOUNT: "Select myob account to line item",
  SELECT_MYOB_ACCOUNT_TO_KIT_ITEM: "Select myob account to kit item",
  SELECT_MYOB_ACCOUNT_TO_SUB_KIT: "Select myob account to sub kit",
  SELECT_MYOB_ACCOUNT_TO_SUB_KIT_ITEM: "Select myob account to sub kit item",
  APPLY_MYOB_ACCOUNT_TO_ALL_LINE_ITEMS: "Apply Myob account to all line items",
  UPDATE_PRODUCT_KIT_COST: "Update product kit cost",
};

// FAC-4861: Shared catalogue->order mapping. Extracted from addProductKit (which previously
// inlined this transform twice — once for top-level products, once for sub-kit products) so the
// nested "add product kit as a sub-kit" path can produce the SAME populated, edit-honouring line
// items as a normal kit add. Sending populated kit_products makes the backend persist exactly what
// the user edited in the preview modal, instead of re-deriving everything from the catalogue.
const mapCatalogueKitProductsToOrderShape = (
  kitProducts,
  { customPricing, orderPrices, disableTaxByDefault, isMarginTrackedByCompany }
) => {
  const getMarkupValue = ({ kitProductPrice, markup = 0 }) => {
    if (kitProductPrice.isZero()) {
      return 0;
    }

    return markup || 0;
  };

  return kitProducts
    .map((kitProduct) => {
      const item = {
        [PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL]: {
          productName: kitProduct.product.name,
          kitProductPrice: customPricing
            ? 0
            : orderPrices
                .get("markupPrices")
                ?.toJS()
                .reduce((accumulatedValue, markup) => {
                  if (!markup.isDeleted) {
                    return accumulatedValue * (1 + markup.percent / 100);
                  }
                  return accumulatedValue;
                }, kitProduct.value),
          cost:
            (customPricing && isMarginTrackedByCompany) || !customPricing
              ? (kitProduct?.productRow?.cost ?? 0)
              : 0,
        },
        [PRODUCT_TYPE_LABOUR_PRODUCT_LABEL]: {
          productName: `${kitProduct.firstName} ${kitProduct.lastName}`,
          kitProductPrice: orderPrices
            .get("markupPrices")
            ?.toJS()
            .reduce(
              (accumulatedValue, markup) => {
                if (!markup.isDeleted) {
                  return accumulatedValue * (1 + markup.percent / 100);
                }
                return accumulatedValue;
              },
              kitProduct.customHourlyRateCharged && kitProduct.customHourlyRateCharged !== 0
                ? kitProduct.customHourlyRateCharged
                : kitProduct.hourlyRateCharged
            ),
          cost:
            (customPricing && isMarginTrackedByCompany) || !customPricing
              ? kitProduct.customHourlyRateCost && kitProduct.customHourlyRateCost !== 0
                ? kitProduct.customHourlyRateCost
                : kitProduct.hourlyRateCost
              : 0,
        },
        [PRODUCT_TYPE_NOTES_ITEM_LABEL]: {
          quantity: 0,
          cost: 0,
          notes: kitProduct.notes,
          notesType: kitProduct.notesType,
          kitProductPrice: 0,
        },
      };

      const quantity = [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(kitProduct.pricingStrategy)
        ? 0
        : kitProduct.quantity;

      const customAttributeValues = kitProduct?.customAttributeValues || [];
      const sumLengths =
        kitProduct.sumLengths && !Array.isArray(kitProduct.sumLengths)
          ? kitProduct.sumLengths
          : {
              total: 0,
              forPrice: 0,
            };
      const types =
        kitProduct.productRow?.types?.map((type) => {
          const matchedType = customAttributeValues.find(
            (attribute) => attribute.name === type.name
          );

          return {
            ...type,
            value: matchedType ? matchedType.value || "" : type.value || "",
          };
        }) ?? [];

      return {
        ...kitProduct,
        customValue: null,
        customValueLm: null,
        discount: null,
        isCompleted: false,
        isTaxFree: disableTaxByDefault || kitProduct.product.isTaxFree,
        lengthItems: kitProduct.lengthItems ? kitProduct.lengthItems : [],
        actualLengthItems: kitProduct.lengthItems ? kitProduct.lengthItems : [],
        initialLengthItems: kitProduct.lengthItems ? kitProduct.lengthItems : [],
        sumLengths,
        actualSumLengths: sumLengths,
        productType: kitProduct.productType,
        pricingStrategy: kitProduct.pricingStrategy,
        productName: item[kitProduct.productType]?.productName,
        types,
        customAttributeValues: kitProduct.customAttributeValues,
        rowPriceLevel: kitProduct.rowPriceUid,
        value: Decimal(item[kitProduct.productType]?.kitProductPrice).toDecimalPlaces(4).toFixed(4),

        totalValue: getQuantityByPricingStrategy({
          quantity,
          lengthItems: kitProduct.lengthItems,
          pricingStrategy: kitProduct.pricingStrategy,
          minLengthForLinealMeters: kitProduct.minLengthForLinealMeters,
          minLengthForLinealFeet: kitProduct.minLengthForLinealFeet,
        })
          .times(item[kitProduct.productType]?.kitProductPrice)
          .toDecimalPlaces(2)
          .toFixed(2),

        quantity: quantity,
        baseQuantity: quantity,
        // FAC-5897: labour starts with a zero actual, everything else mirrors the estimate.
        actualQuantity: initialActualQuantity(kitProduct.productType, quantity),
        ...initialLabourTracking(kitProduct.productType),
        cost: item[kitProduct.productType]?.cost,
        markup: customPricing
          ? 0
          : getMarkupValue({
              kitProductPrice: Decimal(item[kitProduct.productType]?.kitProductPrice),
              markup: kitProduct?.productRow?.markup,
            }),
        markupIsPercentage: kitProduct?.productRow?.markupIsPercentage ?? true,
        margin: 0,
        uid: null,
        kitProductUid: kitProduct.uid,
        labourUser: kitProduct.user,
      };
    })
    .map((kitProduct) => {
      if (kitProduct.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const { customFormula } = kitProduct;
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        const kitCustomFormula = {
          ...customFormula,
          appliedElements: customFormula.elements,
        };

        return {
          ...kitProduct,
          customFormula: kitCustomFormula,
          customFormulaOutput: formattedFormulaOutput,
          actualCustomFormulaOutput: formattedFormulaOutput,
          totalValue: customPricing
            ? 0
            : Decimal(kitProduct.value)
                .times(kitProduct.quantity)
                .times(Decimal(roundedFormulaOutput).toDecimalPlaces(4))
                .toDecimalPlaces(2)
                .toFixed(2),
        };
      }

      return kitProduct;
    });
};

// FAC-4861: Map a catalogue kit's sub-kits into populated order-shaped sub-kits (each carrying its
// own mapped kit_products + a rolled-up total). Mirrors the subKitItems block addProductKit builds.
const mapCatalogueSubKitsToOrderShape = (subKits, ctx) =>
  subKits.map((subKit) => {
    const subKitProducts = mapCatalogueKitProductsToOrderShape(subKit.kitProducts, ctx);

    const totalValueOfSubKit = subKitProducts.reduce((accumulator, currentValue) => {
      return Decimal(accumulator).plus(currentValue.totalValue);
    }, 0);

    return {
      ...subKit,
      uid: null,
      subKitUid: subKit.uid,
      kitProducts: subKitProducts,
      quantity: 1,
      actualQuantity: 1,
      totalValue: Decimal(totalValueOfSubKit).toDecimalPlaces(2).toFixed(2),
      value: Decimal(totalValueOfSubKit).toDecimalPlaces(4).toFixed(4),
      deletedKitItems: [],
      productKitProducts: [],
      isCompleted: false,
    };
  });

// FAC-5734: SET_PRODUCT_KIT (productKits.reducer.js) derives each row's per-item price only for
// STANDARD kits, so a custom-priced kit picked from the preview arrives with RAW rows — product
// rows have no `value` and labour fields can still be nested under `labour`. When such a kit is
// added under a STANDARD parent (populated mapping), the rows must first be normalized to the
// standard shape, using the same price selection SET_PRODUCT_KIT applies: the kit's own custom
// row price for the applicable price level when set (non-zero), else the catalogue row price;
// the customer's default price level wins when it's visible, else the first non-hidden level.
const deriveKitProductRowValue = (kitProduct, customerPriceLevel) => {
  const rowPrices = kitProduct.productRow?.rowPrices ?? [];
  const customRowPrices = kitProduct.customRowPrices ?? [];
  const currentTable = kitProduct.product?.tables?.find(
    (table) => table.name === kitProduct.productRow?.material
  );
  const notHiddenPrices =
    currentTable?.priceLevels?.filter((priceLevel) => !priceLevel.isHidden) ?? [];

  let priceLevelUid = notHiddenPrices[0]?.uid;
  if (
    customerPriceLevel &&
    notHiddenPrices.some((priceLevel) => priceLevel.uid === customerPriceLevel.uid)
  ) {
    priceLevelUid = customerPriceLevel.uid;
  }
  if (!priceLevelUid) {
    // No visible price level to derive from — the row honestly has no per-item price.
    return { value: 0, rowPriceUid: kitProduct.rowPriceUid ?? null };
  }

  const price = rowPrices.find((rowPrice) => rowPrice.priceLevelUid === priceLevelUid);
  const customPrice = customRowPrices.find(
    (customRowPrice) => customRowPrice.priceLevelUid === priceLevelUid
  );
  const hasOverride =
    customPrice?.value !== null &&
    customPrice?.value !== undefined &&
    Number(customPrice.value) !== 0;
  return {
    value: hasOverride ? customPrice.value : (price?.value ?? 0),
    rowPriceUid: price?.uid ?? kitProduct.rowPriceUid ?? null,
  };
};

const normalizeRawCustomKitRows = (kitProducts, customerPriceLevel) =>
  (kitProducts ?? []).map((kitProduct) => {
    if (kitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      // Mirror SET_PRODUCT_KIT's standard branch: labour fields live nested under `labour`.
      return kitProduct.labour ? { ...kitProduct, ...kitProduct.labour } : kitProduct;
    }
    if (
      kitProduct.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL &&
      (kitProduct.value === null || kitProduct.value === undefined)
    ) {
      const { value, rowPriceUid } = deriveKitProductRowValue(kitProduct, customerPriceLevel);
      return { ...kitProduct, value, rowPriceUid };
    }
    return kitProduct;
  });

class AdditionalProducts extends ReduxModule {
  getNamespace() {
    return "[AdditionalProducts]";
  }

  getInitialState() {
    return {
      activeProduct: null, // this.initialProductState()
      activeCategory: null,
      startStateOrderProducts: null, // items: []
      searchValue: "",
      activeTabs: {},
      items: [], // { this.initialItemState() }
      editItem: null,
      dataServer: {
        isPending: false,
        items: {},
      },
      categories: [], // {company: '', isDeleted: bool, name: '', uid: '', isPending: bool}
      isPending: false,
      isDuplicating: false,
      errors: {},
    };
  }

  initialProductState() {
    return {
      uid: "",
      category: "Miscellaneous", // if saving - uid category
      isDeleted: false,
      image: null,
      name: "",
      tables: [
        // this.initialOptionsState()
      ],
      inLength: false,
      pricingStrategy: "",
    };
  }

  initialItemState() {
    return {
      tempUid: nanoid(), // temp uid for the item
      additionalProduct: "", // uid additional product
      isDeleted: false,
      isCompleted: false,
      isTaxFree: false,
      uid: "", // uid item
      name: "",
      productDescription: "",
      types: [],
      rowPriceLevel: null,
      subitems: [], // {amount: '0', length: '0'}
      actualLengthItems: [],
      quantity: "0",
      actualQuantity: "0",
      actualQuantityInventory: null, // FAC-4489: null means "not set", any value >= 0 means "intentionally set"
      value: "0.00",
      totalValue: "0.00",
      customValue: null,
      inLength: false,
      pricingStrategy: "",
      customFormula: null,
      isSavingToProductCatalogue: false,
      jobs: null,
      // sumLengths: {total: 0, forPrice: 0} if inLength: true
      // actualSumLengths: {total: 0, forPrice: 0} if inLength: true
    };
  }

  initialOptionsState() {
    return {
      name: "",
      materialUid: "",
      productUid: "",
      fields: ["Attribute 0"],
      priceLevels: [{}],
      hiddenPriceLevels: [],
      values: [
        {
          types: [
            {
              name: "Attribute 0",
              value: "",
              isHideOrderSummary: false,
              isHideCustomerView: false,
              isHidePurchaseOrder: false,
              isHideInvoicePdf: false,
              isHideQuotePdf: false,
              isHideWorkOrderPdf: false,
              isHideDeliveryDocketPdf: false,
              isHidePurchaseOrderPdf: false,
              isHideAccountingPackage: false,
            },
          ], // name: [fields[id]]
          prices: [{ name: "Price (A)", value: "" }], // name: [titles[id]]
          rowPrices: [{}],
          params: [{ name: "Colour", value: "" }],
          myobItem: { number: "" },
        },
      ],
    };
  }

  static generateProductDataForServer(_, { product, newName, imageFile }) {
    const errors = [];

    let activeProduct = product;
    if (!activeProduct.get("name") && !newName) {
      errors.push("Name of product is empty!");
    }

    const tables = sortByMaterialName(activeProduct.get("tables"));

    activeProduct = activeProduct.set("tables", tables);
    activeProduct = activeProduct.toJS();

    if (errors.length) {
      return { errors };
    }

    if (newName) {
      activeProduct.name = newName;
    }
    activeProduct = snakecaseKeys(activeProduct);

    const { image, ...json } = activeProduct;

    return createFormData(json, imageFile || image);
  }

  generateOrderDataForServer(state) {
    const errorsAll = [];
    const isAutoSaving = state.getIn(["orders", "saving", "auto"]);
    const items = state.getIn(["additionalProducts", "items"]);

    const itemsForServer = [];
    let deletedItems = 0;

    const [...keys] = items.keys();
    if (!isAutoSaving && keys.length === 0) {
      errorsAll.push("Empty products");
    } else {
      items.forEach((item, id) => {
        if (!isAutoSaving) {
          const errors = [];
          if (item.get("isDeleted")) {
            deletedItems += 1;
          }

          if (
            item.get("pricingStrategy") === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL &&
            !item.get("isDeleted")
          ) {
            try {
              const formulaOutput = item.get("customFormulaOutput");
              if (isNaN(formulaOutput) || formulaOutput === Infinity) {
                errors.push("has an error in the custom formula");
              }
            } catch (_) {
              errors.push("has an error in the custom formula");
            }
          }

          if (item.get("productType") === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
            item.get("productKitItems").map((kitItem, kititemIndex) => {
              if (
                kitItem.get("pricingStrategy") === PRICING_STRATEGY_LINEAL_METRES_LABEL &&
                !kitItem.get("lengthItems").size &&
                !kitItem.get("product").get("isDeleted")
              ) {
                errors.push(`product kit item #${kititemIndex + 1} is missing lengths`);
              }

              if (
                kitItem.get("pricingStrategy") === PRICING_STRATEGY_SQUARE_METRES_LABEL &&
                !kitItem.get("lengthItems").size &&
                !kitItem.get("product").get("isDeleted")
              ) {
                errors.push(`product kit item #${kititemIndex + 1} is missing dimensions`);
              }

              if (
                kitItem.get("pricingStrategy") === PRICING_STRATEGY_LINEAL_FEET_LABEL &&
                !kitItem.get("lengthItems").size &&
                !kitItem.get("product").get("isDeleted")
              ) {
                errors.push(`product kit item #${kititemIndex + 1} is missing lengths`);
              }

              if (
                kitItem.get("pricingStrategy") === PRICING_STRATEGY_SQUARE_FEET_LABEL &&
                !kitItem.get("lengthItems").size &&
                !kitItem.get("product").get("isDeleted")
              ) {
                errors.push(`product kit item #${kititemIndex + 1} is missing dimensions`);
              }

              if (
                kitItem.get("pricingStrategy") === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL &&
                !kitItem.get("product").get("isDeleted")
              ) {
                const customFormulaOutput = kitItem.get("customFormulaOutput");
                if (isNaN(customFormulaOutput) || customFormulaOutput === Infinity) {
                  errors.push(
                    `product kit item #${kititemIndex + 1} has an error in the custom formula`
                  );
                }
              }
            });

            item.get("subKits").map((subKit, subKitIndex) => {
              subKit.get("kitProducts").map((kitItem, kititemIndex) => {
                if (
                  kitItem.get("pricingStrategy") === PRICING_STRATEGY_LINEAL_METRES_LABEL &&
                  !kitItem.get("lengthItems").size &&
                  !kitItem.get("product").get("isDeleted")
                ) {
                  errors.push(
                    `sub kit #${subKitIndex + 1} sub kit item #${
                      kititemIndex + 1
                    } is missing lengths`
                  );
                }

                if (
                  kitItem.get("pricingStrategy") === PRICING_STRATEGY_SQUARE_METRES_LABEL &&
                  !kitItem.get("lengthItems").size &&
                  !kitItem.get("product").get("isDeleted")
                ) {
                  errors.push(
                    `sub kit #${subKitIndex + 1} sub kit item #${
                      kititemIndex + 1
                    } is missing dimensions`
                  );
                }

                if (
                  kitItem.get("pricingStrategy") === PRICING_STRATEGY_LINEAL_FEET_LABEL &&
                  !kitItem.get("lengthItems").size &&
                  !kitItem.get("product").get("isDeleted")
                ) {
                  errors.push(
                    `sub kit #${subKitIndex + 1} sub kit item #${
                      kititemIndex + 1
                    } is missing lengths`
                  );
                }

                if (
                  kitItem.get("pricingStrategy") === PRICING_STRATEGY_SQUARE_FEET_LABEL &&
                  !kitItem.get("lengthItems").size &&
                  !kitItem.get("product").get("isDeleted")
                ) {
                  errors.push(
                    `sub kit #${subKitIndex + 1} sub kit item #${
                      kititemIndex + 1
                    } is missing dimensions`
                  );
                }

                if (
                  kitItem.get("pricingStrategy") === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL &&
                  !kitItem.get("product").get("isDeleted")
                ) {
                  const customFormulaOutput = kitItem.get("customFormulaOutput");
                  if (isNaN(customFormulaOutput) || customFormulaOutput === Infinity) {
                    errors.push(
                      `sub kit #${subKitIndex + 1} sub kit item #${
                        kititemIndex + 1
                      } has an error in the custom formula`
                    );
                  }
                }
              });
            });
          }

          if (
            item.get("pricingStrategy") === PRICING_STRATEGY_LINEAL_METRES_LABEL &&
            !item.get("subitems").size &&
            !item.get("isDeleted")
          ) {
            errors.push("is missing lengths");
          }

          if (
            item.get("pricingStrategy") === PRICING_STRATEGY_SQUARE_METRES_LABEL &&
            !item.get("subitems").size &&
            !item.get("isDeleted")
          ) {
            errors.push("is missing dimensions");
          }

          if (
            item.get("pricingStrategy") === PRICING_STRATEGY_LINEAL_FEET_LABEL &&
            !item.get("subitems").size &&
            !item.get("isDeleted")
          ) {
            errors.push("is missing lengths");
          }

          if (
            item.get("pricingStrategy") === PRICING_STRATEGY_SQUARE_FEET_LABEL &&
            !item.get("subitems").size &&
            !item.get("isDeleted")
          ) {
            errors.push("is missing dimensions");
          }

          if (errors.length > 0) {
            const errorsItem = { [`Product #${id + 1}`]: errors };
            errorsAll.push(errorsItem);
            return errors;
          }
        }
        let product = item.toJS();
        if (product.productKitItems) {
          product.productKitItems = product.productKitItems.map((p) => ({
            ...p,
            types: p.types ? p.types.map((p) => ({ ...p, value: p.value || "" })) : [],
            params: p.params ? p.params.map((p) => ({ ...p, value: p.value || "" })) : [],
          }));
        } else {
          product.types = product.types
            ? product.types.map((p) => ({ ...p, value: p.value || "" }))
            : [];
          product.params = product.params
            ? product.params.map((p) => ({ ...p, value: p.value || "" }))
            : [];
        }

        product.productName = product.name;
        delete product.name;

        if (
          [
            PRICING_STRATEGY_LINEAL_METRES_LABEL,
            PRICING_STRATEGY_SQUARE_METRES_LABEL,
            PRICING_STRATEGY_LINEAL_FEET_LABEL,
            PRICING_STRATEGY_SQUARE_FEET_LABEL,
          ].includes(product.pricingStrategy)
        ) {
          delete product.quantity;
          delete product.actualQuantity;
          delete product.actualQuantityInventory;
        }

        product.pricingStrategy = PRICING_STRATEGY_STRING_TO_INT[product.pricingStrategy];

        if (product.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
          // FAC-4861: defensive `?? []` — when a Product Kit is added as a sub-kit, the
          // backend fan-out creates extra SubKitItems and `setUid` briefly leaves sparse
          // Redux entries (only `uid` populated) until the order reload finishes. Without
          // these guards `generateOrderDataForServer` crashes mid-payload-build with
          // "Cannot read properties of undefined (reading 'map')".
          product.productKitItems = (product.productKitItems ?? []).map((p) => ({
            ...p,
            pricingStrategy: PRICING_STRATEGY_STRING_TO_INT[p?.pricingStrategy],
          }));
          product.subKits = (product.subKits ?? []).map((s) => ({
            ...s,
            kitProducts: (s?.kitProducts ?? []).map((k) => ({
              ...k,
              pricingStrategy: PRICING_STRATEGY_STRING_TO_INT[k?.pricingStrategy],
            })),
          }));
        }
        product = snakecaseKeys(product);
        itemsForServer.push(product);

        return product;
      });
    }

    if (deletedItems > 0 && deletedItems === itemsForServer.length) {
      errorsAll.push("Empty products");
    }

    return { data: itemsForServer, errors: errorsAll };
  }

  generateOrderDataFromServer(itemsData, minLengthProducts, minLengthLinealFeet) {
    const getMinLengthForStrategy = (pricingStrategy) =>
      pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL
        ? (minLengthLinealFeet ?? 0)
        : (minLengthProducts ?? 0);

    const productItems = orderBy(
      [
        ...camelcaseKeys(itemsData, { deep: true }).map((d) => ({
          ...d,

          productKitItems: d.productKitItems ? d.productKitItems : [],
          subKits: d.subKits
            ? d.subKits.map((s) => ({
                ...s,
                kitProducts: s.kitProducts ? s.kitProducts : [],
              }))
            : [],
        })),
      ],
      ["index"],
      ["asc"]
    );

    return productItems.map((item) => {
      const product = camelcaseKeys(item, { deep: true });
      product.name = product.productName;
      delete product.productName;

      product.cost = product.cost || "0";
      product.markup = product.markup || "0";
      product.margin = product.margin || "0";
      product.types = Array.isArray(product.types) ? product.types : [];
      product.params = Array.isArray(product.params) ? product.params : [];

      const minLength = getMinLengthForStrategy(product.pricingStrategy);
      const rawSumLengths = getSumLengthsByPricingStrategy({
        lengthItems: product.subitems,
        pricingStrategy: product.pricingStrategy,
        minLength,
      });
      product.sumLengths = {
        total: parseFloat(rawSumLengths.total),
        ...(rawSumLengths.forPrice !== undefined && {
          forPrice: parseFloat(rawSumLengths.forPrice),
        }),
      };
      const rawActualSumLengths = getSumLengthsByPricingStrategy({
        lengthItems: product.actualLengthItems,
        pricingStrategy: product.pricingStrategy,
        minLength,
      });
      product.actualSumLengths = {
        total: parseFloat(rawActualSumLengths.total),
        ...(rawActualSumLengths.forPrice !== undefined && {
          forPrice: parseFloat(rawActualSumLengths.forPrice),
        }),
      };

      if (product.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        if (product.customFormula) {
          const formula = product.customFormula.elements.map((e) => e.value).join("");
          const formulaOutput = evaluate(formula);
          const roundedFormulaOutput = mathjsRound(formulaOutput, 4);

          if (!isNaN(roundedFormulaOutput) && roundedFormulaOutput !== Infinity) {
            const formattedFormulaOutput = product.customFormula.isRoundedToNearestWholeNumber
              ? Math.round(roundedFormulaOutput)
              : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);
            product.customFormulaOutput = formattedFormulaOutput;
          }
          const actualFormula = product.customFormula.appliedElements.map((e) => e.value).join("");
          const actualFormulaOutput = evaluate(actualFormula);
          const roundedActualFormulaOutput = mathjsRound(actualFormulaOutput, 4);

          if (!isNaN(roundedActualFormulaOutput) && roundedActualFormulaOutput !== Infinity) {
            const formattedFormulaOutput = product.customFormula.isRoundedToNearestWholeNumber
              ? Math.round(roundedActualFormulaOutput)
              : Decimal(roundedActualFormulaOutput).toDecimalPlaces(4).toFixed(4);

            product.actualCustomFormulaOutput = formattedFormulaOutput;
          }
        } else {
          product.customFormulaOutput = Decimal(product.formulaAnswer)
            .toDecimalPlaces(4)
            .toFixed(4);
          product.actualCustomFormulaOutput = Decimal(product.formulaAnswer)
            .toDecimalPlaces(4)
            .toFixed(4);
        }
      }

      if (product.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
        product.productKitItems = orderBy([...product.productKitItems], ["cartIndex"], ["asc"]).map(
          (kitItem) => {
            if (kitItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
              return {
                ...kitItem,
                isSavingToProductCatalogue: false,
                types: Array.isArray(kitItem.types) ? kitItem.types : [],
                params: Array.isArray(kitItem.params) ? kitItem.params : [],
                cost: kitItem.cost || "0",
                markup: kitItem.markup || "0",
                margin: kitItem.margin || "0",
              };
            }

            if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              if (kitItem.customFormula) {
                const { customFormula } = kitItem;
                const formula = customFormula.elements.map((e) => e.value).join("");
                const formulaOutput = evaluate(formula);
                const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
                const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
                  ? Math.round(roundedFormulaOutput)
                  : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

                const actualFormula = customFormula.appliedElements.map((e) => e.value).join("");
                const actualFormulaOutput = evaluate(actualFormula);
                const roundedActualFormulaOutput = mathjsRound(actualFormulaOutput, 4);
                const formattedActualFormulaOutput = customFormula.isRoundedToNearestWholeNumber
                  ? Math.round(roundedActualFormulaOutput)
                  : Decimal(roundedActualFormulaOutput).toDecimalPlaces(4).toFixed(4);

                return {
                  ...kitItem,
                  isSavingToProductCatalogue: false,
                  types: Array.isArray(kitItem.types) ? kitItem.types : [],
                  params: Array.isArray(kitItem.params) ? kitItem.params : [],
                  cost: kitItem.cost || "0",
                  markup: kitItem.markup || "0",
                  margin: kitItem.margin || "0",
                  customFormulaOutput: formattedFormulaOutput,
                  actualCustomFormulaOutput: formattedActualFormulaOutput,
                };
              }
              return {
                ...kitItem,
                isSavingToProductCatalogue: false,
                types: Array.isArray(kitItem.types) ? kitItem.types : [],
                params: Array.isArray(kitItem.params) ? kitItem.params : [],
                cost: kitItem.cost || "0",
                markup: kitItem.markup || "0",
                margin: kitItem.margin || "0",
                customFormulaOutput: Decimal(kitItem.formulaAnswer).toDecimalPlaces(4).toFixed(4),
                actualCustomFormulaOutput: Decimal(kitItem.formulaAnswer)
                  .toDecimalPlaces(4)
                  .toFixed(4),
              };
            }

            const minLength = getMinLengthForStrategy(kitItem.pricingStrategy);
            const rawSumLengths = getSumLengthsByPricingStrategy({
              lengthItems: kitItem.lengthItems,
              pricingStrategy: kitItem.pricingStrategy,
              minLength,
            });
            const sumLengths = {
              total: parseFloat(rawSumLengths.total),
              ...(rawSumLengths.forPrice !== undefined && {
                forPrice: parseFloat(rawSumLengths.forPrice),
              }),
            };
            const rawActualSumLengths = getSumLengthsByPricingStrategy({
              lengthItems: kitItem.actualLengthItems,
              pricingStrategy: kitItem.pricingStrategy,
              minLength,
            });
            const actualSumLengths = {
              total: parseFloat(rawActualSumLengths.total),
              ...(rawActualSumLengths.forPrice !== undefined && {
                forPrice: parseFloat(rawActualSumLengths.forPrice),
              }),
            };

            return {
              ...kitItem,
              isSavingToProductCatalogue: false,
              types: Array.isArray(kitItem.types) ? kitItem.types : [],
              params: Array.isArray(kitItem.params) ? kitItem.params : [],
              sumLengths,
              actualSumLengths,
              cost: kitItem.cost || "0",
              markup: kitItem.markup || "0",
              margin: kitItem.margin || "0",
            };
          }
        );

        product.subKits = orderBy([...product.subKits], ["subKitIndex"], ["asc"]).map((subKit) => {
          const subKitItems = orderBy([...subKit.kitProducts], ["cartIndex"], ["asc"]).map(
            (kitItem) => {
              if (kitItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
                return {
                  ...kitItem,
                  isSavingToProductCatalogue: false,
                  types: Array.isArray(kitItem.types) ? kitItem.types : [],
                  params: Array.isArray(kitItem.params) ? kitItem.params : [],
                  cost: kitItem.cost || "0",
                  markup: kitItem.markup || "0",
                  margin: kitItem.margin || "0",
                };
              }

              if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
                if (kitItem.customFormula) {
                  const { customFormula } = kitItem;
                  const formula = customFormula.elements.map((e) => e.value).join("");
                  const formulaOutput = evaluate(formula);
                  const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
                  const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
                    ? Math.round(roundedFormulaOutput)
                    : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

                  const actualFormula = customFormula.appliedElements.map((e) => e.value).join("");
                  const actualFormulaOutput = evaluate(actualFormula);
                  const roundedActualFormulaOutput = mathjsRound(actualFormulaOutput, 4);
                  const formattedActualFormulaOutput = customFormula.isRoundedToNearestWholeNumber
                    ? Math.round(roundedActualFormulaOutput)
                    : Decimal(roundedActualFormulaOutput).toDecimalPlaces(4).toFixed(4);

                  return {
                    ...kitItem,
                    isSavingToProductCatalogue: false,
                    types: Array.isArray(kitItem.types) ? kitItem.types : [],
                    params: Array.isArray(kitItem.params) ? kitItem.params : [],
                    cost: kitItem.cost || "0",
                    markup: kitItem.markup || "0",
                    margin: kitItem.margin || "0",
                    customFormulaOutput: formattedFormulaOutput,
                    actualCustomFormulaOutput: formattedActualFormulaOutput,
                  };
                }
                return {
                  ...kitItem,
                  isSavingToProductCatalogue: false,
                  types: Array.isArray(kitItem.types) ? kitItem.types : [],
                  params: Array.isArray(kitItem.params) ? kitItem.params : [],
                  cost: kitItem.cost || "0",
                  markup: kitItem.markup || "0",
                  margin: kitItem.margin || "0",
                  customFormulaOutput: Decimal(kitItem.formulaAnswer).toDecimalPlaces(4).toFixed(4),
                  actualCustomFormulaOutput: Decimal(kitItem.formulaAnswer)
                    .toDecimalPlaces(4)
                    .toFixed(4),
                };
              }

              const minLength = getMinLengthForStrategy(kitItem.pricingStrategy);
              const rawSumLengths = getSumLengthsByPricingStrategy({
                lengthItems: kitItem.lengthItems,
                pricingStrategy: kitItem.pricingStrategy,
                minLength,
              });
              const sumLengths = {
                total: parseFloat(rawSumLengths.total),
                ...(rawSumLengths.forPrice !== undefined && {
                  forPrice: parseFloat(rawSumLengths.forPrice),
                }),
              };
              const rawActualSumLengths = getSumLengthsByPricingStrategy({
                lengthItems: kitItem.actualLengthItems,
                pricingStrategy: kitItem.pricingStrategy,
                minLength,
              });
              const actualSumLengths = {
                total: parseFloat(rawActualSumLengths.total),
                ...(rawActualSumLengths.forPrice !== undefined && {
                  forPrice: parseFloat(rawActualSumLengths.forPrice),
                }),
              };

              return {
                ...kitItem,
                isSavingToProductCatalogue: false,
                types: Array.isArray(kitItem.types) ? kitItem.types : [],
                params: Array.isArray(kitItem.params) ? kitItem.params : [],
                sumLengths,
                actualSumLengths,
                cost: kitItem.cost || "0",
                markup: kitItem.markup || "0",
                margin: kitItem.margin || "0",
              };
            }
          );

          return {
            ...subKit,
            kitProducts: subKitItems,
            deletedKitItems: [],
          };
        });
      }

      return product;
    });
  }

  createNewOrder = (_state) => {
    _state.merge(fromJS(this.getInitialState()));
  };

  setUidOfCustomFormula = (_state, payload) => {
    if (payload.data.custom_formula) {
      _state.setIn(
        ["items", payload.itemIndex, "customFormula", "uid"],
        payload.data.custom_formula.uid
      );
    }

    if (payload.data?.kit_items) {
      const localKitItemsCF = _state.getIn(["items", payload.itemIndex, "productKitItems"]);
      const localKitItemsCFCount = localKitItemsCF ? localKitItemsCF.size : 0;
      orderBy([...payload.data.kit_items], ["cart_index"], ["asc"]).forEach(
        (kitItem, kitItemIndex) => {
          if (kitItemIndex >= localKitItemsCFCount) {
            return;
          }
          if (kitItem.custom_formula) {
            _state.setIn(
              ["items", payload.itemIndex, "productKitItems", kitItemIndex, "customFormula", "uid"],
              kitItem.custom_formula.uid
            );
          }
        }
      );
    }

    if (payload.data?.sub_kits) {
      // FAC-4861: same out-of-bounds guard as setUid — backend fan-out can return more
      // sub-kits / kit-products than the frontend pre-allocated. Immutable.setIn at an
      // out-of-bounds index auto-creates the missing intermediate as a Map (not a List)
      // which after toJS becomes `{ "0": {...} }` and crashes downstream `.reduce`/`.filter`.
      const localSubKits = _state.getIn(["items", payload.itemIndex, "subKits"]);
      const localSubKitsCount = localSubKits ? localSubKits.size : 0;

      orderBy([...payload.data.sub_kits], ["sub_kit_index"], ["asc"]).forEach(
        (subKit, subKitIndex) => {
          if (subKitIndex >= localSubKitsCount) {
            return;
          }
          if (subKit.kit_products) {
            const localKitProducts = _state.getIn([
              "items",
              payload.itemIndex,
              "subKits",
              subKitIndex,
              "kitProducts",
            ]);
            const localKitProductsCount = localKitProducts ? localKitProducts.size : 0;

            orderBy([...subKit.kit_products], ["cart_index"], ["asc"]).forEach(
              (subKitItem, subKitItemIndex) => {
                if (subKitItemIndex >= localKitProductsCount) {
                  return;
                }
                if (subKitItem.custom_formula) {
                  _state.setIn(
                    [
                      "items",
                      payload.itemIndex,
                      "subKits",
                      subKitIndex,
                      "kitProducts",
                      subKitItemIndex,
                      "customFormula",
                      "uid",
                    ],
                    subKitItem.custom_formula.uid
                  );
                }
              }
            );
          }
        }
      );
    }
  };

  setUidOfKitItemCustomFormula = (_state, payload) => {
    const basePath = payload?.subKitIndex
      ? [
          "items",
          payload.itemIndex,
          "subKits",
          payload.subKitIndex,
          "kitProducts",
          payload.cartIndex,
        ]
      : ["items", payload.itemIndex, "productKitItems", payload.cartIndex];

    const hasCustomFormula = _state.getIn([...basePath, "customFormula"]);
    if (hasCustomFormula) {
      _state.setIn([...basePath, "customFormula", "uid"], payload.uid);
    }
  };

  setUid = (_state, payload) => {
    _state.setIn(["items", payload.itemIndex, "uid"], payload.data.uid);

    if (payload.data?.kit_items) {
      const localKitItems = _state.getIn(["items", payload.itemIndex, "productKitItems"]);
      const localKitItemsCount = localKitItems ? localKitItems.size : 0;
      orderBy([...payload.data.kit_items], ["cart_index"], ["asc"]).forEach(
        (kitItem, kitItemIndex) => {
          if (kitItemIndex >= localKitItemsCount) {
            return;
          }
          _state.setIn(
            ["items", payload.itemIndex, "productKitItems", kitItemIndex, "uid"],
            kitItem.uid
          );
        }
      );
    }

    if (payload.data?.sub_kits) {
      // FAC-4861: backend fan-out can return MORE sub-kits / kit-products than the frontend
      // pre-created (one frontend pick → preserved + N promoted). Skip slots the frontend
      // didn't allocate — Immutable.setIn at an out-of-bounds index auto-creates the missing
      // intermediate as a Map (not a List), which after toJS becomes `{ "0": {...} }` and
      // crashes downstream `.filter`/`.map` calls. The openOrder reload chained after this
      // dispatch hydrates the new slots from the server's full payload.
      const localSubKits = _state.getIn(["items", payload.itemIndex, "subKits"]);
      const localSubKitsCount = localSubKits ? localSubKits.size : 0;

      orderBy([...payload.data.sub_kits], ["sub_kit_index"], ["asc"]).forEach(
        (subKit, subKitIndex) => {
          if (subKitIndex >= localSubKitsCount) {
            return;
          }

          _state.setIn(["items", payload.itemIndex, "subKits", subKitIndex, "uid"], subKit.uid);

          if (subKit.kit_products) {
            const localKitProducts = _state.getIn([
              "items",
              payload.itemIndex,
              "subKits",
              subKitIndex,
              "kitProducts",
            ]);
            const localKitProductsCount = localKitProducts ? localKitProducts.size : 0;

            orderBy([...subKit.kit_products], ["cart_index"], ["asc"]).forEach(
              (subKitItem, subKitItemIndex) => {
                if (subKitItemIndex >= localKitProductsCount) {
                  return;
                }
                _state.setIn(
                  [
                    "items",
                    payload.itemIndex,
                    "subKits",
                    subKitIndex,
                    "kitProducts",
                    subKitItemIndex,
                    "uid",
                  ],
                  subKitItem.uid
                );
              }
            );
          }
        }
      );
    }
  };

  setKitItemUid = (_state, payload) => {
    if (payload?.subKitIndex !== undefined) {
      _state.setIn(
        [
          "items",
          payload.itemIndex,
          "subKits",
          payload.subKitIndex,
          "kitProducts",
          payload.cartIndex,
          "uid",
        ],
        payload.uid
      );
    } else {
      _state.setIn(
        ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "uid"],
        payload.uid
      );
    }
  };

  setUids = (_state, data) => {
    if (data.length === 0) {
      return;
    }

    const items = orderBy([..._state.get("items")], ["index"], ["asc"]);

    items.forEach((_price, i) => {
      if (data[i]) {
        _state.setIn(["items", i, "uid"], data[i].uid);

        if (data[i].kit_items) {
          orderBy([...data[i].kit_items], ["cart_index"], ["asc"]).forEach((_item, index) => {
            if (data[i].kit_items[index]) {
              _state.setIn(
                ["items", i, "productKitItems", index, "uid"],
                data[i].kit_items[index].uid
              );
            }
          });
        }

        if (data[i].sub_kits) {
          // FAC-4861: same out-of-bounds guard as setUid / setUidOfCustomFormula. A full-order
          // save can echo back more sub-kits / kit-products than the frontend pre-allocated (a
          // not-yet-fanned-out kit-with-sub-kits). Immutable.setIn at an out-of-bounds index
          // auto-creates the missing intermediate as a Map (not a List), which after toJS becomes
          // `{ "0": {...} }` and crashes downstream `.filter`/`.map`. Skip slots the frontend
          // didn't allocate; the order reload hydrates them from the server's full payload.
          const localSubKits = _state.getIn(["items", i, "subKits"]);
          const localSubKitsCount = localSubKits ? localSubKits.size : 0;

          orderBy([...data[i].sub_kits], ["sub_kit_index"], ["asc"]).forEach(
            (subKit, subKitIndex) => {
              if (subKitIndex >= localSubKitsCount) {
                return;
              }
              _state.setIn(["items", i, "subKits", subKitIndex, "uid"], subKit.uid);
              if (subKit.kit_products) {
                const localKitProducts = _state.getIn([
                  "items",
                  i,
                  "subKits",
                  subKitIndex,
                  "kitProducts",
                ]);
                const localKitProductsCount = localKitProducts ? localKitProducts.size : 0;

                orderBy([...subKit.kit_products], ["cart_index"], ["asc"]).forEach(
                  (_kitItem, index) => {
                    if (index >= localKitProductsCount) {
                      return;
                    }
                    if (subKit.kit_products[index]) {
                      _state.setIn(
                        ["items", i, "subKits", subKitIndex, "kitProducts", index, "uid"],
                        subKit.kit_products[index].uid
                      );
                    }
                  }
                );
              }
            }
          );
        }
      }
    });
  };

  openOrder = (_state, additionalProducts) => {
    _state.merge(fromJS(this.getInitialState()));

    _state.set("items", additionalProducts);
    _state.set("startStateOrderProducts", additionalProducts);
  };

  openOrderThunk = ({ dispatch, getState, fulfilled }, data) => {
    fulfilled(fromJS(data));

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }
  };

  resetOrderProductsThunk = ({ fulfilled }) => {
    fulfilled();
  };

  searchProductsThunk = (
    { token, dispatch, pendingAction, fulfilled },
    { search = "", categoryName = "" }
  ) => {
    dispatch(pendingAction({ isPending: true }));
    return additionalProductsApi
      .searchProducts(token, {
        search: search || undefined,
        category: categoryName || undefined,
      })
      .then((response) => {
        dispatch(pendingAction({ isPending: false }));
        fulfilled(response);
        return response.data;
      });
  };

  searchProductsByCategoryThunk = (
    { token, getState, dispatch, pendingAction, fulfilled },
    { categoryUid }
  ) => {
    const id = getState()
      .getIn(["additionalProducts", "categories"])
      .findIndex((item) => item.get("uid") === categoryUid);
    const category = getState().getIn(["additionalProducts", "categories", id, "name"]);
    dispatch(pendingAction({ id, isPending: true }));
    const searchValue = "";
    additionalProductsApi
      .searchProducts(token, {
        searchValue,
        category,
      })
      .then((response) => {
        dispatch(pendingAction({ id, isPending: false }));
        fulfilled(response);
        return response.data;
      });
  };

  getProduct = ({ token, fulfilled }, uid) =>
    additionalProductsApi.getProduct(token, uid).then((response) => {
      fulfilled(camelcaseKeys(response.data, { deep: true }));
      return response.data;
    });

  getCategories = ({ token, fulfilled }, uid) =>
    additionalProductsApi.getCategories(token, uid).then((response) => {
      fulfilled(response);
      return response.data;
    });

  addItem = (
    { getState, dispatch, fulfilled },
    { material, row, cellId, isMarkupValue, ...data }
  ) => {
    const state = getState();
    const itemIndex = state.getIn(["additionalProducts", "items"]).size;
    const activeProduct = state.getIn(["additionalProducts", "activeProduct"]);
    const orderPrices = state.getIn(["prices", "orderPrices"]);
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const newItem = this.initialItemState();

    console.log("activeProduct", activeProduct.toJS());
    newItem.additionalProduct = activeProduct.get("uid");
    newItem.productType = PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL;
    newItem.isStockTrackedByCompany = activeProduct.get("isStockTrackedByCompany");
    newItem.isTaxFree = disableTaxByDefault || activeProduct.get("isTaxFree");
    newItem.name = activeProduct.get("name");
    newItem.inLength = activeProduct.get("inLength");
    newItem.index = itemIndex;
    newItem.pricingStrategy = PRICING_STRATEGY[`${activeProduct.get("pricingStrategy")}`];

    try {
      const customFormula = activeProduct.get("customFormula").toJS();
      const formula = customFormula.elements.map((e) => e.value).join("");
      const formulaOutput = evaluate(formula);
      const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
      const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
        ? Math.round(roundedFormulaOutput)
        : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);
      if (!isNaN(roundedFormulaOutput) && roundedFormulaOutput !== Infinity) {
        const itemCustomFormula = {
          ...customFormula,
          appliedElements: customFormula.elements,
        };
        newItem.customFormula = itemCustomFormula;
        newItem.customFormulaOutput = formattedFormulaOutput;
        newItem.actualCustomFormulaOutput = formattedFormulaOutput;
      }
    } catch (_) {
      /** Do nothing */
    }

    newItem.additionalProductRow = row.uid;
    newItem.categoryName = activeProduct.get("category");

    newItem.types = row.types ? row.types.map((p) => ({ ...p, value: p.value || "" })) : [];
    newItem.inventory = row.inventory;
    newItem.cost = row.cost ? row.cost : 0;
    newItem.markup = row.markup;
    newItem.markupIsPercentage = row.markupIsPercentage;

    if (row.params) {
      newItem.params = row.params ? row.params.map((p) => ({ ...p, value: p.value || "" })) : [];
    }

    if (isMarkupValue) {
      newItem.rowPriceLevel = null;
      newItem.value = row.markedUpPrice;
    } else {
      const { uid: rowPriceUid, value: priceValue } = row.rowPrices[cellId];
      newItem.rowPriceLevel = rowPriceUid;
      newItem.value = data?.value || priceValue;
    }

    if (
      [PRICING_STRATEGY_BASIC_QUANTITIES_LABEL, PRICING_STRATEGY_CUSTOM_FORMULA_LABEL].includes(
        newItem.pricingStrategy
      )
    ) {
      newItem.actualQuantity = data?.actualQuantity || "1";
      newItem.quantity = data?.quantity || "1";
      newItem.margin =
        Decimal(newItem.cost) > 0
          ? Decimal(newItem.value).minus(newItem.cost).toDecimalPlaces(2).toFixed(2)
          : 0;
    } else if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(newItem.pricingStrategy)
    ) {
      newItem.sumLengths = { total: 0, forPrice: 0 };
      newItem.actualSumLengths = { total: 0, forPrice: 0 };
      newItem.margin = 0;
      newItem.subitems = [];
      newItem.actualLengthItems = [];
      delete newItem.quantity;
      delete newItem.actualQuantity;
    }

    if (material?.uid) {
      newItem.materialName = material.name;
    }
    const withAccounting = state.getIn(["customers", "company", "accountingSyncAllowed"]);
    if (withAccounting) {
      const displayField =
        state.getIn([
          "customers",
          "company",
          "settings",
          "accountingSettings",
          "accountingItemDisplayField",
        ]) || "name";
      newItem.accountingItemName = getAccountingItemLabel(row.accountingItem, displayField) ?? "";
    }
    newItem.inventory = row.inventory;

    newItem.value = orderPrices
      .get("markupPrices")
      ?.toJS()
      .reduce((accumulatedValue, markup) => {
        if (!markup.isDeleted) {
          return accumulatedValue * (1 + markup.percent / 100);
        }
        return accumulatedValue;
      }, newItem.value);

    fulfilled(fromJS(newItem));
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: newItem.value,
      })
    );

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    const items = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = items.find((item) => item.tempUid === newItem.tempUid);
    return item;
  };

  addProductKit = ({ getState, dispatch, fulfilled }, { productKit }) => {
    const state = getState();
    const itemIndex = state.getIn(["additionalProducts", "items"]).size;
    const orderPrices = state.getIn(["prices", "orderPrices"]);
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const { kitProducts, subKits, row, customPricing } = productKit;
    const newProductKit = this.initialItemState();

    const ctx = {
      customPricing,
      orderPrices,
      disableTaxByDefault,
      isMarginTrackedByCompany: productKit.isMarginTrackedByCompany,
    };

    const productKitItems = mapCatalogueKitProductsToOrderShape(kitProducts, ctx);

    const subKitItems = mapCatalogueSubKitsToOrderShape(subKits, ctx);

    const cost = row?.cost ? row.cost : 0;
    const cartItemTotals = productKitItems.reduce((prev, current) => {
      return Decimal(prev).plus(current.totalValue);
    }, 0);
    const subKitTotals = subKitItems.reduce((prev, current) => {
      return Decimal(prev).plus(current.totalValue);
    }, 0);
    newProductKit.additionalProduct = productKit.uid;
    newProductKit.productType = PRODUCT_TYPE_PRODUCT_KIT_LABEL;
    newProductKit.name = productKit.name;
    newProductKit.categoryName = productKit.category;
    newProductKit.index = itemIndex;
    newProductKit.quantity = "1";
    newProductKit.actualQuantity = "1";
    newProductKit.value = customPricing
      ? productKit.selectedValue
      : Decimal(cartItemTotals).plus(subKitTotals).toDecimalPlaces(2).toFixed(2);
    newProductKit.totalValue = customPricing
      ? productKit.selectedValue
      : Decimal(cartItemTotals).plus(subKitTotals).toDecimalPlaces(2).toFixed(2);
    newProductKit.productKitItems = productKitItems;
    newProductKit.deletedKitItems = [];
    newProductKit.productKitDescription = productKit.description;
    newProductKit.subKits = subKitItems;
    newProductKit.customPricing = customPricing;
    newProductKit.displayKitItems = productKit.displayKitItems;
    newProductKit.displaySubKits = productKit.displaySubKits;
    newProductKit.types = customPricing ? row.attributes : [];
    newProductKit.row = customPricing ? row : null;
    newProductKit.rowPriceLevel = customPricing ? productKit.rowPriceUid : null;
    newProductKit.pricingStrategy = productKit.pricingStrategy;
    newProductKit.cost = customPricing ? cost : 0;
    newProductKit.markup = customPricing ? row.markup : 0;
    newProductKit.markupIsPercentage = customPricing ? row.markupIsPercentage : false;
    newProductKit.margin = customPricing ? Decimal(newProductKit.value).minus(cost).toFixed(2) : 0;
    newProductKit.isMarginTrackedByCompany = productKit.isMarginTrackedByCompany;

    const withAccounting = state.getIn(["customers", "company", "accountingSyncAllowed"]);
    if (withAccounting) {
      const displayField =
        state.getIn([
          "customers",
          "company",
          "settings",
          "accountingSettings",
          "accountingItemDisplayField",
        ]) || "name";
      const accountingItem = customPricing ? row.accountingItem : productKit.accountingItem;
      newProductKit.accountingItemName = getAccountingItemLabel(accountingItem, displayField) ?? "";
    }

    fulfilled(fromJS(newProductKit));

    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: newProductKit.totalValue,
      })
    );

    // if not custom priced kit then calculate markup for all kit items
    if (!customPricing) {
      productKitItems.map((kitProduct, kitIndex) => {
        dispatch(
          this.actions.changeProductKitItemPriceValue({
            itemIndex: itemIndex,
            cartIndex: kitIndex,
            type: "value",
            value: kitProduct.value,
          })
        );
      });
      subKitItems.forEach((subKit, subKitIndex) => {
        subKit.kitProducts.forEach((kitProduct, kitIndex) => {
          dispatch(
            this.actions.changeSubKitItemPriceValue({
              itemIndex: itemIndex,
              subKitIndex,
              cartIndex: kitIndex,
              type: "value",
              value: kitProduct.value,
            })
          );
        });
      });
    }
    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    const items = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = items.find((item) => item.tempUid === newProductKit.tempUid);
    return item;
  };

  addOnTheFlyProductKit = ({ getState, dispatch, fulfilled }, productKit) => {
    const state = getState();
    const itemIndex = state.getIn(["additionalProducts", "items"]).size;
    const { customPricing } = productKit;
    const newProductKit = this.initialItemState();
    const company = state.getIn(["customers", "company"]).toJS();

    const displayProductKitItemsCompanySettings =
      company.settings.ordersSettings.orders.find(
        (setting) => setting.key === "DISPLAY_PRODUCT_KIT_ITEMS"
      )?.selected || [];

    const displaySubKitItemsCompanySettings =
      company.settings.ordersSettings.orders.find(
        (setting) => setting.key === "DISPLAY_SUB_KIT_ITEMS"
      )?.selected || [];
    const quantity = 1;
    newProductKit.productType = PRODUCT_TYPE_PRODUCT_KIT_LABEL;
    newProductKit.additionalProduct = null;
    newProductKit.index = itemIndex;
    newProductKit.isStockTrackedByCompany = false;
    newProductKit.quantity = quantity;
    newProductKit.actualQuantity = quantity;
    newProductKit.productKitItems = [];
    newProductKit.subKits = [];
    newProductKit.customPricing = customPricing;
    newProductKit.displayKitItems = displayProductKitItemsCompanySettings;
    newProductKit.displaySubKits = displaySubKitItemsCompanySettings;
    newProductKit.deletedKitItems = [];
    newProductKit.cost = 0;
    newProductKit.markup = 0;
    newProductKit.markupIsPercentage = true;
    newProductKit.margin = 0;
    newProductKit.types = [];

    fulfilled(fromJS(newProductKit));

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    const items = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = items.find((item) => item.tempUid === newProductKit.tempUid);
    return item;
  };

  addOnTheFlySubKit = ({ getState, fulfilled }, { itemIndex }) => {
    const state = getState();
    const subKitIndex = state.getIn(["additionalProducts", "items", itemIndex, "subKits"]).size;
    const quantity = 1;

    const newSubKit = {
      subKitIndex: subKitIndex,
      title: "",
      quantity: quantity,
      actualQuantity: quantity,
      kitProducts: [],
      deletedKitItems: [],
      cost: 0,
      markup: 0,
      markupIsPercentage: true,
      margin: 0,
      value: 0,
      totalValue: 0,
    };

    fulfilled({ itemIndex, subKitIndex, subKit: newSubKit });

    return newSubKit;
  };

  // FAC-4861: Add a Product Kit picked from the catalogue modal as one or more sub-kits of the
  // parent kit at `itemIndex`. The picked kit lands as a "preserved" sub-kit carrying its top-level
  // products, and each of its catalogue sub-kits is flattened into a sibling "promoted" sub-kit —
  // mirroring the backend fan-out, but done on the FRONTEND so the user's preview-modal edits
  // (quantities, prices, custom-formula, dimensions) are snapshotted into POPULATED kit_products
  // using the same mapping a normal kit add uses. The backend persists exactly what we send (its
  // catalogue fan-out only fires for an EMPTY kit_products shell), so edits are preserved instead
  // of re-derived from the catalogue. `subKitUid` still points at the source AdditionalProduct so
  // the backend circular-reference validator can reject cycles before any DB writes.
  addProductKitAsSubKit = ({ getState, dispatch, fulfilled }, { itemIndex, productKit }) => {
    const state = getState();

    // FAC-5734 (team decision 2026-07-13): a nested kit follows the PARENT kit's pricing
    // strategy. Adding a custom-priced kit into a STANDARD parent deliberately DISREGARDS the
    // kit-level price — the rows keep their per-item prices from the kit definition and roll
    // up to the parent, exactly like a standard kit's rows (this matches how the Product
    // Catalogue kit editor already treats nested custom kits). Only when BOTH kits are
    // custom-priced do the rows stay zeroed: the parent's fixed price is the money and the
    // children are build-only.
    const parentIsCustomPriced = !!state.getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "customPricing",
    ]);

    // FAC-4861: a self-add (the picked catalogue kit IS the parent item's kit) is detectable
    // client-side — reject it up-front so no optimistic rows are spliced and no request is
    // sent. The backend validator remains the authority for wrapper-mismatch self-adds and
    // indirect circular references.
    const parentAdditionalProduct = state.getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "additionalProduct",
    ]);
    if (
      productKit?.uid &&
      parentAdditionalProduct &&
      String(productKit.uid) === String(parentAdditionalProduct)
    ) {
      dispatch(
        alerts.actions.addAlert({
          type: "danger",
          message: "Cannot add a kit to itself.",
        })
      );
      return null;
    }

    const subKitIndex = state.getIn(["additionalProducts", "items", itemIndex, "subKits"]).size;
    const orderPrices = state.getIn(["prices", "orderPrices"]);
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);

    const ctx = {
      // Zero the rows only for custom → custom; under a standard parent a custom-priced kit
      // maps like a standard kit (per-item prices populated) so the roll-up below carries them.
      customPricing: !!productKit?.customPricing && parentIsCustomPriced,
      orderPrices,
      disableTaxByDefault,
      isMarginTrackedByCompany: productKit?.isMarginTrackedByCompany,
    };

    // FAC-5734: custom-priced kits reach here with RAW preview rows (SET_PRODUCT_KIT derives
    // per-item prices for standard kits only) — normalize them before the populated mapping,
    // or Decimal() crashes on the missing values.
    const needsRawRowNormalization = !!productKit?.customPricing && !parentIsCustomPriced;
    let sourceKitProducts = productKit?.kitProducts || [];
    let sourceSubKits = productKit?.subKits || [];
    if (needsRawRowNormalization) {
      const rawPriceLevel = state.getIn(["customers", "activeCustomer", "defaultPriceLevel"]);
      const customerPriceLevel =
        rawPriceLevel && typeof rawPriceLevel.toJS === "function"
          ? rawPriceLevel.toJS()
          : rawPriceLevel;
      sourceKitProducts = normalizeRawCustomKitRows(sourceKitProducts, customerPriceLevel);
      sourceSubKits = sourceSubKits.map((subKit) => ({
        ...subKit,
        kitProducts: normalizeRawCustomKitRows(subKit.kitProducts, customerPriceLevel),
      }));
    }

    // Preserved sub-kit — the picked kit itself, carrying its (edited) top-level products.
    const preservedProducts = mapCatalogueKitProductsToOrderShape(sourceKitProducts, ctx);
    const preservedTotal = preservedProducts.reduce(
      (accumulator, current) => Decimal(accumulator).plus(current.totalValue),
      0
    );
    const preservedSubKit = {
      subKitIndex,
      uid: null,
      // FAC-4861: keep the source UID only when the preserved entry carries products. An EMPTY
      // preserved entry (a picked kit with no top-level products) that still carried a source UID
      // would trip the backend's catalogue fan-out (_expand_sub_kits_from_catalogue) into
      // re-promoting the catalogue sub-kits we ALREADY send here — double-promotion. Nulling it
      // lets the empty entry pass through unchanged (the flatten is already done on the frontend).
      subKitUid: preservedProducts.length > 0 ? productKit?.uid || null : null,
      title: productKit?.name || "",
      quantity: 1,
      actualQuantity: 1,
      kitProducts: preservedProducts,
      deletedKitItems: [],
      productKitProducts: [],
      cost: 0,
      markup: 0,
      markupIsPercentage: true,
      margin: 0,
      isCompleted: false,
      value: Decimal(preservedTotal).toDecimalPlaces(4).toFixed(4),
      totalValue: Decimal(preservedTotal).toDecimalPlaces(2).toFixed(2),
    };

    // Promoted sub-kits — one per catalogue sub-kit of the picked kit, flattened to siblings.
    const promotedSubKits = mapCatalogueSubKitsToOrderShape(sourceSubKits, ctx).map(
      (subKit, offset) => ({
        ...subKit,
        subKitIndex: subKitIndex + 1 + offset,
      })
    );

    const subKits = [preservedSubKit, ...promotedSubKits];

    fulfilled({ itemIndex, subKitIndex, subKits });

    // if not custom priced kit then calculate markup for all kit items — same recalculation the
    // top-level addProductKit runs, so value == cost × (1 + markup/100) holds for the backend and
    // margin, sub-kit totals, and the parent kit roll-up are refreshed.
    if (!ctx.customPricing) {
      subKits.forEach((subKit, offset) => {
        subKit.kitProducts.forEach((kitProduct, kitIndex) => {
          dispatch(
            this.actions.changeSubKitItemPriceValue({
              itemIndex,
              subKitIndex: subKitIndex + offset,
              cartIndex: kitIndex,
              type: "value",
              value: kitProduct.value,
            })
          );
        });
      });
    }

    return subKits;
  };

  addOnTheFlyProductItem = ({ dispatch, fulfilled, getState }, data) => {
    const state = getState();
    const items = state.getIn(["additionalProducts", "items"]);
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const itemIndex = items.size;
    const newItem = this.initialItemState();

    newItem.additionalProduct = null;
    newItem.productType = PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL;
    newItem.isStockTrackedByCompany = false;
    newItem.isTaxFree = disableTaxByDefault || false;
    newItem.name = data?.name ?? "";
    newItem.description = data?.productDescription || "";
    newItem.productDescription = data?.productDescription || "";
    newItem.index = itemIndex;
    newItem.pricingStrategy = data.pricingStrategy;
    newItem.customFormula = null;
    newItem.additionalProductRow = null; // Change to a uid if saved to a product catalogue;
    newItem.categoryName = "";
    newItem.types = [];
    newItem.inventory = null;
    newItem.cost = 0;
    newItem.markup = 0;
    newItem.markupIsPercentage = true;
    newItem.params = [];
    newItem.rowPriceLevel = null;
    newItem.value = data?.value || 0;
    newItem.materialName = "";
    newItem.rowPriceLevel = null;

    if (newItem.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
      newItem.subitems.push({ amount: "1" });
      newItem.actualQuantity = data?.actualQuantity || "1";
      newItem.quantity = data?.quantity || "1";
      newItem.margin = 0;
      newItem.discount = data?.discount || "0";
    } else if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(newItem.pricingStrategy)
    ) {
      newItem.sumLengths = data?.sumLengths || { total: 0, forPrice: 0 };
      newItem.actualSumLengths = data?.actualSumLengths || { total: 0, forPrice: 0 };
      newItem.margin = 0;
      newItem.subitems = data?.subitems || [];
      newItem.actualLengthItems = data?.actualLengthItems || [];
      delete newItem.quantity;
      delete newItem.actualQuantity;
    }

    newItem.accountingItemName = "";

    fulfilled(fromJS(newItem));

    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex: itemIndex,
        type: "value",
        value: data?.value || 0,
      })
    );

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.tempUid === newItem.tempUid);
    return item;
  };

  addNotesLineItem = ({ getState, fulfilled }, data) => {
    const state = getState();
    const items = state.getIn(["additionalProducts", "items"]);
    const itemIndex = items.size;
    const newItem = this.initialItemState();

    newItem.notes = data?.notes || "";
    newItem.productType = PRODUCT_TYPE_NOTES_ITEM_LABEL;
    newItem.notesType = data.notesType;
    newItem.index = itemIndex;

    fulfilled(fromJS(newItem));

    return newItem;
  };

  addLabourLineItem = ({ dispatch, fulfilled, getState }, data) => {
    const state = getState();
    const items = state.getIn(["additionalProducts", "items"]);
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const itemIndex = items.size;
    const newItem = this.initialItemState();

    newItem.tempUid = nanoid();
    newItem.additionalProduct = null;
    newItem.productType = PRODUCT_TYPE_LABOUR_PRODUCT_LABEL;
    newItem.isStockTrackedByCompany = false;
    newItem.isTaxFree = disableTaxByDefault || false;
    newItem.name = `${data.firstName} ${data.lastName}`;
    newItem.description = "";
    newItem.index = itemIndex;
    newItem.pricingStrategy = PRICING_STRATEGY_BASIC_QUANTITIES_LABEL;
    newItem.customFormula = null;
    newItem.labourUser = data.id;
    newItem.additionalProductRow = null; // Change to a uid if saved to a product catalogue;
    newItem.categoryName = "";
    newItem.types = [];
    newItem.inventory = null;
    newItem.cost = data.hourlyRateCost;
    newItem.markup = 0;
    newItem.markupIsPercentage = true;
    newItem.params = [];
    newItem.rowPriceLevel = null;
    newItem.value = data.hourlyRateCharged;
    newItem.materialName = "";
    newItem.rowPriceLevel = null;
    newItem.subitems.push({ amount: "1" });
    // FAC-5897: a labour row's actual is hours worked, so it starts at zero, and the two fields
    // it is derived from start at zero with it.
    newItem.actualQuantity = LABOUR_INITIAL_ACTUAL_QUANTITY;
    newItem.totalTrackedTime = "0.0000";
    newItem.customActualQuantity = "0.0000";
    newItem.quantity = "1";
    newItem.margin = 0;

    newItem.accountingItemName = "";

    fulfilled(fromJS(newItem));
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex: itemIndex,
        type: "value",
        value: data.hourlyRateCharged,
      })
    );

    return newItem;
  };

  editLabourLineItem = ({ dispatch, fulfilled, getState }, data) => {
    const state = getState();
    const editItem = state.getIn(["additionalProducts", "editItem"]).toJS();

    const item = fromJS({
      ...editItem,
      labourUser: data.id,
      margin: 0,
      cost: data.hourlyRateCost,
      value: data.hourlyRateCharged,
      name: `${data.firstName} ${data.lastName}`,
      quantity: 1,
    });

    fulfilled({
      itemIndex: editItem.index,
      item,
    });

    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex: editItem.index,
        type: "value",
        value: data.hourlyRateCharged,
      })
    );

    return { ...item, index: editItem.index };
  };

  toggleProductIsTaxFree = ({ fulfilled }, { itemIndex, status }) => {
    fulfilled({ itemIndex, status });
  };

  toggleProductKitItemIsTaxFree = ({ fulfilled }, { itemIndex, cartIndex, status }) => {
    fulfilled({ itemIndex, cartIndex, status });
  };

  toggleSubKitItemIsTaxFree = ({ fulfilled }, { itemIndex, subKitIndex, cartIndex, status }) => {
    fulfilled({ itemIndex, subKitIndex, cartIndex, status });
  };

  setIsDuplicatingItemThunk = ({ fulfilled }, { indexes, productType, value }) => {
    if (productType === PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL) {
      const { itemIndex, cartIndex } = indexes;
      fulfilled({ itemIndex, cartIndex, productType, value });
    } else if (productType === PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL) {
      const { itemIndex, cartIndex, subKitIndex } = indexes;
      fulfilled({ itemIndex, cartIndex, subKitIndex, productType, value });
    } else {
      const { itemIndex } = indexes;
      fulfilled({ itemIndex, productType, value });
    }
  };

  duplicateItem = ({ getState, token, dispatch, fulfilled }, index) => {
    const state = getState();
    const itemIndex = state.getIn(["additionalProducts", "items"]).size;
    const duplicateItem = state.getIn(["additionalProducts", "items", index]).toJS();

    const newItem = {
      ...duplicateItem,
      uid: "",
      isDuplicating: false,
      tempUid: nanoid(),
      totalValue: "0.00",
      discount: "0.00",
      customValue: null,
      quantity: 1,
      // FAC-5897: a duplicate is a brand new row -- nobody has worked on the copy yet.
      actualQuantity: initialActualQuantity(duplicateItem.productType, 1),
      ...initialLabourTracking(duplicateItem.productType),
      margin: 0,
    };

    if (duplicateItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      return additionalProductsApi
        .getProduct(token, duplicateItem.additionalProduct)
        .then((response) => {
          const formattedResponse = camelcaseKeys(response.data, { deep: true });
          newItem.customFormula = {
            ...formattedResponse.customFormula,
            appliedElements: formattedResponse.customFormula.elements,
          };

          newItem.index = itemIndex;

          const formula = formattedResponse.customFormula.elements.map((e) => e.value).join("");
          const formulaOutput = evaluate(formula);
          const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
          const formattedFormulaOutput = formattedResponse.customFormula
            .isRoundedToNearestWholeNumber
            ? Math.round(roundedFormulaOutput)
            : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

          newItem.customFormulaOutput = formattedFormulaOutput;
          newItem.actualCustomFormulaOutput = formattedFormulaOutput;

          fulfilled(fromJS(newItem));
          dispatch(
            this.actions.changeProductPriceValue({
              itemIndex,
              type: "value",
              value: newItem.value,
            })
          );

          const orderOptions =
            getState()
              .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
              ?.toJS() ?? [];

          const groupByCategory = orderOptions.find(
            (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
          );

          if (groupByCategory?.isSelected) {
            dispatch(this.actions.sortByCategory());
          }

          // This is to get the latest index of the item after the item is sorted from the above action
          const items = getState().getIn(["additionalProducts", "items"]).toJS();
          const item = items.find((item) => item.tempUid === newItem.tempUid);
          return new Promise((resolve) => resolve(item));
        });
    }

    if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(duplicateItem.pricingStrategy)
    ) {
      newItem.sumLengths = { total: 0, forPrice: 0 };
      newItem.subitems = [];
      newItem.actualSumLengths = { total: 0, forPrice: 0 };
      newItem.actualLengthItems = [];
      delete newItem.quantity;
      delete newItem.actualQuantity;
    }
    newItem.index = itemIndex;
    fulfilled(fromJS(newItem));
    if (duplicateItem.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL) {
      dispatch(
        this.actions.changeProductPriceValue({
          itemIndex,
          type: "value",
          value: newItem.value,
        })
      );
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    const items = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = items.find((item) => item.tempUid === newItem.tempUid);
    return new Promise((resolve) => resolve(item));
  };

  duplicateKit = ({ getState, fulfilled, dispatch }, data) => {
    const state = getState();
    const { index } = data;
    const itemIndex = state.getIn(["additionalProducts", "items"]).size;
    const item = state.getIn(["additionalProducts", "items", index]).toJS();

    const newItem = {
      ...item,
      uid: "",
      isDuplicating: false,
      tempUid: nanoid(),
      actualQuantity: 1,
      quantity: 1,
      totalValue: parseFloat(item.totalValue) / parseFloat(item.quantity),
      productKitItems: item.productKitItems.map((p) => {
        const quantity =
          parseFloat(p.quantity) === 1 ? 1 : parseFloat(p.quantity) / parseFloat(item.quantity);
        const newProductKitem = {
          ...p,
          uid: "",
          tempUid: nanoid(),
          totalValue: 0,
          quantity,
          // FAC-5897: a duplicate is a brand new row -- nobody has worked on the copy yet.
          actualQuantity: initialActualQuantity(p.productType, quantity),
          ...initialLabourTracking(p.productType),
          sumLengths: { total: 0, forPrice: 0 },
          actualSumlengths: { total: 0, forPrice: 0 },
          discount: 0,
          lengthItems: [],
          actualLengthItems: [],
          initialLengthItems: [],
          cost: p?.cost ?? 0,
          markup: item.customPricing ? 0 : p.markup,
          margin: 0,
        };

        if (p.product.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
          newProductKitem.customFormula = {
            ...p.customFormula,
            appliedElements: p.customFormula.elements,
            uid: null,
            additionalProductUid: null,
            kitProductUid: null,
          };

          const formula = p.customFormula.elements.map((e) => e.value).join("");
          const formulaOutput = evaluate(formula);
          const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
          const formattedFormulaOutput = p.customFormula.isRoundedToNearestWholeNumber
            ? Math.round(roundedFormulaOutput)
            : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

          newProductKitem.customFormulaOutput = formattedFormulaOutput;
          newProductKitem.actualCustomFormulaOutput = formattedFormulaOutput;
        }

        return newProductKitem;
      }),
      subKits: item.subKits.map((s) => {
        const subKitQuantity =
          parseFloat(s.quantity) === 1 ? 1 : parseFloat(s.quantity) / parseFloat(item.quantity);
        return {
          ...s,
          uid: "",
          tempUid: nanoid(),
          quantity: 1,
          actualQuantity: 1,
          totalValue: parseFloat(s.totalValue) / parseFloat(subKitQuantity),
          kitProducts: s.kitProducts.map((p) => {
            const quantity = parseFloat(p.quantity) === 1 ? 1 : parseFloat(p.quantity) / s.quantity;
            const newSubKitItem = {
              ...p,
              uid: "",
              tempUid: nanoid(),
              totalValue: 0,
              quantity,
              // FAC-5897: a duplicate is a brand new row -- nobody has worked on the copy yet.
              actualQuantity: initialActualQuantity(p.productType, quantity),
              ...initialLabourTracking(p.productType),
              sumLengths: { total: 0, forPrice: 0 },
              actualSumlengths: { total: 0, forPrice: 0 },
              discount: 0,
              lengthItems: [],
              actualLengthItems: [],
              initialLengthItems: [],
              cost: p?.cost ?? 0,
              markup: item.customPricing ? 0 : p.markup,
              margin: 0,
            };

            if (p.product.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
              newSubKitItem.customFormula = {
                ...p.customFormula,
                appliedElements: p.customFormula.elements,
                uid: null,
                additionalProductUid: null,
                kitProductUid: null,
              };

              const formula = p.customFormula.elements.map((e) => e.value).join("");
              const formulaOutput = evaluate(formula);
              const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
              const formattedFormulaOutput = p.customFormula.isRoundedToNearestWholeNumber
                ? Math.round(roundedFormulaOutput)
                : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

              newSubKitItem.customFormulaOutput = formattedFormulaOutput;
              newSubKitItem.actualCustomFormulaOutput = formattedFormulaOutput;
            }

            return newSubKitItem;
          }),
        };
      }),
    };

    newItem.index = itemIndex;
    fulfilled(fromJS(newItem));
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: newItem.value,
      })
    );

    if (!item.customPricing) {
      newItem.productKitItems.forEach((kitItem, cartIndex) => {
        dispatch(
          this.actions.changeProductKitItemPriceValue({
            itemIndex: itemIndex,
            cartIndex,
            type: "value",
            value: kitItem.value,
          })
        );
      });

      newItem.subKits.forEach((subKit, subKitIndex) => {
        subKit.kitProducts.forEach((kitProduct, kitIndex) => {
          dispatch(
            this.actions.changeSubKitItemPriceValue({
              itemIndex: itemIndex,
              subKitIndex,
              cartIndex: kitIndex,
              type: "value",
              value: kitProduct.value,
            })
          );
        });
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const lineItem = lineItems.find((item) => item.tempUid === newItem.tempUid);
    return new Promise((resolve) => resolve(lineItem));
  };

  duplicateKitItem = ({ getState, fulfilled, dispatch }, data) => {
    const state = getState();
    const { itemIndex, cartIndex } = data;
    const items = state.getIn(["additionalProducts", "items", itemIndex, "productKitItems"]).toJS();
    const productKit = state.getIn(["additionalProducts", "items", itemIndex]).toJS();
    const duplicateItem = state
      .getIn(["additionalProducts", "items", itemIndex, "productKitItems", cartIndex])
      .toJS();

    const newItem = {
      ...duplicateItem,
      isDuplicating: false,
      uid: null,
      tempUid: nanoid(),
      totalValue: "0.00",
      quantity: "1",
      // FAC-5897: a duplicate is a brand new row -- nobody has worked on the copy yet.
      actualQuantity: initialActualQuantity(duplicateItem.productType, "1"),
      ...initialLabourTracking(duplicateItem.productType),
      sumLengths: { total: 0, forPrice: 0 },
      actualSumlengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      actualLengthItems: [],
      initialLengthItems: [],
    };

    if (duplicateItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      newItem.customFormula = {
        ...duplicateItem.customFormula,
        appliedElements: duplicateItem.customFormula.elements,
        uid: null,
        additionalProductUid: null,
        kitProductUid: null,
      };
      newItem.index = items.length;

      const formula = duplicateItem.customFormula.elements.map((e) => e.value).join("");
      const formulaOutput = evaluate(formula);
      const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
      const formattedFormulaOutput = duplicateItem.customFormula.isRoundedToNearestWholeNumber
        ? Math.round(roundedFormulaOutput)
        : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

      newItem.customFormulaOutput = formattedFormulaOutput;
      newItem.actualCustomFormulaOutput = formattedFormulaOutput;
    }

    const newItems = [...items, newItem];

    fulfilled({ itemIndex, items: fromJS(newItems) });

    if (duplicateItem.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL) {
      dispatch(
        this.actions.changeProductKitItemPriceValue({
          itemIndex,
          cartIndex: newItems.length - 1,
          type: "value",
          value: newItem.value,
        })
      );
    }

    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    return new Promise((resolve) => resolve({ index: itemIndex }));
  };

  duplicateSubKitItem = ({ getState, fulfilled, dispatch }, data) => {
    const state = getState();
    const { itemIndex, subKitIndex, cartIndex } = data;
    const items = state
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const productKit = state.getIn(["additionalProducts", "items", itemIndex]).toJS();
    const duplicateItem = state
      .getIn([
        "additionalProducts",
        "items",
        itemIndex,
        "subKits",
        subKitIndex,
        "kitProducts",
        cartIndex,
      ])
      .toJS();

    const newItem = {
      ...duplicateItem,
      uid: "",
      isDuplicating: false,
      tempUid: nanoid(),
      totalValue: "0.00",
      quantity: "1",
      // FAC-5897: a duplicate is a brand new row -- nobody has worked on the copy yet.
      actualQuantity: initialActualQuantity(duplicateItem.productType, "1"),
      ...initialLabourTracking(duplicateItem.productType),
      sumLengths: { total: 0, forPrice: 0 },
      actualSumlengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      actualLengthItems: [],
      initialLengthItems: [],
    };

    if (duplicateItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      newItem.customFormula = {
        ...duplicateItem.customFormula,
        appliedElements: duplicateItem.customFormula.elements,
        uid: null,
        additionalProductUid: null,
        kitProductUid: null,
      };
      newItem.index = items.length;

      const formula = duplicateItem.customFormula.elements.map((e) => e.value).join("");
      const formulaOutput = evaluate(formula);
      const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
      const formattedFormulaOutput = duplicateItem.customFormula.isRoundedToNearestWholeNumber
        ? Math.round(roundedFormulaOutput)
        : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

      newItem.customFormulaOutput = formattedFormulaOutput;
      newItem.actualCustomFormulaOutput = formattedFormulaOutput;
    }

    const newItems = [...items, newItem];

    fulfilled({ itemIndex, subKitIndex, items: fromJS(newItems) });

    dispatch(
      this.actions.changeSubKitItemPriceValue({
        itemIndex,
        subKitIndex,
        cartIndex: newItems.length - 1,
        type: "value",
        value: newItem.value,
      })
    );

    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    return new Promise((resolve) => resolve({ index: itemIndex }));
  };

  addItemToKit = ({ getState, dispatch, fulfilled }, { productKit, payload, itemIndex }) => {
    const state = getState();

    const items = state.getIn(["additionalProducts", "items", itemIndex, "productKitItems"]).toJS();
    const categories = state.getIn(["additionalProducts", "categories"]).toJS();
    const product = state.getIn(["additionalProducts", "activeProduct"]).toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);

    const { customPricing, isMarginTrackedByCompany } = productKit;
    const { material, row, cellId, isMarkupValue } = payload;

    const category = categories?.find((category) => category.uid === product.categoryUid) || {};
    const companyId = productKit.productKitItems?.[0]?.product?.company ?? null;

    const kitItemPrice = isMarkupValue ? row.markedUpPrice : row.rowPrices[cellId]?.value || 0;
    const quantity = 1;
    const pricingStrategy = Number.isInteger(product.pricingStrategy)
      ? PRICING_STRATEGY[product.pricingStrategy]
      : product.pricingStrategy;

    const newItemKit = {
      customValue: null,
      customValueLm: null,
      discount: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || product.isTaxFree,
      isDeleted: false,
      lengthItems: [],
      actualLengthItems: [],
      initialLengthItems: [],
      sumLengths: { total: 0, forPrice: 0 },
      actualSumLengths: { total: 0, forPrice: 0 },
      pricingStrategy,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_ADDITIONAL_PRODUCT],
      productName: product.name,
      poCreated: false,
      types: row.types ? row.types.map((p) => ({ ...p, value: p.value || "" })) : [],
      rowPriceLevel: isMarkupValue ? null : row.rowPrices[cellId]?.uid,
      value: customPricing ? 0 : kitItemPrice,
      totalValue: 0,
      cost: (customPricing && isMarginTrackedByCompany) || !customPricing ? (row?.cost ?? 0) : 0,
      markup: customPricing ? 0 : row.markup,
      markupIsPercentage: row.markupIsPercentage,
      margin: 0,
      productRow: {
        material: "",
        colourOptions: null,
        colour: null,
        uid: null,
        types: [],
        rowPrices: [],
        inventory: null,
        accountingItem: null,
      },
      product: {
        category: category?.name,
        company: companyId,
        categoryUid: product.categoryUid,
        customFormula: product.customFormula,
        image: product.image,
        inLength: product.inLength,
        isDeleted: product.isDeleted,
        isStockTrackedByCompany: product.isStockTrackedByCompany,
        isMarginTrackedByCompany: product.isMarginTrackedByCompany,
        isTaxFree: disableTaxByDefault || product.isTaxFree,
        name: product.name,
        pricingStrategy: product.pricingStrategy,
        uid: product.uid,
      },
      customFormula: product.customFormula,
      cartIndex: items.length,
    };

    if (
      [PRICING_STRATEGY_BASIC_QUANTITIES_LABEL, PRICING_STRATEGY_CUSTOM_FORMULA_LABEL].includes(
        newItemKit.pricingStrategy
      )
    ) {
      newItemKit.actualQuantity = quantity;
      newItemKit.quantity = quantity;
      newItemKit.baseQuantity = quantity;
      newItemKit.margin =
        Decimal(newItemKit.cost) > 0
          ? Decimal(newItemKit.value).minus(newItemKit.cost).toDecimalPlaces(2).toFixed(2)
          : 0;
      newItemKit.totalValue = Decimal(newItemKit.value)
        .times(quantity)
        .toDecimalPlaces(2)
        .toFixed(2);

      if (newItemKit.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const { customFormula } = product;
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        newItemKit.customFormula = { ...customFormula, appliedElements: customFormula.elements };
        newItemKit.customFormulaOutput = formattedFormulaOutput;
        newItemKit.margin =
          Decimal(newItemKit.cost) > 0
            ? Decimal(newItemKit.value)
                .minus(Decimal(newItemKit.cost).times(formattedFormulaOutput))
                .toDecimalPlaces(2)
                .toFixed(2)
            : 0;
        newItemKit.actualCustomFormulaOutput = formattedFormulaOutput;
        newItemKit.totalValue = customPricing
          ? 0
          : Decimal(newItemKit.value)
              .times(newItemKit.quantity)
              .times(Decimal(roundedFormulaOutput).toDecimalPlaces(4))
              .toDecimalPlaces(2)
              .toFixed(2);
      }
    } else if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(newItemKit.pricingStrategy)
    ) {
      delete newItemKit.quantity;
      delete newItemKit.baseQuantity;
      delete newItemKit.actualQuantity;
    }

    if (material?.uid) {
      newItemKit.productRow.material = material.name;

      if (material.colours?.length > 0) {
        newItemKit.productRow.colourOptions = orderBy(
          material.colours,
          [(colour) => colour],
          ["asc"]
        ).map((colour) => ({
          label: colour,
          value: colour,
        }));
        newItemKit.productRow.colour = row?.params[0]?.value ?? "";
      }
    }

    if (row) {
      newItemKit.productRow = {
        ...newItemKit.productRow,
        types: row.types,
        rowPrices: row.rowPrices,
        uid: row.uid,
        inventory: row.inventory,
        accountingItem: row.accountingItem,
      };
    }

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, items: fromJS(newKitItems) });

    // if not custom priced kit then calculate markup for all kit items
    if (!customPricing) {
      dispatch(
        this.actions.changeProductKitItemQuantity({
          itemIndex,
          cartIndex: newKitItems.length - 1,
          quantity,
        })
      );

      newKitItems.forEach(({ value }, cartIndex) => {
        dispatch(
          this.actions.changeProductKitItemPriceValue({
            itemIndex,
            cartIndex,
            type: "value",
            value,
          })
        );
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    if (customPricing && isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return new Promise((resolve) => resolve(item));
  };

  addOnTheFlyItemToKit = (
    { dispatch, fulfilled, getState },
    { productKit, pricingStrategy, itemIndex, productName }
  ) => {
    const state = getState();
    const items = state.getIn(["additionalProducts", "items", itemIndex, "productKitItems"]).toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const { customPricing } = productKit;

    const quantity = 1;
    const newItemKit = {
      customValue: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || false,
      markup: 0,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_ON_THE_FLY_PRODUCT],
      quantity,
      baseQuantity: quantity,
      actualQuantity: quantity,
      markupIsPercentage: true,
      actualLengthItems: [],
      initialLengthItems: [],
      totalValue: 0,
      productDescription: "",
      cost: 0,
      margin: 0,
      isDeleted: false,
      value: 0,
      customFormula: null,
      rowPriceLevel: null,
      types: [],
      pricingStrategy,
      productName,
      discount: null,
      customValueLm: null,
      sumLengths: { total: 0, forPrice: 0 },
      formulaAnswer: null,
      poCreated: false,
      supplierForCreatedPo: null,
      productRow: {},
      product: {},
      actualSumLengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      isSavingToProductCatalogue: false,
      cartIndex: items.length,
    };

    if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(pricingStrategy)
    ) {
      delete newItemKit.quantity;
      delete newItemKit.baseQuantity;
      delete newItemKit.actualQuantity;
    }

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, items: fromJS(newKitItems) });

    // if not custom priced kit then calculate markup for all kit items
    if (!customPricing) {
      dispatch(
        this.actions.changeProductKitItemQuantity({
          itemIndex,
          cartIndex: newKitItems.length - 1,
          quantity,
        })
      );
      newKitItems.forEach(({ value }, cartIndex) => {
        dispatch(
          this.actions.changeProductKitItemPriceValue({
            itemIndex,
            cartIndex,
            type: "value",
            value,
          })
        );
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since product kit
    // is already existing before adding the on the fly line item
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  addLabourLineItemToKit = (
    { dispatch, fulfilled, getState },
    { productKit, itemIndex, labour }
  ) => {
    const state = getState();
    const items = state.getIn(["additionalProducts", "items", itemIndex, "productKitItems"]).toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const { customPricing, isMarginTrackedByCompany } = productKit;

    const quantity = 1;
    const newItemKit = {
      tempUid: nanoid(),
      customValue: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || false,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_LABOUR_ITEM],
      quantity,
      baseQuantity: quantity,
      // FAC-5897: a labour row's actual is hours worked, so it starts at zero, and the two fields
      // it is derived from start at zero with it.
      actualQuantity: LABOUR_INITIAL_ACTUAL_QUANTITY,
      totalTrackedTime: "0.0000",
      customActualQuantity: "0.0000",
      markupIsPercentage: true,
      actualLengthItems: [],
      initialLengthItems: [],
      totalValue: labour.hourlyRateCharged,
      productDescription: "",
      cost:
        (customPricing && isMarginTrackedByCompany) || !customPricing
          ? (labour?.hourlyRateCost ?? 0)
          : 0,
      markup: 0,
      margin: 0,
      isDeleted: false,
      value: labour.hourlyRateCharged,
      hourlyRateCost: labour.hourlyRateCost,
      hourlyRateCharged: labour.hourlyRateCharged,
      customFormula: null,
      rowPriceLevel: null,
      types: [],
      pricingStrategy: PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
      productName: `${labour.firstName} ${labour.lastName}`,
      discount: null,
      customValueLm: null,
      sumLengths: { total: 0, forPrice: 0 },
      formulaAnswer: null,
      poCreated: false,
      supplierForCreatedPo: null,
      productRow: {},
      product: {},
      actualSumLengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      labourUser: labour.id,
      cartIndex: items.length,
    };

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, items: fromJS(newKitItems) });

    // if not custom priced kit then calculate markup for all kit items

    if (!customPricing) {
      dispatch(
        this.actions.changeProductKitItemQuantity({
          itemIndex,
          cartIndex: newKitItems.length - 1,
          quantity,
        })
      );

      newKitItems.forEach(({ value }, cartIndex) => {
        dispatch(
          this.actions.changeProductKitItemPriceValue({
            itemIndex,
            cartIndex,
            type: "value",
            value,
          })
        );
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    if (customPricing && isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since product kit
    // is already existing before adding the labour line item
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  addNotesLineItemToKit = ({ dispatch, fulfilled, getState }, { notesType, itemIndex }) => {
    if (!NOTES_TYPES_TO_INT[notesType]) {
      return;
    }

    const state = getState();
    const productKit = state.getIn(["additionalProducts", "items", itemIndex]).toJS();
    const items = state.getIn(["additionalProducts", "items", itemIndex, "productKitItems"]).toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const newItemKit = {
      tempUid: nanoid(),
      customValue: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || false,
      markup: 0,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_NOTES_ITEM],
      markupIsPercentage: true,
      actualLengthItems: [],
      initialLengthItems: [],
      totalValue: 0,
      productDescription: "",
      cost: 0,
      margin: 0,
      isDeleted: false,
      value: 0,
      customFormula: null,
      rowPriceLevel: null,
      types: [],
      pricingStrategy: PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
      productName: "",
      discount: null,
      customValueLm: null,
      sumLengths: { total: 0, forPrice: 0 },
      formulaAnswer: null,
      poCreated: false,
      supplierForCreatedPo: null,
      productRow: {},
      product: {},
      notesType: NOTES_TYPES_TO_INT[notesType],
      notes: "",
      actualSumLengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      quantity: 0,
      baseQuantity: 0,
      actualQuantity: 0,
      cartIndex: items.length,
    };

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, items: fromJS(newKitItems) });

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since product kit
    // is already existing before adding the notes line item
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  addOnTheFlyItemToSubKit = (
    { dispatch, fulfilled, getState },
    { productKit, pricingStrategy, itemIndex, subKitIndex, productName }
  ) => {
    const state = getState();
    const items = state
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const { customPricing } = productKit;

    const quantity = 1;
    const newItemKit = {
      customValue: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || false,
      markup: 0,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_ON_THE_FLY_PRODUCT],
      quantity,
      baseQuantity: quantity,
      actualQuantity: quantity,
      markupIsPercentage: true,
      actualLengthItems: [],
      initialLengthItems: [],
      totalValue: 0,
      productDescription: "",
      cost: 0,
      margin: 0,
      isDeleted: false,
      value: 0,
      customFormula: null,
      rowPriceLevel: null,
      types: [],
      pricingStrategy,
      productName,
      discount: null,
      customValueLm: null,
      sumLengths: { total: 0, forPrice: 0 },
      formulaAnswer: null,
      poCreated: false,
      supplierForCreatedPo: null,
      productRow: {},
      product: {},
      actualSumLengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      isSavingToProductCatalogue: false,
      cartIndex: items.length,
    };

    if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(pricingStrategy)
    ) {
      delete newItemKit.quantity;
      delete newItemKit.baseQuantity;
      delete newItemKit.actualQuantity;
    }

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, subKitIndex, items: fromJS(newKitItems) });

    // if not custom priced kit then calculate markup for all kit items
    if (!customPricing) {
      dispatch(
        this.actions.changeSubKitItemQuantity({
          itemIndex,
          subKitIndex,
          cartIndex: newKitItems.length - 1,
          quantity,
        })
      );
      newKitItems.forEach(({ value }, cartIndex) => {
        dispatch(
          this.actions.changeSubKitItemPriceValue({
            itemIndex,
            subKitIndex,
            cartIndex,
            type: "value",
            value,
          })
        );
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since sub kit
    // is already existing before adding the on the fly line item
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  addLabourLineItemToSubKit = (
    { dispatch, fulfilled, getState },
    { productKit, itemIndex, subKitIndex, labour }
  ) => {
    const state = getState();
    const items = state
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);
    const { customPricing, isMarginTrackedByCompany } = productKit;

    const quantity = 1;
    const newItemKit = {
      tempUid: nanoid(),
      customValue: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || false,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_LABOUR_ITEM],
      quantity,
      baseQuantity: quantity,
      // FAC-5897: a labour row's actual is hours worked, so it starts at zero, and the two fields
      // it is derived from start at zero with it.
      actualQuantity: LABOUR_INITIAL_ACTUAL_QUANTITY,
      totalTrackedTime: "0.0000",
      customActualQuantity: "0.0000",
      markupIsPercentage: true,
      actualLengthItems: [],
      initialLengthItems: [],
      totalValue: labour.hourlyRateCharged,
      productDescription: "",
      cost:
        (customPricing && isMarginTrackedByCompany) || !customPricing
          ? (labour?.hourlyRateCost ?? 0)
          : 0,
      markup: 0,
      margin: 0,
      isDeleted: false,
      value: labour.hourlyRateCharged,
      hourlyRateCost: labour.hourlyRateCost,
      hourlyRateCharged: labour.hourlyRateCharged,
      customFormula: null,
      rowPriceLevel: null,
      types: [],
      pricingStrategy: PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
      productName: `${labour.firstName} ${labour.lastName}`,
      discount: null,
      customValueLm: null,
      sumLengths: { total: 0, forPrice: 0 },
      formulaAnswer: null,
      poCreated: false,
      supplierForCreatedPo: null,
      productRow: {},
      product: {},
      actualSumLengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      labourUser: labour.id,
      cartIndex: items.length,
    };

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, subKitIndex, items: fromJS(newKitItems) });

    // if not custom priced kit then calculate markup for all kit items

    if (!customPricing) {
      dispatch(
        this.actions.changeSubKitItemQuantity({
          itemIndex,
          subKitIndex,
          cartIndex: newKitItems.length - 1,
          quantity,
        })
      );

      newKitItems.forEach(({ value }, cartIndex) => {
        dispatch(
          this.actions.changeSubKitItemPriceValue({
            itemIndex,
            subKitIndex,
            cartIndex,
            type: "value",
            value,
          })
        );
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    if (customPricing && isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since sub kit
    // is already existing before adding the labour line item
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  addNotesLineItemToSubKit = (
    { dispatch, fulfilled, getState },
    { notesType, itemIndex, subKitIndex }
  ) => {
    const state = getState();
    const productKit = state.getIn(["additionalProducts", "items", itemIndex]).toJS();
    const items = state
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);

    const newItemKit = {
      customValue: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || false,
      markup: 0,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_NOTES_ITEM],
      markupIsPercentage: true,
      actualLengthItems: [],
      initialLengthItems: [],
      totalValue: 0,
      productDescription: "",
      cost: 0,
      margin: 0,
      isDeleted: false,
      value: 0,
      customFormula: null,
      rowPriceLevel: null,
      types: [],
      pricingStrategy: PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
      productName: "",
      discount: null,
      customValueLm: null,
      sumLengths: { total: 0, forPrice: 0 },
      formulaAnswer: null,
      poCreated: false,
      supplierForCreatedPo: null,
      productRow: {},
      product: {},
      notesType: NOTES_TYPES_TO_INT[notesType],
      notes: "",
      actualSumLengths: { total: 0, forPrice: 0 },
      lengthItems: [],
      quantity: 0,
      baseQuantity: 0,
      actualQuantity: 0,
      cartIndex: items.length,
    };

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, subKitIndex, items: fromJS(newKitItems) });

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since sub kit
    // is already existing before adding the notes line item
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  addItemToSubKit = (
    { getState, dispatch, fulfilled },
    { productKit, payload, itemIndex, subKitIndex }
  ) => {
    const state = getState();

    const items = state
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const categories = state.getIn(["additionalProducts", "categories"]).toJS();
    const product = state.getIn(["additionalProducts", "activeProduct"]).toJS();
    const disableTaxByDefault = state.getIn(["customers", "activeCustomer", "disableTaxByDefault"]);

    const { customPricing, isMarginTrackedByCompany } = productKit;
    const { material, row, cellId, isMarkupValue } = payload;

    const category = categories?.find((category) => category.uid === product.categoryUid) || {};
    const companyId = productKit.productKitItems?.[0]?.product?.company ?? null;

    const kitItemPrice = isMarkupValue ? row.markedUpPrice : row.rowPrices[cellId]?.value || 0;
    const quantity = 1;
    const pricingStrategy = Number.isInteger(product.pricingStrategy)
      ? PRICING_STRATEGY[product.pricingStrategy]
      : product.pricingStrategy;

    const newItemKit = {
      customValue: null,
      customValueLm: null,
      discount: null,
      isCompleted: false,
      isTaxFree: disableTaxByDefault || product.isTaxFree,
      isDeleted: false,
      lengthItems: [],
      actualLengthItems: [],
      initialLengthItems: [],
      sumLengths: { total: 0, forPrice: 0 },
      actualSumLengths: { total: 0, forPrice: 0 },
      pricingStrategy,
      productType: PRODUCT_TYPES[PRODUCT_TYPE_ADDITIONAL_PRODUCT],
      productName: product.name,
      poCreated: false,
      types: row.types ? row.types.map((p) => ({ ...p, value: p.value || "" })) : [],
      rowPriceLevel: isMarkupValue ? null : row.rowPrices[cellId]?.uid,
      value: customPricing ? 0 : kitItemPrice,
      totalValue: 0,
      cost: (customPricing && isMarginTrackedByCompany) || !customPricing ? (row?.cost ?? 0) : 0,
      markup: customPricing ? 0 : row.markup,
      markupIsPercentage: row.markupIsPercentage,
      margin: 0,
      productRow: {
        material: "",
        colourOptions: null,
        colour: null,
        uid: null,
        types: [],
        rowPrices: [],
        inventory: null,
        accountingItem: null,
      },
      product: {
        category: category?.name,
        company: companyId,
        categoryUid: product.categoryUid,
        customFormula: product.customFormula,
        image: product.image,
        inLength: product.inLength,
        isDeleted: product.isDeleted,
        isStockTrackedByCompany: product.isStockTrackedByCompany,
        isMarginTrackedByCompany: product.isMarginTrackedByCompany,
        isTaxFree: disableTaxByDefault || product.isTaxFree,
        name: product.name,
        pricingStrategy: product.pricingStrategy,
        uid: product.uid,
      },
      customFormula: product.customFormula,
      cartIndex: items.length,
    };

    if (
      [PRICING_STRATEGY_BASIC_QUANTITIES_LABEL, PRICING_STRATEGY_CUSTOM_FORMULA_LABEL].includes(
        newItemKit.pricingStrategy
      )
    ) {
      newItemKit.actualQuantity = quantity;
      newItemKit.quantity = quantity;
      newItemKit.baseQuantity = quantity;
      newItemKit.margin =
        Decimal(newItemKit.cost) > 0
          ? Decimal(newItemKit.value).minus(newItemKit.cost).toDecimalPlaces(2).toFixed(2)
          : 0;
      newItemKit.totalValue = Decimal(newItemKit.value)
        .times(quantity)
        .toDecimalPlaces(2)
        .toFixed(2);

      if (newItemKit.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
        const { customFormula } = product;
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        newItemKit.customFormula = { ...customFormula, appliedElements: customFormula.elements };
        newItemKit.customFormulaOutput = formattedFormulaOutput;
        newItemKit.actualCustomFormulaOutput = formattedFormulaOutput;
        newItemKit.margin =
          Decimal(newItemKit.cost) > 0
            ? Decimal(newItemKit.value)
                .minus(Decimal(newItemKit.cost).times(formattedFormulaOutput))
                .toDecimalPlaces(2)
                .toFixed(2)
            : 0;
        newItemKit.totalValue = customPricing
          ? 0
          : Decimal(newItemKit.value)
              .times(newItemKit.quantity)
              .times(Decimal(roundedFormulaOutput).toDecimalPlaces(4))
              .toDecimalPlaces(2)
              .toFixed(2);
      }
    } else if (
      [
        PRICING_STRATEGY_LINEAL_METRES_LABEL,
        PRICING_STRATEGY_SQUARE_METRES_LABEL,
        PRICING_STRATEGY_LINEAL_FEET_LABEL,
        PRICING_STRATEGY_SQUARE_FEET_LABEL,
      ].includes(newItemKit.pricingStrategy)
    ) {
      delete newItemKit.quantity;
      delete newItemKit.baseQuantity;
      delete newItemKit.actualQuantity;
    }

    if (material?.uid) {
      newItemKit.productRow.material = material.name;

      if (material?.colours?.length > 0) {
        newItemKit.productRow.colourOptions = orderBy(
          material.colours,
          [(colour) => colour],
          ["asc"]
        ).map((colour) => ({
          label: colour,
          value: colour,
        }));
        newItemKit.productRow.colour = row?.params[0]?.value ?? "";
      }
    }

    if (row) {
      newItemKit.productRow = {
        ...newItemKit.productRow,
        types: row.types,
        rowPrices: row.rowPrices,
        uid: row.uid,
        inventory: row.inventory,
        accountingItem: row.accountingItem,
      };
    }

    const newKitItems = [...items, newItemKit];

    fulfilled({ itemIndex, subKitIndex, items: fromJS(newKitItems) });

    // if not custom priced kit then calculate markup for all kit items
    if (!customPricing) {
      dispatch(
        this.actions.changeSubKitItemQuantity({
          itemIndex,
          subKitIndex,
          cartIndex: newKitItems.length - 1,
          quantity,
        })
      );

      newKitItems.forEach(({ value }, cartIndex) => {
        dispatch(
          this.actions.changeSubKitItemPriceValue({
            itemIndex,
            subKitIndex,
            cartIndex,
            type: "value",
            value,
          })
        );
      });
    }

    const orderOptions =
      getState()
        .getIn(["customers", "company", "settings", "ordersSettings", "orderOptions"])
        ?.toJS() ?? [];

    const groupByCategory = orderOptions.find(
      (setting) => setting.key === "GROUP_PRODUCT_BY_CATEGORY"
    );

    if (groupByCategory?.isSelected) {
      dispatch(this.actions.sortByCategory());
    }

    if (customPricing && isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    // This is to get the latest index of the item after the item is sorted from the above action
    // We use the uid to find the item because the tempUid is not applicable since sub kit
    // is already existing before adding the item to sub kit
    const lineItems = getState().getIn(["additionalProducts", "items"]).toJS();
    const item = lineItems.find((item) => item.uid === productKit.uid);
    return item;
  };

  saveProductEdit = (
    { getState, dispatch, fulfilled },
    { material, row, cellId, isMarkupValue }
  ) => {
    const state = getState();
    const editItem = state.getIn(["additionalProducts", "editItem"]).toJS();
    const itemIndex = state
      .getIn(["additionalProducts", "items"])
      .findIndex((item) => item.getIn(["uid"]) === editItem.uid);
    editItem.additionalProductRow = row.uid;
    if (row.params) {
      editItem.params = row.params;
    }

    if (isMarkupValue) {
      editItem.rowPriceLevel = null;
      editItem.value = row.markedUpPrice;
    } else {
      const { uid: rowPriceUid, value: priceValue } = row.rowPrices[cellId];
      editItem.rowPriceLevel = rowPriceUid;
      editItem.value = priceValue;
    }

    editItem.categoryName = state.getIn(["additionalProducts", "activeCategory", "name"]);
    editItem.inventory = row.inventory;

    editItem.types = row.types;
    if (material?.uid) {
      editItem.materialName = material.name;
    }
    const withAccounting = state.getIn(["customers", "company", "accountingSyncAllowed"]);
    if (withAccounting) {
      const displayField =
        state.getIn([
          "customers",
          "company",
          "settings",
          "accountingSettings",
          "accountingItemDisplayField",
        ]) || "name";
      editItem.accountingItemName = getAccountingItemLabel(row.accountingItem, displayField) ?? "";
    }
    fulfilled({ editItem, itemIndex });
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: editItem.value,
      })
    );
  };

  changeQuantityThunk = ({ getState, dispatch, fulfilled }, { itemIndex, value }) => {
    fulfilled({ itemIndex, value });

    const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: price,
      })
    );
  };

  changeActualQuantityThunk = ({ getState, dispatch, fulfilled }, { itemIndex, value }) => {
    const item = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      if (
        parseFloat(item.customActualQuantity || "0.0000") === 0 &&
        parseFloat(item.totalTrackedTime || "0.0000") === 0
      ) {
        fulfilled({
          itemIndex,
          value,
          customActualQuantity: value,
        });
      } else {
        const totalTrackedTime = item.totalTrackedTime
          ? parseFloat(item.totalTrackedTime || "0.0000")
          : 0;
        const customActualQuantity =
          parseFloat(value) -
          item.actualQuantity +
          parseFloat(item.customActualQuantity || "0.0000");
        const actualQuantity = totalTrackedTime + parseFloat(customActualQuantity);
        fulfilled({
          itemIndex,
          value: actualQuantity,
          customActualQuantity,
        });
      }
    } else {
      fulfilled({ itemIndex, value });
    }

    const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: price,
      })
    );
  };

  changeActualQuantityInventoryThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, value }
  ) => {
    // Simple thunk for inventory-only quantity changes
    // This field affects stock levels but not margin calculations
    fulfilled({ itemIndex, value });

    const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
    dispatch(
      this.actions.changeProductPriceValue({
        itemIndex,
        type: "value",
        value: price,
      })
    );
  };

  changeProductKitQuantityThunk = ({ getState, dispatch, fulfilled }, { itemIndex, value }) => {
    const currentKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    const subKits = getState().getIn(["additionalProducts", "items", itemIndex, "subKits"]).toJS();
    const kitItems = getState()
      .getIn(["additionalProducts", "items", itemIndex, "productKitItems"])
      .toJS();
    const minLength = getMinLengthProducts(getState());
    const orderSettings = getState()
      .getIn(["customers", "company", "settings", "ordersSettings"])
      .toJS();
    const duplicateLinealSQMOnKit =
      orderSettings?.orderOptions.find(
        (s) => s.key === "DUPLICATE_LINEAL_AND_SQM_LINE_ITEMS_ON_KIT"
      )?.isSelected ?? false;
    const duplicateLinealSQFTOnKit =
      orderSettings?.orderOptions.find(
        (s) => s.key === "DUPLICATE_LINEAL_AND_SQFT_LINE_ITEMS_ON_KIT"
      )?.isSelected ?? false;
    const newKitItems = kitItems.map((kitItem) => {
      const parentKitQuantity = Math.floor(value);

      if (
        duplicateLinealSQMOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL
      ) {
        const newLengthItems =
          kitItem.initialLengthItems?.map((item) => ({
            ...item,
            amount: Decimal(item.amount).times(parentKitQuantity).toDecimalPlaces(0).toNumber(),
          })) || [];
        const newSumLengths = getLinealMetresQuantityInMm({
          items: newLengthItems,
          minLengthInMm: minLength,
        });
        // For lineal metres, we multiply amount field of the initial entries by the parent kit quantity
        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
        };
      }
      if (
        duplicateLinealSQMOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL
      ) {
        // For square metres, we duplicate the initial entries by the parent kit quantity
        const newLengthItems = [];
        for (let i = 0; i < parentKitQuantity; i++) {
          kitItem.initialLengthItems?.forEach((item) => {
            3;
            newLengthItems.push({ ...item });
          });
        }
        const newSumLengths = getSquareMetresQuantityInSquareMetres({ items: newLengthItems });

        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
          },
        };
      }
      if (
        duplicateLinealSQFTOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL
      ) {
        const newLengthItems =
          kitItem.initialLengthItems?.map((item) => ({
            ...item,
            amount: Decimal(item.amount).times(parentKitQuantity).toDecimalPlaces(0).toNumber(),
          })) || [];
        const newSumLengths = getLinealFeetQuantityInMm({
          items: newLengthItems,
          minLengthInMm: minLength,
        });
        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
        };
      }
      if (
        duplicateLinealSQFTOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL
      ) {
        const newLengthItems = [];
        for (let i = 0; i < parentKitQuantity; i++) {
          kitItem.initialLengthItems?.forEach((item) => {
            newLengthItems.push({ ...item });
          });
        }
        const newSumLengths = getSquareFeetQuantityInSquareMeter({ items: newLengthItems });
        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
          },
        };
      }
      // Default behavior for other pricing strategies or when toggle is off

      if (
        [
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(kitItem.pricingStrategy)
      ) {
        return { ...kitItem };
      }

      const nextQuantity = Decimal(kitItem.baseQuantity).times(value).toDecimalPlaces(4).toFixed(4);

      // FAC-5897: a labour child's actual is hours worked, never a scaled copy of the estimate,
      // so resizing the kit moves only the estimate.
      if (kitItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
        return { ...kitItem, quantity: nextQuantity };
      }

      // FAC-5896: keep an untouched actual in step with the estimate, preserve a user-set one.
      return isActualTrackingEstimate(kitItem.quantity, kitItem.actualQuantity)
        ? { ...kitItem, quantity: nextQuantity, actualQuantity: nextQuantity }
        : { ...kitItem, quantity: nextQuantity };
    });

    fulfilled({ itemIndex, value, newKitItems: fromJS(newKitItems) });

    newKitItems.map((kitItem, kitIndex) => {
      dispatch(
        this.actions.changeProductKitItemPriceValue({
          itemIndex: itemIndex,
          cartIndex: kitIndex,
          type: "value",
          value: kitItem.value,
        })
      );
    });

    subKits.forEach((subKit, subKitIndex) => {
      const baseQuantity = parseFloat(subKit.quantity) / parseFloat(currentKit.quantity);
      dispatch(
        this.actions.changeSubKitQuantity({
          itemIndex,
          subKitIndex,
          value:
            parseFloat(currentKit.quantity) === parseFloat(value)
              ? subKit.quantity
              : baseQuantity * parseFloat(value),
        })
      );
    });

    if (currentKit.customPricing) {
      const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
      dispatch(
        this.actions.changeProductPriceValue({
          itemIndex,
          type: "value",
          value: price,
        })
      );

      if (currentKit.isMarginTrackedByCompany) {
        dispatch(this.actions.updateProductKitCost({ itemIndex }));
      }
    }
  };

  changeSubKitQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, subKitIndex, value }
  ) => {
    const currentKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    const subKitItems = getState()
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const minLength = getMinLengthProducts(getState());
    const orderSettings = getState()
      .getIn(["customers", "company", "settings", "ordersSettings"])
      .toJS();
    const duplicateLinealSQMOnKit =
      orderSettings?.orderOptions.find(
        (s) => s.key === "DUPLICATE_LINEAL_AND_SQM_LINE_ITEMS_ON_KIT"
      )?.isSelected ?? false;
    const duplicateLinealSQFTOnKit =
      orderSettings?.orderOptions.find(
        (s) => s.key === "DUPLICATE_LINEAL_AND_SQFT_LINE_ITEMS_ON_KIT"
      )?.isSelected ?? false;
    const newSubKitItems = subKitItems.map((kitItem) => {
      const parentKitQuantity = Math.floor(value);

      if (
        duplicateLinealSQMOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL
      ) {
        const newLengthItems =
          kitItem.initialLengthItems?.map((item) => ({
            ...item,
            amount: Decimal(item.amount).times(parentKitQuantity).toDecimalPlaces(0).toNumber(),
          })) || [];
        const newSumLengths = getLinealMetresQuantityInMm({
          items: newLengthItems,
          minLengthInMm: minLength,
        });
        // For lineal metres, we multiply amount field of the initial entries by the parent kit quantity
        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
        };
      }
      if (
        duplicateLinealSQMOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL
      ) {
        // For square metres, we duplicate the initial entries by the parent kit quantity
        const newLengthItems = [];
        for (let i = 0; i < parentKitQuantity; i++) {
          kitItem.initialLengthItems?.forEach((item) => {
            3;
            newLengthItems.push({ ...item });
          });
        }
        const newSumLengths = getSquareMetresQuantityInSquareMetres({ items: newLengthItems });

        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
          },
        };
      }
      if (
        duplicateLinealSQFTOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL
      ) {
        const newLengthItems =
          kitItem.initialLengthItems?.map((item) => ({
            ...item,
            amount: Decimal(item.amount).times(parentKitQuantity).toDecimalPlaces(0).toNumber(),
          })) || [];
        const newSumLengths = getLinealFeetQuantityInMm({
          items: newLengthItems,
          minLengthInMm: minLength,
        });
        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
            forPrice: parseFloat(newSumLengths.forPrice),
          },
        };
      }
      if (
        duplicateLinealSQFTOnKit &&
        kitItem.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL
      ) {
        const newLengthItems = [];
        for (let i = 0; i < parentKitQuantity; i++) {
          kitItem.initialLengthItems?.forEach((item) => {
            newLengthItems.push({ ...item });
          });
        }
        const newSumLengths = getSquareFeetQuantityInSquareMeter({ items: newLengthItems });
        return {
          ...kitItem,
          lengthItems: newLengthItems,
          sumLengths: {
            total: parseFloat(newSumLengths.total),
          },
          actualLengthItems: newLengthItems,
          actualSumLengths: {
            total: parseFloat(newSumLengths.total),
          },
        };
      }
      // Default behavior for other pricing strategies or when toggle is off

      if (
        [
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(kitItem.pricingStrategy)
      ) {
        return { ...kitItem };
      }

      const nextQuantity = Decimal(kitItem.baseQuantity).times(value).toDecimalPlaces(4).toFixed(4);

      // FAC-5897: a labour child's actual is hours worked, never a scaled copy of the estimate,
      // so resizing the kit moves only the estimate.
      if (kitItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
        return { ...kitItem, quantity: nextQuantity };
      }

      // FAC-5896: keep an untouched actual in step with the estimate, preserve a user-set one.
      return isActualTrackingEstimate(kitItem.quantity, kitItem.actualQuantity)
        ? { ...kitItem, quantity: nextQuantity, actualQuantity: nextQuantity }
        : { ...kitItem, quantity: nextQuantity };
    });

    fulfilled({ itemIndex, subKitIndex, value, newSubKitItems: fromJS(newSubKitItems) });

    newSubKitItems.map((kitItem, kitIndex) => {
      dispatch(
        this.actions.changeSubKitItemPriceValue({
          itemIndex: itemIndex,
          subKitIndex,
          cartIndex: kitIndex,
          type: "value",
          value: kitItem.value,
        })
      );
    });

    if (currentKit.customPricing) {
      const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
      dispatch(
        this.actions.changeProductPriceValue({
          itemIndex,
          type: "value",
          value: price,
        })
      );

      if (currentKit.isMarginTrackedByCompany) {
        dispatch(this.actions.updateProductKitCost({ itemIndex }));
      }
    }
  };

  changeProductKitActualQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, value }
  ) => {
    const currentKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    const subKits = getState().getIn(["additionalProducts", "items", itemIndex, "subKits"]).toJS();
    const kitItems = getState()
      .getIn(["additionalProducts", "items", itemIndex, "productKitItems"])
      .toJS();
    const newKitItems = kitItems.map((kitItem) => {
      if (
        [
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(kitItem.pricingStrategy)
      ) {
        return { ...kitItem };
      }
      // FAC-5897: a labour child's actual is hours worked. It does not scale with the kit's
      // actual quantity, so a kit-level actual edit leaves labour children alone.
      if (kitItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
        return { ...kitItem };
      }

      return {
        ...kitItem,
        actualQuantity: Decimal(kitItem.baseQuantity).times(value).toDecimalPlaces(4).toFixed(4),
      };
    });

    subKits.forEach((_, subKitIndex) => {
      dispatch(
        this.actions.changeSubKitActualQuantity({
          itemIndex,
          subKitIndex,
          value,
        })
      );
    });

    fulfilled({ itemIndex, value, newKitItems: fromJS(newKitItems) });

    newKitItems.map((kitItem, kitIndex) => {
      dispatch(
        this.actions.changeProductKitItemPriceValue({
          itemIndex: itemIndex,
          cartIndex: kitIndex,
          type: "value",
          value: kitItem.value,
        })
      );
    });

    if (currentKit.customPricing) {
      const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
      dispatch(
        this.actions.changeProductPriceValue({
          itemIndex,
          type: "value",
          value: price,
        })
      );
    }
  };

  changeProductKitActualQuantityInventoryThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, value }
  ) => {
    // Simple thunk for kit inventory-only quantity changes
    // This field affects stock levels but not margin calculations
    const kitItems = getState()
      .getIn(["additionalProducts", "items", itemIndex, "productKitItems"])
      .toJS();

    const newKitItems = kitItems.map((kitItem) => {
      if (
        [
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(kitItem.pricingStrategy)
      ) {
        return { ...kitItem };
      }
      return {
        ...kitItem,
        // FAC-4489: Preserve null - don't calculate if not set
        actualQuantityInventory:
          kitItem.actualQuantityInventory === null
            ? null
            : Decimal(kitItem.baseQuantity).times(value).toDecimalPlaces(4).toFixed(4),
      };
    });

    const subKits = getState().getIn(["additionalProducts", "items", itemIndex, "subKits"]).toJS();
    subKits.forEach((_, subKitIndex) => {
      dispatch(
        this.actions.changeSubKitActualQuantityInventory({
          itemIndex,
          subKitIndex,
          value,
        })
      );
    });

    fulfilled({ itemIndex, value, newKitItems: fromJS(newKitItems) });
  };

  changeSubKitActualQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, subKitIndex, value }
  ) => {
    const currentKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    const subKitItems = getState()
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();
    const newSubKitItems = subKitItems.map((kitItem) => {
      if (
        [
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(kitItem.pricingStrategy)
      ) {
        return { ...kitItem };
      }
      // FAC-5897: a labour child's actual is hours worked. It does not scale with the kit's
      // actual quantity, so a kit-level actual edit leaves labour children alone.
      if (kitItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
        return { ...kitItem };
      }

      return {
        ...kitItem,
        actualQuantity: Decimal(kitItem.baseQuantity).times(value).toDecimalPlaces(4).toFixed(4),
      };
    });

    fulfilled({ itemIndex, subKitIndex, value, newSubKitItems: fromJS(newSubKitItems) });

    newSubKitItems.map((kitItem, kitIndex) => {
      dispatch(
        this.actions.changeSubKitItemPriceValue({
          itemIndex: itemIndex,
          subKitIndex,
          cartIndex: kitIndex,
          type: "value",
          value: kitItem.value,
        })
      );
    });

    if (currentKit.customPricing) {
      const price = getState().getIn(["additionalProducts", "items", itemIndex, "value"]);
      dispatch(
        this.actions.changeProductPriceValue({
          itemIndex,
          type: "value",
          value: price,
        })
      );
    }
  };

  changeSubKitActualQuantityInventoryThunk = (
    { getState, _, fulfilled },
    { itemIndex, subKitIndex, value }
  ) => {
    // Simple thunk for sub kit inventory-only quantity changes
    // This field affects stock levels but not margin calculations
    const subKitItems = getState()
      .getIn(["additionalProducts", "items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .toJS();

    const newSubKitItems = subKitItems.map((kitItem) => {
      if (
        [
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(kitItem.pricingStrategy)
      ) {
        return { ...kitItem };
      }
      return {
        ...kitItem,
        // FAC-4489: Preserve null - don't calculate if not set
        actualQuantityInventory:
          kitItem.actualQuantityInventory === null
            ? null
            : Decimal(kitItem.baseQuantity).times(value).toDecimalPlaces(4).toFixed(4),
      };
    });

    fulfilled({ itemIndex, subKitIndex, value, newSubKitItems: fromJS(newSubKitItems) });
  };

  changeProductKitItemQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, cartIndex, quantity }
  ) => {
    const kitQuantity = getState().getIn(["additionalProducts", "items", itemIndex, "quantity"]);
    fulfilled({ itemIndex, cartIndex, kitQuantity, quantity });

    const price = getState().getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "productKitItems",
      cartIndex,
      "value",
    ]);

    dispatch(
      this.actions.changeProductKitItemPriceValue({
        itemIndex,
        cartIndex,
        type: "value",
        value: price,
      })
    );

    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }
  };

  changeSubKitItemQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, subKitIndex, cartIndex, quantity }
  ) => {
    const subKitQuantity = getState().getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "quantity",
    ]);
    fulfilled({ itemIndex, subKitIndex, cartIndex, subKitQuantity, quantity });

    const price = getState().getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "kitProducts",
      cartIndex,
      "value",
    ]);
    dispatch(
      this.actions.changeSubKitItemPriceValue({
        itemIndex,
        subKitIndex,
        cartIndex,
        type: "value",
        value: price,
      })
    );

    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }
  };

  changeProductKitItemActualQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, cartIndex, actualQuantity }
  ) => {
    const item = getState()
      .getIn(["additionalProducts", "items", itemIndex, "productKitItems", cartIndex])
      .toJS();

    if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      if (
        parseFloat(item.customActualQuantity || "0.0000") === 0 &&
        parseFloat(item.totalTrackedTime || "0.0000") === 0
      ) {
        fulfilled({
          itemIndex,
          cartIndex,
          actualQuantity,
          customActualQuantity: actualQuantity,
        });
      } else {
        const totalTrackedTime = item.totalTrackedTime
          ? parseFloat(item.totalTrackedTime || "0.0000")
          : 0;
        const customActualQuantity =
          parseFloat(actualQuantity) -
          item.actualQuantity +
          parseFloat(item.customActualQuantity || "0.0000");
        const newActualQuantity = totalTrackedTime + parseFloat(customActualQuantity);

        fulfilled({
          itemIndex,
          cartIndex,
          actualQuantity: newActualQuantity,
          customActualQuantity,
        });
      }
    } else {
      fulfilled({ itemIndex, cartIndex, actualQuantity });
    }

    const price = getState().getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "productKitItems",
      cartIndex,
      "value",
    ]);

    dispatch(
      this.actions.changeProductKitItemPriceValue({
        itemIndex,
        cartIndex,
        type: "value",
        value: price,
      })
    );
  };

  changeProductKitItemActualQuantityInventoryThunk = (
    { fulfilled },
    { itemIndex, cartIndex, actualQuantityInventory }
  ) => {
    // Simple thunk for kit item inventory-only quantity changes
    // This field affects stock levels but not margin calculations
    fulfilled({ itemIndex, cartIndex, actualQuantityInventory });
  };

  changeSubKitItemActualQuantityThunk = (
    { getState, dispatch, fulfilled },
    { itemIndex, subKitIndex, cartIndex, actualQuantity }
  ) => {
    const item = getState().getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "kitProducts",
      cartIndex,
    ]);

    if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      if (
        parseFloat(item.customActualQuantity || "0.0000") === 0 &&
        parseFloat(item.totalTrackedTime || "0.0000") === 0
      ) {
        fulfilled({
          itemIndex,
          subKitIndex,
          cartIndex,
          actualQuantity,
          customActualQuantity: actualQuantity,
        });
      } else {
        const totalTrackedTime = item.totalTrackedTime
          ? parseFloat(item.totalTrackedTime || "0.0000")
          : 0;
        const customActualQuantity =
          parseFloat(actualQuantity) -
          item.actualQuantity +
          parseFloat(item.customActualQuantity || "0.0000");
        const newActualQuantity = totalTrackedTime + parseFloat(customActualQuantity);

        fulfilled({
          itemIndex,
          subKitIndex,
          cartIndex,
          actualQuantity: newActualQuantity,
          customActualQuantity,
        });
      }
    } else {
      fulfilled({ itemIndex, subKitIndex, cartIndex, actualQuantity });
    }

    const price = getState().getIn([
      "additionalProducts",
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "kitProducts",
      cartIndex,
      "value",
    ]);
    dispatch(
      this.actions.changeSubKitItemPriceValue({
        itemIndex,
        subKitIndex,
        cartIndex,
        type: "value",
        value: price,
      })
    );
  };

  changeSubKitItemActualQuantityInventoryThunk = (
    { fulfilled },
    { itemIndex, subKitIndex, cartIndex, actualQuantityInventory }
  ) => {
    // Simple thunk for sub kit item inventory-only quantity changes
    // This field affects stock levels but not margin calculations
    fulfilled({ itemIndex, subKitIndex, cartIndex, actualQuantityInventory });
  };

  changeProductKitDescriptionThunk = ({ fulfilled }, { itemIndex, value }) => {
    fulfilled({ itemIndex, value });
  };

  deleteItemThunk = ({ getState, dispatch, fulfilled }, id) => {
    const item = getState().getIn(["additionalProducts", "items", id.itemIndex]);
    const identity = item?.get("uid") || item?.get("tempUid");
    fulfilled(id);
    if (identity) {
      dispatch(orders.actions.clearFailedSavesForItem({ identity }));
    }
  };

  deleteSubKitThunk = ({ getState, dispatch, fulfilled }, payload) => {
    // Deletion and parent-total recompute happen in `removeSubKitFromOrder`
    const { itemIndex, subKitIndex } = payload;
    const parentItem = getState().getIn(["additionalProducts", "items", itemIndex]);
    const parentIdentity = parentItem?.get("uid") || parentItem?.get("tempUid");
    fulfilled({ itemIndex, subKitIndex });
    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    if (parentIdentity) {
      dispatch(orders.actions.clearFailedSavesForItem({ identity: parentIdentity }));
    }
  };

  deleteKitItemThunk = ({ getState, dispatch, fulfilled }, data) => {
    // Deletion happens here in the `removeKitItemFromOrder`
    const { itemIndex } = data;
    const parentItem = getState().getIn(["additionalProducts", "items", itemIndex]);
    const parentIdentity = parentItem?.get("uid") || parentItem?.get("tempUid");
    fulfilled(data);
    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    if (parentIdentity) {
      dispatch(orders.actions.clearFailedSavesForItem({ identity: parentIdentity }));
    }
  };

  deleteSubKitItemThunk = ({ getState, dispatch, fulfilled }, data) => {
    // Deletion and total recompute happen in `removeSubKitItemFromOrder`
    const { itemIndex } = data;
    const parentItem = getState().getIn(["additionalProducts", "items", itemIndex]);
    const parentIdentity = parentItem?.get("uid") || parentItem?.get("tempUid");
    fulfilled(data);
    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    if (parentIdentity) {
      dispatch(orders.actions.clearFailedSavesForItem({ identity: parentIdentity }));
    }
  };

  changeProductPriceThunk = (
    { fulfilled },
    { itemIndex, type, value, isPercentage: isPercentageParam }
  ) => {
    let price;
    let isPercentage = false;
    if (type === "discount") {
      isPercentage = isPercentageParam ?? true;
      if (isPercentage) {
        price = isPositiveNumber(value) ? value : "0";
      } else {
        price = isPositiveNumber(value) ? value : "0";
      }
    } else if (type === "markup") {
      isPercentage = isPercentageParam ?? false;
      price = isNumeric(value) ? value : "0.00";
    } else if (type === "cost") {
      price = isPositiveNumber(value) ? value : "0.00";
    } else {
      price = isNumeric(value) ? value : "0.00";
    }

    fulfilled({ itemIndex, type, value: price, isPercentage });
  };

  changeProductKitItemPriceThunk = (
    { fulfilled },
    { itemIndex, cartIndex, type, value, isPercentage: isPercentageParam }
  ) => {
    let price;
    let isPercentage = false;
    if (type === "discount") {
      isPercentage = isPercentageParam ?? true;
      if (isPercentage) {
        price = isPositiveNumber(value) ? value : "0";
      } else {
        price = isPositiveNumber(value) ? value : "0";
      }
    } else if (type === "markup") {
      isPercentage = isPercentageParam ?? false;
      price = isNumeric(value) ? value : "0.00";
    } else if (type === "cost") {
      price = isPositiveNumber(value) ? value : "0.00";
    } else {
      price = isNumeric(value) ? value : "0.00";
    }
    fulfilled({ itemIndex, cartIndex, type, value: price, isPercentage });
  };

  changeSubKitItemPriceThunk = (
    { fulfilled },
    { itemIndex, subKitIndex, cartIndex, type, value, isPercentage: isPercentageParam }
  ) => {
    let price;
    let isPercentage = false;
    if (type === "discount") {
      isPercentage = isPercentageParam ?? true;
      if (isPercentage) {
        price = isPositiveNumber(value) ? value : "0";
      } else {
        price = isPositiveNumber(value) ? value : "0";
      }
    } else if (type === "markup") {
      isPercentage = isPercentageParam ?? false;
      price = isNumeric(value) ? value : "0.00";
    } else if (type === "cost") {
      price = isPositiveNumber(value) ? value : "0.00";
    } else {
      price = isNumeric(value) ? value : "0.00";
    }
    fulfilled({ itemIndex, subKitIndex, cartIndex, type, value: price, isPercentage });
  };

  changeSubKitPriceThunk = (
    { fulfilled },
    { itemIndex, subKitIndex, type, value, isPercentage: isPercentageParam }
  ) => {
    let price;
    let isPercentage = false;
    if (type === "discount") {
      isPercentage = isPercentageParam ?? true;
      if (isPercentage) {
        price = isPositiveNumber(value) ? value : "0";
      } else {
        price = isPositiveNumber(value) ? value : "0";
      }
    } else if (type === "markup") {
      isPercentage = isPercentageParam ?? false;
      price = isNumeric(value) ? value : "0.00";
    } else if (type === "cost") {
      price = isPositiveNumber(value) ? value : "0.00";
    } else {
      price = isNumeric(value) ? value : "0.00";
    }
    fulfilled({ itemIndex, subKitIndex, type, value: price, isPercentage });
  };

  changeProductCustomFormulaValueThunk = ({ token, getState, dispatch, fulfilled }, payload) => {
    cancelOnGoinApiRequest();

    const { itemIndex, subKitIndex, cartIndex, isProductKit, isSubKit, key } = payload;

    fulfilled(payload);

    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    const item = isSubKit
      ? getState()
          ?.getIn([
            "additionalProducts",
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
          ])
          ?.toJS()
      : isProductKit
        ? getState()
            ?.getIn(["additionalProducts", "items", itemIndex, "productKitItems", cartIndex])
            ?.toJS()
        : getState()?.getIn(["additionalProducts", "items", itemIndex])?.toJS();

    try {
      const formula = item.customFormula.elements.map((e) => e.value).join("");
      const formulaOutput = evaluate(formula);
      const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
      if (isNaN(roundedFormulaOutput) || roundedFormulaOutput === Infinity) {
        return;
      }

      return additionalProductsApi
        .patchSaveCustomFormula(token, item.customFormula.uid, {
          name: item.customFormula.name,
          elements: item.customFormula.elements.map((e) => {
            if (e.type === "variable" && e.name === "") {
              return { ...e, name: "Label" };
            }
            return e;
          }),
          applied_elements: item.customFormula.elements.map((e) => {
            if (e.type === "variable" && e.name === "") {
              return { ...e, name: "Label" };
            }
            return e;
          }),
        })
        .then((response) => {
          if (key === "name") {
            const { data } = response;
            const element = data.elements.find((_, index) => index === payload.index);
            if (element) {
              fulfilled({ ...payload, value: element.name });
            }
          }
        });
    } catch (_) {
      /* Do nothing */
    }
  };

  changeProductActualCustomFormulaValueThunk = ({ token, getState, fulfilled }, payload) => {
    cancelOnGoinApiRequest();

    const { itemIndex, subKitIndex, cartIndex, isProductKit, isSubKit, key } = payload;

    fulfilled(payload);

    const item = isSubKit
      ? getState()
          ?.getIn([
            "additionalProducts",
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
          ])
          ?.toJS()
      : isProductKit
        ? getState()
            ?.getIn(["additionalProducts", "items", itemIndex, "productKitItems", cartIndex])
            ?.toJS()
        : getState()?.getIn(["additionalProducts", "items", itemIndex])?.toJS();

    try {
      const formula = item.customFormula.appliedElements.map((e) => e.value).join("");
      const formulaOutput = evaluate(formula);
      const roundedFormulaOutput = mathjsRound(formulaOutput, 4);

      if (isNaN(roundedFormulaOutput) || roundedFormulaOutput === Infinity) {
        return;
      }

      return additionalProductsApi
        .patchSaveCustomFormula(token, item.customFormula.uid, {
          name: item.customFormula.name,
          applied_elements: item.customFormula.appliedElements.map((e) => {
            if (e.type === "variable" && e.name === "") {
              return { ...e, name: "Label" };
            }
            return e;
          }),
        })
        .then((response) => {
          if (key === "name") {
            const { data } = response;
            const element = data.appliedElements.find((_, index) => index === payload.index);
            if (element) {
              fulfilled({ ...payload, value: element.name });
            }
          }
        });
    } catch (_) {
      /* Do nothing */
    }
  };

  addProductCustomFormulaValueThunk = ({ getState, token, dispatch, fulfilled }, payload) => {
    cancelOnGoinApiRequest();
    fulfilled(payload);

    const { itemIndex, subKitIndex, cartIndex, isProductKit, isSubKit } = payload;

    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    const item = isSubKit
      ? getState()
          ?.getIn([
            "additionalProducts",
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
          ])
          ?.toJS()
      : isProductKit
        ? getState()
            ?.getIn(["additionalProducts", "items", itemIndex, "productKitItems", cartIndex])
            ?.toJS()
        : getState()?.getIn(["additionalProducts", "items", itemIndex])?.toJS();

    try {
      const formulaOutput = item.customFormulaOutput;
      if (isNaN(formulaOutput) || formulaOutput === Infinity) {
        return;
      }

      return additionalProductsApi.patchSaveCustomFormula(token, item.customFormula.uid, {
        name: item.customFormula.name,
        elements: item.customFormula.elements,
        applied_elements: item.customFormula.elements,
      });
    } catch (_) {
      /* Do nothing */
    }
  };

  removeProductCustomFormulaValueThunk = ({ getState, token, dispatch, fulfilled }, payload) => {
    cancelOnGoinApiRequest();

    fulfilled(payload);

    const { itemIndex, subKitIndex, cartIndex, isProductKit, isSubKit } = payload;

    const productKit = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    if (productKit.customPricing && productKit.isMarginTrackedByCompany) {
      dispatch(this.actions.updateProductKitCost({ itemIndex }));
    }

    const customFormula = isSubKit
      ? getState()
          ?.getIn([
            "additionalProducts",
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
          ])
          ?.toJS()
      : isProductKit
        ? getState()
            ?.getIn([
              "additionalProducts",
              "items",
              itemIndex,
              "productKitItems",
              cartIndex,
              "customFormula",
            ])
            ?.toJS()
        : getState()?.getIn(["additionalProducts", "items", itemIndex, "customFormula"])?.toJS();

    return additionalProductsApi.patchSaveCustomFormula(token, customFormula.uid, {
      name: customFormula.name,
      elements: customFormula.elements,
      applied_elements: customFormula.elements,
    });
  };

  saveProductToCatalogueThunk = async ({ dispatch, fulfilled, pendingAction, token }, payload) => {
    dispatch(pendingAction({ id: payload.index, isSavingToProductCatalogue: true }));
    try {
      const { data: priceLevels } = await priceLevelApi.getPriceLevels()();
      const { data: categories } = await additionalProductsApi.getCategories(token);
      const miscellaneousCategory = categories.find((c) => c.name === "Miscellaneous");

      const pricingStrategy = Object.keys(PRICING_STRATEGY).find(
        (key) => PRICING_STRATEGY[key] === payload.pricingStrategy
      );
      const data = {
        name: payload.name,
        category_uid: miscellaneousCategory?.uid ?? null,
        tables: [
          {
            material_uid: "",
            colours: [],
            fields: [
              {
                name: "Description",
                is_hide_order_summary: false,
                is_hide_invoice_pdf: false,
                is_hide_customer_view: false,
                is_hide_work_order_pdf: false,
              },
            ],
            values: [
              {
                inventory: [],
                types: [
                  {
                    name: "Description",
                    value: payload.productDescription,
                    is_hide_order_summary: false,
                    is_hide_invoice_pdf: false,
                    is_hide_customer_view: false,
                    is_hide_work_order_pdf: false,
                  },
                ],
                prices: [
                  {
                    name: "Price (A)",
                    value: "",
                  },
                ],
                accounting_item: {},
                is_deleted: false,
                suppliers: [],
                row_prices: priceLevels.map((p) => ({
                  price_level_uid: p.uid,
                  value: payload.value,
                })),
                cost: payload.cost,
                markup: payload.markup,
                marked_up_price: payload.value,
                markup_is_percentage: 0,
              },
            ],
            price_levels: priceLevels,
            hiddenPriceLevels: [],
          },
        ],
        pricing_strategy: parseInt(pricingStrategy),
        is_deleted: false,
        is_purchased_by_company: false,
        is_sold_by_company: true,
        is_stock_tracked_by_company: false,
        is_margin_tracked_by_company: payload.cost > 0,
        suppliers: [],
        custom_formula: null,
      };

      const bodyFormData = new FormData();
      bodyFormData.append("json", JSON.stringify(snakecaseKeys(data)));
      bodyFormData.append("image", "");

      const { data: postResponse } = await productsApi.saveProduct(bodyFormData)();

      fulfilled({ ...postResponse, index: payload.index });

      dispatch(
        alerts.actions.addAlert({
          type: "success",
          message: `Product saved to catalogue as ${payload.name}`,
          closeDelay: 3000,
        })
      );

      return { ...postResponse, index: payload.index };
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(
        pendingAction({
          id: payload.index,
          isSavingToProductCatalogue: false,
        })
      );
    }
  };

  startSavingProductKitToCatalogueThunk = async ({ dispatch, pendingAction }, { itemIndex }) => {
    dispatch(pendingAction({ id: itemIndex, isSavingToProductCatalogue: true }));
  };

  saveProductKitToCatalogueThunk = async (
    { dispatch, fulfilled, pendingAction, token },
    productKit
  ) => {
    dispatch(pendingAction({ id: productKit.index, isSavingToProductCatalogue: true }));
    try {
      const { data: priceLevels } = await priceLevelApi.getPriceLevels()();
      const { data: categories } = await additionalProductsApi.getCategories(token);
      const miscellaneousCategory = categories.find((c) => c.name === "Miscellaneous");

      const { productKitItems, subKits } = camelcaseKeys(productKit, { deep: true });

      const newKitProducts = productKitItems.map((kitProduct, cartIndex) => {
        if (kitProduct.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
          return {
            uid: kitProduct.uid ? kitProduct.uid : null,
            productType: PRODUCT_TYPE_ADDITIONAL_PRODUCT,
            product: {
              uid: kitProduct.product.uid,
              pricingStrategy: kitProduct.product.pricingStrategy,
            },
            productRow: {
              uid: kitProduct.productRow.uid,
              colourOptions: kitProduct.productRow.colourOptions,
              colour: kitProduct.productRow.colour,
              material: kitProduct.productRow.material,
            },
            quantity: kitProduct.quantity,
            customAttributeValues: kitProduct.customAttributeValues,
            customFormula: kitProduct.customFormula,
            customRowPrices: kitProduct.customRowPrices,
            lengthItems: kitProduct.lengthItems,
            sumLengths: kitProduct.sumLengths,
            cartIndex,
          };
        }
        if (kitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
          return {
            ...kitProduct,
            productType: PRODUCT_TYPE_LABOUR_ITEM,
            user: kitProduct.labourUser,
            hourlyRateCost: kitProduct.hourlyRateCost,
            hourlyRateCharged: kitProduct.hourlyRateCharged,
          };
        }
        if (kitProduct.productType === PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL) {
          return {
            ...kitProduct,
          };
        }
        if (kitProduct.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
          return {
            ...kitProduct,
            productType: PRODUCT_TYPE_NOTES_ITEM,
          };
        }

        return kitProduct;
      });

      const newSubKits = subKits.map((newSubKit) => {
        const newSubKitProducts = newSubKit.kitProducts.map((subKitProduct) => {
          if (subKitProduct.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
            return {
              uid: subKitProduct.uid ? subKitProduct.uid : null,
              productType: PRODUCT_TYPE_ADDITIONAL_PRODUCT,
              product: {
                uid: subKitProduct.product.uid,
                pricingStrategy: subKitProduct.product.pricingStrategy,
              },
              productRow: {
                uid: subKitProduct.productRow.uid,
                colourOptions: subKitProduct.productRow.colourOptions,
                colour: subKitProduct.productRow.colour,
                material: subKitProduct.productRow.material,
              },
              quantity: subKitProduct.quantity,
              customAttributeValues: subKitProduct.customAttributeValues,
              customFormula: subKitProduct.customFormula,
              customRowPrices: subKitProduct.customRowPrices,
              lengthItems: subKitProduct.lengthItems,
              sumLengths: subKitProduct.sumLengths,
              cartIndex: subKitProduct.cartIndex,
            };
          }
          if (subKitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
            return {
              ...subKitProduct,
              productType: PRODUCT_TYPE_LABOUR_ITEM,
              user: subKitProduct.labourUser,
              hourlyRateCost: subKitProduct.hourlyRateCost,
              hourlyRateCharged: subKitProduct.hourlyRateCharged,
            };
          }
          if (subKitProduct.productType === PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL) {
            return {
              ...subKitProduct,
            };
          }
          if (subKitProduct.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
            return {
              ...subKitProduct,
              productType: PRODUCT_TYPE_NOTES_ITEM,
            };
          }

          return subKitProduct;
        });

        return {
          ...newSubKit,
          kitProducts: newSubKitProducts,
          uid: null,
        };
      });

      // Create the post object
      const postData = {
        title: productKit.name,
        description: productKit.productDescription,
        customPricing: productKit.customPricing,
        categoryUid: miscellaneousCategory?.uid ?? null,
        category: miscellaneousCategory?.uid ?? null,
        displayKitItems: productKit.displayKitItems,
        displaySubKits: productKit.displaySubKits,
        productKitRows: productKit.customPricing
          ? [
              {
                uid: null,
                attributes: [
                  {
                    name: "Description",
                    value: "",
                    isHideOrderSummary: false,
                    isHideInvoicePdf: false,
                    isHideCustomerView: false,
                    isHideWorkOrderPdf: false,
                  },
                ],
                row_price_levels: priceLevels.map((p) => ({
                  price_level_uid: p.uid,
                  value: productKit.value,
                })),
              },
            ]
          : [],
        fields: [
          {
            name: "Description",
            is_hide_order_summary: false,
            is_hide_invoice_pdf: false,
            is_hide_customer_view: false,
            is_hide_work_order_pdf: false,
          },
        ],
        priceLevels: [],
        hiddenPriceLevels: [],
        kitProducts: newKitProducts,
        productKitItem: [],
        subKits: newSubKits,
      };

      const bodyFormData = new FormData();
      bodyFormData.append("json", JSON.stringify(snakecaseKeys(postData)));

      const { data: postResponse } = await productKitsApi.postProductKit(bodyFormData)();

      fulfilled({ ...postResponse, index: productKit.index });

      dispatch(
        alerts.actions.addAlert({
          type: "success",
          message: `Product kit saved to catalogue as ${productKit.name}`,
          closeDelay: 3000,
        })
      );

      return { ...postResponse, index: productKit.index };
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(
        pendingAction({
          id: productKit.index,
          isSavingToProductCatalogue: false,
        })
      );
    }
  };

  saveKitItemToCatalogueThunk = async (
    { dispatch, fulfilled, pendingAction, token },
    { itemIndex, cartItem }
  ) => {
    dispatch(
      pendingAction({ itemIndex, cartIndex: cartItem.cartIndex, isSavingToProductCatalogue: true })
    );
    try {
      const { data: priceLevels } = await priceLevelApi.getPriceLevels()();
      const { data: categories } = await additionalProductsApi.getCategories(token);
      const miscellaneousCategory = categories.find((c) => c.name === "Miscellaneous");

      const pricingStrategy = Object.keys(PRICING_STRATEGY).find(
        (key) => PRICING_STRATEGY[key] === cartItem.pricingStrategy
      );
      const data = {
        name: cartItem.productName,
        category_uid: miscellaneousCategory?.uid ?? null,
        tables: [
          {
            material_uid: "",
            colours: [],
            fields: [
              {
                name: "Description",
                is_hide_order_summary: false,
                is_hide_invoice_pdf: false,
                is_hide_customer_view: false,
                is_hide_work_order_pdf: false,
              },
            ],
            values: [
              {
                inventory: [],
                types: [
                  {
                    name: "Description",
                    value: cartItem.productDescription,
                    is_hide_order_summary: false,
                    is_hide_invoice_pdf: false,
                    is_hide_customer_view: false,
                    is_hide_work_order_pdf: false,
                  },
                ],
                prices: [
                  {
                    name: "Price (A)",
                    value: "",
                  },
                ],
                accounting_item: {},
                is_deleted: false,
                suppliers: [],
                row_prices: priceLevels.map((p) => ({
                  price_level_uid: p.uid,
                  value: cartItem.value,
                })),
                cost: cartItem.cost,
                markup: cartItem.markup,
                marked_up_price: cartItem.value,
                markup_is_percentage: cartItem.markupIsPercentage,
              },
            ],
            price_levels: priceLevels,
            hiddenPriceLevels: [],
          },
        ],
        pricing_strategy: parseInt(pricingStrategy),
        is_deleted: false,
        is_purchased_by_company: false,
        is_sold_by_company: true,
        is_stock_tracked_by_company: false,
        is_margin_tracked_by_company: cartItem.cost > 0,
        suppliers: [],
        custom_formula: null,
      };

      const bodyFormData = new FormData();
      bodyFormData.append("json", JSON.stringify(snakecaseKeys(data)));
      bodyFormData.append("image", "");

      const { data: postResponse } = await productsApi.saveProduct(bodyFormData)();

      fulfilled({ ...postResponse, itemIndex, cartIndex: cartItem.cartIndex });

      dispatch(
        alerts.actions.addAlert({
          type: "success",
          message: `Product saved to catalogue as ${cartItem.productName}`,
          closeDelay: 3000,
        })
      );
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(
        pendingAction({
          itemIndex,
          cartIndex: cartItem.cartIndex,
          isSavingToProductCatalogue: false,
        })
      );
    }
  };

  saveSubKitItemToCatalogueThunk = async (
    { dispatch, fulfilled, pendingAction, token },
    { itemIndex, subKitIndex, cartItem }
  ) => {
    dispatch(
      pendingAction({
        itemIndex,
        subKitIndex,
        cartIndex: cartItem.cartIndex,
        isSavingToProductCatalogue: true,
      })
    );
    try {
      const { data: priceLevels } = await priceLevelApi.getPriceLevels()();
      const { data: categories } = await additionalProductsApi.getCategories(token);
      const miscellaneousCategory = categories.find((c) => c.name === "Miscellaneous");

      const pricingStrategy = Object.keys(PRICING_STRATEGY).find(
        (key) => PRICING_STRATEGY[key] === cartItem.pricingStrategy
      );
      const data = {
        name: cartItem.productName,
        category_uid: miscellaneousCategory?.uid ?? null,
        tables: [
          {
            material_uid: "",
            colours: [],
            fields: [
              {
                name: "Description",
                is_hide_order_summary: false,
                is_hide_invoice_pdf: false,
                is_hide_customer_view: false,
                is_hide_work_order_pdf: false,
              },
            ],
            values: [
              {
                inventory: [],
                types: [
                  {
                    name: "Description",
                    value: cartItem.productDescription,
                    is_hide_order_summary: false,
                    is_hide_invoice_pdf: false,
                    is_hide_customer_view: false,
                    is_hide_work_order_pdf: false,
                  },
                ],
                prices: [
                  {
                    name: "Price (A)",
                    value: "",
                  },
                ],
                accounting_item: {},
                is_deleted: false,
                suppliers: [],
                row_prices: priceLevels.map((p) => ({
                  price_level_uid: p.uid,
                  value: cartItem.value,
                })),
                cost: cartItem.cost,
                markup: cartItem.markup,
                marked_up_price: cartItem.value,
                markup_is_percentage: cartItem.markupIsPercentage,
              },
            ],
            price_levels: priceLevels,
            hiddenPriceLevels: [],
          },
        ],
        pricing_strategy: parseInt(pricingStrategy),
        is_deleted: false,
        is_purchased_by_company: false,
        is_sold_by_company: true,
        is_stock_tracked_by_company: false,
        is_margin_tracked_by_company: cartItem.cost > 0,
        suppliers: [],
        custom_formula: null,
      };

      const bodyFormData = new FormData();
      bodyFormData.append("json", JSON.stringify(snakecaseKeys(data)));
      bodyFormData.append("image", "");

      const { data: postResponse } = await productsApi.saveProduct(bodyFormData)();

      fulfilled({ ...postResponse, itemIndex, subKitIndex, cartIndex: cartItem.cartIndex });

      dispatch(
        alerts.actions.addAlert({
          type: "success",
          message: `Product saved to catalogue as ${cartItem.productName}`,
          closeDelay: 3000,
        })
      );
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(
        pendingAction({
          itemIndex,
          subKitIndex,
          cartIndex: cartItem.cartIndex,
          isSavingToProductCatalogue: false,
        })
      );
    }
  };

  selectXeroTrackingCategory = ({ fulfilled, dispatch, getState }, { itemIndex, data }) => {
    fulfilled({ itemIndex, data });

    const item = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
      if (Array.isArray(item.productKitItems)) {
        item.productKitItems.forEach((_, cartIndex) => {
          dispatch(
            this.actions.selectXeroTrackingCategoryKitItem({
              itemIndex,
              cartIndex,
              data,
            })
          );
        });
      }

      if (Array.isArray(item.subKits)) {
        item.subKits.forEach((_, subKitIndex) => {
          dispatch(
            this.actions.selectXeroTrackingCategorySubKit({
              itemIndex,
              subKitIndex,
              data,
            })
          );
        });
      }
    }
  };

  selectXeroTrackingCategoryKitItem = ({ fulfilled }, { itemIndex, cartIndex, data }) => {
    fulfilled({ itemIndex, cartIndex, data });
  };

  selectXeroTrackingCategorySubKit = (
    { fulfilled, dispatch, getState },
    { itemIndex, subKitIndex, data }
  ) => {
    fulfilled({ itemIndex, subKitIndex, data });

    const item = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (item && Array.isArray(item.subKits) && item.subKits[subKitIndex]) {
      const subKit = item.subKits[subKitIndex];

      if (Array.isArray(subKit.kitProducts)) {
        subKit.kitProducts.forEach((_, cartIndex) => {
          dispatch(
            this.actions.selectXeroTrackingCategorySubKitItem({
              itemIndex,
              subKitIndex,
              cartIndex,
              data,
            })
          );
        });
      }
    }
  };

  selectXeroTrackingCategorySubKitItem = (
    { fulfilled },
    { itemIndex, subKitIndex, cartIndex, data }
  ) => {
    fulfilled({ itemIndex, subKitIndex, cartIndex, data });
  };

  applyXeroTrackingCategoryToAllLineItems = async ({ dispatch, getState }, data) => {
    const items = getState().getIn(["additionalProducts", "items"]).toJS();

    const productsForUpdating = items
      .filter((p) => !p.isDeleted)
      .map((product, itemIndex) => {
        if (
          product.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL &&
          (Array.isArray(product.productKitItems) || Array.isArray(product.subKits))
        ) {
          const result = [
            {
              productType: product.productType,
              itemIndex,
              data,
            },
          ];

          if (Array.isArray(product.subKits)) {
            product.subKits.forEach((subKit, subKitIndex) => {
              result.push({
                itemIndex,
                subKitIndex,
                isSubKit: true,
                data,
              });

              if (Array.isArray(subKit.kitProducts)) {
                subKit.kitProducts.forEach((_, cartIndex) => {
                  result.push({
                    productType: PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
                    itemIndex,
                    subKitIndex,
                    cartIndex,
                    data,
                  });
                });
              }
            });
          }

          if (Array.isArray(product.productKitItems)) {
            product.productKitItems.forEach((_, cartIndex) => {
              result.push({
                productType: PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
                itemIndex,
                cartIndex,
                data,
              });
            });
          }

          return result;
        }

        return {
          productType: product.productType,
          itemIndex,
          data,
        };
      });

    return Promise.all(
      flattenDeep(productsForUpdating).map((product) => {
        if (product.productType === PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectXeroTrackingCategoryKitItem({
                itemIndex: product.itemIndex,
                cartIndex: product.cartIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        if (product.productType === PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectXeroTrackingCategorySubKitItem({
                itemIndex: product.itemIndex,
                subKitIndex: product.subKitIndex,
                cartIndex: product.cartIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        if (product.isSubKit) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectXeroTrackingCategorySubKit({
                itemIndex: product.itemIndex,
                subKitIndex: product.subKitIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        return new Promise((resolve) => {
          dispatch(
            this.actions.selectXeroTrackingCategory({
              itemIndex: product.itemIndex,
              data: product.data,
            })
          );

          resolve();
        });
      })
    ).then(() => {
      return Promise.all(
        flattenDeep(productsForUpdating)
          .filter(
            (product) =>
              ![
                PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
                PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
                PRODUCT_TYPE_NOTES_ITEM_LABEL,
              ].includes(product.productType) && !product.isSubKit
          )
          .map((product) => {
            return new Promise((resolve) => {
              dispatch(
                orders.actions.submitProductsToOrder({
                  itemIndex: product.itemIndex,
                })
              );

              resolve();
            });
          })
      );
    });
  };

  selectMyobJob = ({ fulfilled, dispatch, getState }, { itemIndex, data }) => {
    fulfilled({ itemIndex, data });

    const item = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
      if (Array.isArray(item.productKitItems)) {
        item.productKitItems.forEach((_, cartIndex) => {
          dispatch(
            this.actions.selectMyobJobKitItem({
              itemIndex,
              cartIndex,
              data,
            })
          );
        });
      }

      if (Array.isArray(item.subKits)) {
        item.subKits.forEach((_, subKitIndex) => {
          dispatch(
            this.actions.selectMyobJobSubKit({
              itemIndex,
              subKitIndex,
              data,
            })
          );
        });
      }
    }
  };

  selectMyobJobSubKit = ({ fulfilled, dispatch, getState }, { itemIndex, subKitIndex, data }) => {
    fulfilled({ itemIndex, subKitIndex, data });

    const item = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();

    if (item && Array.isArray(item.subKits) && item.subKits[subKitIndex]) {
      const subKit = item.subKits[subKitIndex];

      if (Array.isArray(subKit.kitProducts)) {
        subKit.kitProducts.forEach((_, cartIndex) => {
          dispatch(
            this.actions.selectMyobJobSubKitItem({
              itemIndex,
              subKitIndex,
              cartIndex,
              data,
            })
          );
        });
      }
    }
  };

  selectMyobJobKitItem = ({ fulfilled }, { itemIndex, cartIndex, data }) => {
    fulfilled({ itemIndex, cartIndex, data });
  };

  selectMyobJobSubKitItem = ({ fulfilled }, { itemIndex, subKitIndex, cartIndex, data }) => {
    fulfilled({ itemIndex, subKitIndex, cartIndex, data });
  };

  applyMyobJobToAllLineItems = ({ dispatch, getState }, data) => {
    const items = getState().getIn(["additionalProducts", "items"]).toJS();

    const productsForUpdating = items
      .filter((p) => !p.isDeleted)
      .map((product, itemIndex) => {
        if (
          product.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL &&
          (Array.isArray(product.productKitItems) || Array.isArray(product.subKits))
        ) {
          const result = [
            {
              productType: product.productType,
              itemIndex,
              data,
            },
          ];

          if (Array.isArray(product.subKits)) {
            product.subKits.forEach((subKit, subKitIndex) => {
              result.push({
                itemIndex,
                subKitIndex,
                isSubKit: true,
                data,
              });

              if (Array.isArray(subKit.kitProducts)) {
                subKit.kitProducts.forEach((_, cartIndex) => {
                  result.push({
                    productType: PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
                    itemIndex,
                    subKitIndex,
                    cartIndex,
                    data,
                  });
                });
              }
            });
          }

          if (Array.isArray(product.productKitItems)) {
            product.productKitItems.forEach((_, cartIndex) => {
              result.push({
                productType: PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
                itemIndex,
                cartIndex,
                data,
              });
            });
          }

          return result;
        }

        return {
          productType: product.productType,
          itemIndex,
          data,
        };
      });

    return Promise.all(
      flattenDeep(productsForUpdating).map((product) => {
        if (product.productType === PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectMyobJobKitItem({
                itemIndex: product.itemIndex,
                cartIndex: product.cartIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        if (product.productType === PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectMyobJobSubKitItem({
                itemIndex: product.itemIndex,
                subKitIndex: product.subKitIndex,
                cartIndex: product.cartIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        if (product.isSubKit) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectMyobJobSubKit({
                itemIndex: product.itemIndex,
                subKitIndex: product.subKitIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        return new Promise((resolve) => {
          dispatch(
            this.actions.selectMyobJob({
              itemIndex: product.itemIndex,
              data: product.data,
            })
          );

          resolve();
        });
      })
    ).then(() => {
      return Promise.all(
        flattenDeep(productsForUpdating)
          .filter(
            (product) =>
              ![
                PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
                PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
                PRODUCT_TYPE_NOTES_ITEM_LABEL,
              ].includes(product.productType) && !product.isSubKit
          )
          .map((product) => {
            return new Promise((resolve) => {
              dispatch(
                orders.actions.submitProductsToOrder({
                  itemIndex: product.itemIndex,
                })
              );

              resolve();
            });
          })
      );
    });
  };

  selectMyobAccount = ({ fulfilled }, { itemIndex, data }) => {
    fulfilled({ itemIndex, data });
  };

  selectMyobAccountKitItem = ({ fulfilled }, { itemIndex, cartIndex, data }) => {
    fulfilled({ itemIndex, cartIndex, data });
  };

  selectMyobAccountSubKit = ({ fulfilled }, { itemIndex, subKitIndex, data }) => {
    fulfilled({ itemIndex, subKitIndex, data });
  };

  selectMyobAccountSubKitItem = ({ fulfilled }, { itemIndex, subKitIndex, cartIndex, data }) => {
    fulfilled({ itemIndex, subKitIndex, cartIndex, data });
  };

  applyMyobAccountToAllLineItems = ({ dispatch, getState }, data) => {
    const items = getState().getIn(["additionalProducts", "items"]).toJS();

    const productsForUpdating = items
      .filter((p) => !p.isDeleted)
      .map((product, itemIndex) => {
        if (
          product.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL &&
          (Array.isArray(product.productKitItems) || Array.isArray(product.subKits))
        ) {
          const updates = [
            {
              productType: product.productType,
              itemIndex,
              data,
            },
          ];

          if (Array.isArray(product.subKits)) {
            product.subKits.forEach((subKit, subKitIndex) => {
              updates.push({
                productType: "SUB_KIT",
                itemIndex,
                subKitIndex,
                data,
              });

              if (Array.isArray(subKit.kitProducts)) {
                subKit.kitProducts.forEach((_, cartIndex) => {
                  updates.push({
                    productType: PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
                    itemIndex,
                    subKitIndex,
                    cartIndex,
                    data,
                  });
                });
              }
            });
          }

          if (Array.isArray(product.productKitItems)) {
            product.productKitItems.forEach((_, cartIndex) => {
              updates.push({
                productType: PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
                itemIndex,
                cartIndex,
                data,
              });
            });
          }

          return updates;
        }

        return {
          productType: product.productType,
          itemIndex,
          data,
        };
      });

    Promise.all(
      flattenDeep(productsForUpdating).map((product) => {
        if (product.productType === PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectMyobAccountKitItem({
                itemIndex: product.itemIndex,
                cartIndex: product.cartIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        if (product.productType === "SUB_KIT") {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectMyobAccountSubKit({
                itemIndex: product.itemIndex,
                subKitIndex: product.subKitIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        if (product.productType === PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL) {
          return new Promise((resolve) => {
            dispatch(
              this.actions.selectMyobAccountSubKitItem({
                itemIndex: product.itemIndex,
                subKitIndex: product.subKitIndex,
                cartIndex: product.cartIndex,
                data: product.data,
              })
            );
            resolve();
          });
        }

        return new Promise((resolve) => {
          dispatch(
            this.actions.selectMyobAccount({
              itemIndex: product.itemIndex,
              data: product.data,
            })
          );

          resolve();
        });
      })
    );

    Promise.all(
      flattenDeep(productsForUpdating)
        .filter(
          (product) =>
            ![
              PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL,
              "SUB_KIT",
              PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL,
              PRODUCT_TYPE_NOTES_ITEM_LABEL,
            ].includes(product.productType)
        )
        .map((product) => {
          return new Promise((resolve) => {
            dispatch(
              orders.actions.submitProductsToOrder({
                itemIndex: product.itemIndex,
              })
            );

            resolve();
          });
        })
    );
  };

  updateProductKitCost = ({ dispatch, getState }, { itemIndex }) => {
    const item = getState().getIn(["additionalProducts", "items", itemIndex]).toJS();
    const { productKitItems: kitProducts, subKits } = item;
    const minLengthForLinealMetres = getMinLengthLinealFeet(getState());
    const minLengthForLinealFeet = getMinLengthLinealFeet(getState());
    const totalKitProductsCost = kitProducts
      .filter(
        (kitProduct) =>
          kitProduct.productType !== PRODUCT_TYPE_LABOUR_PRODUCT_LABEL &&
          kitProduct.product.isMarginTrackedByCompany
      )
      .reduce((acc, kitProduct) => {
        const baseCost = Decimal(kitProduct.cost || 0);

        switch (kitProduct.pricingStrategy) {
          case PRICING_STRATEGY_LINEAL_METRES:
          case PRICING_STRATEGY_LINEAL_METRES_LABEL: {
            const { forPrice } = getLinealMetresQuantityInMetres({
              items: kitProduct.lengthItems,
              minLengthInMm: minLengthForLinealMetres,
            });
            return Decimal(acc).plus(baseCost.times(forPrice).toDecimalPlaces(4));
          }

          case PRICING_STRATEGY_SQUARE_METRES:
          case PRICING_STRATEGY_SQUARE_METRES_LABEL: {
            const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
              items: kitProduct.lengthItems,
            });

            return Decimal(acc).plus(baseCost.times(sqmTotal).toDecimalPlaces(4));
          }

          case PRICING_STRATEGY_CUSTOM_FORMULA:
          case PRICING_STRATEGY_CUSTOM_FORMULA_LABEL: {
            const formula = kitProduct.customFormula.elements.map((e) => e.value).join("");
            const formulaOutput = evaluate(formula);
            const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
            const formattedFormulaOutput = kitProduct.customFormula.isRoundedToNearestWholeNumber
              ? Math.round(roundedFormulaOutput)
              : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);
            const quantity = getCustomFormulaQuantity({
              quantity: kitProduct.quantity,
              formulaOutput: formattedFormulaOutput,
            });
            return Decimal(acc).plus(baseCost.times(quantity).toDecimalPlaces(4));
          }

          case PRICING_STRATEGY_LINEAL_FEET:
          case PRICING_STRATEGY_LINEAL_FEET_LABEL: {
            const { forPrice } = getLinealFeetQuantityInFeet({
              items: kitProduct.lengthItems,
              minLengthInMm: minLengthForLinealFeet,
            });
            return Decimal(acc).plus(baseCost.times(forPrice).toDecimalPlaces(4));
          }

          case PRICING_STRATEGY_SQUARE_FEET:
          case PRICING_STRATEGY_SQUARE_FEET_LABEL: {
            const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
              items: kitProduct.lengthItems,
            });
            return Decimal(acc).plus(baseCost.times(sqFtTotal).toDecimalPlaces(4));
          }

          default:
            return Decimal(acc).plus(
              baseCost.times(Decimal(kitProduct.quantity)).toDecimalPlaces(4)
            );
        }
      }, new Decimal(0));

    const totalSubKitsCost = subKits.reduce((subKitAcc, subKit) => {
      const subKitCost = subKit.kitProducts
        .filter(
          (kitProduct) =>
            kitProduct.productType !== PRODUCT_TYPE_LABOUR_PRODUCT_LABEL &&
            kitProduct.product.isMarginTrackedByCompany
        )
        .reduce((kitAcc, kitProduct) => {
          const baseCost = Decimal(kitProduct.cost || 0);

          switch (kitProduct.pricingStrategy) {
            case PRICING_STRATEGY_LINEAL_METRES:
            case PRICING_STRATEGY_LINEAL_METRES_LABEL: {
              const { forPrice } = getLinealMetresQuantityInMetres({
                items: kitProduct.lengthItems,
                minLengthInMm: minLengthForLinealMetres,
              });

              return Decimal(kitAcc).plus(baseCost.times(forPrice).toDecimalPlaces(4));
            }

            case PRICING_STRATEGY_SQUARE_METRES:
            case PRICING_STRATEGY_SQUARE_METRES_LABEL: {
              const { total: sqmTotal } = getSquareMetresQuantityInSquareMetres({
                items: kitProduct.lengthItems,
              });

              return Decimal(kitAcc).plus(baseCost.times(sqmTotal).toDecimalPlaces(4));
            }

            case PRICING_STRATEGY_CUSTOM_FORMULA:
            case PRICING_STRATEGY_CUSTOM_FORMULA_LABEL: {
              const formula = kitProduct.customFormula.elements.map((e) => e.value).join("");
              const formulaOutput = evaluate(formula);
              const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
              const formattedFormulaOutput = kitProduct.customFormula.isRoundedToNearestWholeNumber
                ? Math.round(roundedFormulaOutput)
                : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);
              const quantity = getCustomFormulaQuantity({
                quantity: kitProduct.quantity,
                formulaOutput: formattedFormulaOutput,
              });
              return Decimal(kitAcc).plus(baseCost.times(quantity).toDecimalPlaces(4));
            }

            case PRICING_STRATEGY_LINEAL_FEET:
            case PRICING_STRATEGY_LINEAL_FEET_LABEL: {
              const { forPrice } = getLinealFeetQuantityInFeet({
                items: kitProduct.lengthItems,
                minLengthInMm: minLengthForLinealFeet,
              });
              return Decimal(kitAcc).plus(baseCost.times(forPrice).toDecimalPlaces(4));
            }

            case PRICING_STRATEGY_SQUARE_FEET:
            case PRICING_STRATEGY_SQUARE_FEET_LABEL: {
              const { total: sqFtTotal } = getSquareFeetQuantityInSquareFeet({
                items: kitProduct.lengthItems,
              });
              return Decimal(kitAcc).plus(baseCost.times(sqFtTotal).toDecimalPlaces(4));
            }

            default:
              return Decimal(kitAcc).plus(baseCost.times(kitProduct.quantity).toDecimalPlaces(4));
          }
        }, new Decimal(0));

      return Decimal(subKitAcc).plus(Decimal(subKitCost).toDecimalPlaces(4));
    }, new Decimal(0));

    const totalKitLabourCost = kitProducts
      .filter((kitProduct) => kitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL)
      .reduce(
        (acc, kitLabour) =>
          new Decimal(acc).plus(
            Decimal(kitLabour.cost).times(Decimal(kitLabour.quantity)).toDecimalPlaces(4)
          ),
        new Decimal(0)
      );

    const totalSubKitLabourCost = subKits.reduce((subKitAcc, subKit) => {
      const subKitLabourCost = subKit.kitProducts
        .filter((kitProduct) => kitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL)
        .reduce(
          (acc, kitLabour) =>
            new Decimal(acc).plus(
              Decimal(kitLabour.cost).times(Decimal(kitLabour.quantity)).toDecimalPlaces(4)
            ),
          new Decimal(0)
        );

      return new Decimal(subKitAcc).plus(new Decimal(subKitLabourCost).toDecimalPlaces(4));
    }, new Decimal(0));

    const perUnitTotalCost = Decimal(totalKitProductsCost)
      .plus(totalSubKitsCost)
      .plus(totalKitLabourCost)
      .plus(totalSubKitLabourCost)
      .dividedBy(item.quantity)
      .toDecimalPlaces(4)
      .toFixed(4);

    dispatch(
      this.actions.changeProductPriceValue({ itemIndex, type: "cost", value: perUnitTotalCost })
    );
  };

  defineActions() {
    const clearActiveOrder = this.resetToInitialState(ACTIONS.CLEAR_ACTIVE_ORDER);
    const createNewOrder = this.createAction(ACTIONS.CREATE_NEW_ORDER);
    const setUidOfCustomFormula = this.createAction(ACTIONS.SET_UID_OF_CUSTOM_FORMULA);
    const setUidOfKitItemCustomFormula = this.createAction(
      ACTIONS.SET_UID_OF_KIT_ITEM_CUSTOM_FORMULA
    );
    const setUid = this.createAction(ACTIONS.SET_UID);
    const setKitItemUid = this.createAction(ACTIONS.SET_KIT_ITEM_UID);
    const setUids = this.createAction(ACTIONS.SET_UIDS);
    const setActiveTab = this.createAction(ACTIONS.SET_ACTIVE_TAB);
    const setSearchValue = this.set(ACTIONS.SET_SEARCH_VALUE, "searchValue");

    const searchProducts = this.thunkAction(
      ACTIONS.SEARCH_PRODUCTS,
      this.searchProductsThunk,
      ({ isPending }) => ({ isPending }),
      false
    );
    const searchProductsByCategory = this.thunkAction(
      ACTIONS.SEARCH_PRODUCTS_BY_CATEGORY,
      this.searchProductsByCategoryThunk,
      ({ isPending, id }) => ({ isPending, id }),
      false
    );
    const getProduct = this.thunkAction(ACTIONS.GET_PRODUCT, this.getProduct, true);
    const openOrder = this.thunkAction(ACTIONS.OPEN_ORDER, this.openOrderThunk);
    const resetOrderChanges = this.thunkAction(
      ACTIONS.RESET_ORDER_PRODUCTS,
      this.resetOrderProductsThunk
    );

    // actions for product editor
    const setActiveProduct = this.createAction(ACTIONS.SET_ACTIVE_PRODUCT);

    const getCategories = this.thunkAction(ACTIONS.GET_CATEGORIES, this.getCategories, true);

    // actions for adding to order
    const sortByCategory = this.createAction(ACTIONS.SORT_BY_CATEGORY);
    const addItem = this.thunkAction(ACTIONS.ADD_ADDITIONAL_PRODUCT, this.addItem);
    const addProductKit = this.thunkAction(ACTIONS.ADD_ADDITIONAL_PRODUCT, this.addProductKit);
    const addOnTheFlyProductKit = this.thunkAction(
      ACTIONS.ADD_ON_THE_FLY_PRODUCT_KIT,
      this.addOnTheFlyProductKit
    );
    const addOnTheFlySubKit = this.thunkAction(
      ACTIONS.ADD_ON_THE_FLY_SUB_KIT,
      this.addOnTheFlySubKit
    );
    const addProductKitAsSubKit = this.thunkAction(
      ACTIONS.ADD_PRODUCT_KIT_AS_SUB_KIT,
      this.addProductKitAsSubKit
    );
    const removeSubKits = this.createAction(ACTIONS.REMOVE_SUB_KITS);
    const addOnTheFlyProductItem = this.thunkAction(
      ACTIONS.ADD_ON_THE_FLY_PRODUCT_ITEM,
      this.addOnTheFlyProductItem
    );
    const addNotesLineItem = this.thunkAction(ACTIONS.ADD_NOTES_LINE_ITEM, this.addNotesLineItem);
    const addLabourLineItem = this.thunkAction(
      ACTIONS.ADD_LABOUR_LINE_ITEM,
      this.addLabourLineItem
    );
    const editLabourLineItem = this.thunkAction(
      ACTIONS.EDIT_LABOUR_LINE_ITEM,
      this.editLabourLineItem
    );
    const setEditItem = this.createAction(ACTIONS.EDIT_ADDITIONAL_PRODUCT);
    const saveProductEdit = this.thunkAction(
      ACTIONS.SAVE_EDIT_ADDITIONAL_PRODUCT,
      this.saveProductEdit
    );
    const setIsDuplicatingItem = this.thunkAction(
      ACTIONS.IS_DUPLICATING_PRODUCT,
      this.setIsDuplicatingItemThunk
    );
    const duplicateItem = this.thunkAction(
      ACTIONS.DUPLICATE_ADDITIONAL_PRODUCT,
      this.duplicateItem
    );
    const duplicateKit = this.thunkAction(ACTIONS.DUPLICATE_KIT, this.duplicateKit);
    const duplicateKitItem = this.thunkAction(ACTIONS.DUPLICATE_KIT_ITEM, this.duplicateKitItem);
    const duplicateSubKitItem = this.thunkAction(
      ACTIONS.DUPLICATE_SUB_KIT_ITEM,
      this.duplicateSubKitItem
    );
    const deleteItem = this.thunkAction(ACTIONS.REMOVE_PRODUCT_FROM_ORDER, this.deleteItemThunk);
    const deleteSubKit = this.thunkAction(
      ACTIONS.DELETE_SUB_KIT_FROM_ORDER,
      this.deleteSubKitThunk
    );
    const deleteKitItem = this.thunkAction(
      ACTIONS.REMOVE_KIT_ITEM_FROM_ORDER,
      this.deleteKitItemThunk
    );
    const deleteSubKitItem = this.thunkAction(
      ACTIONS.REMOVE_SUB_KIT_ITEM_FROM_ORDER,
      this.deleteSubKitItemThunk
    );
    const changeQuantity = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_QUANTITY,
      this.changeQuantityThunk
    );
    const changeActualQuantity = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_ACTUAL_QUANTITY,
      this.changeActualQuantityThunk
    );
    const changeActualQuantityInventory = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_ACTUAL_QUANTITY_INVENTORY,
      this.changeActualQuantityInventoryThunk
    );

    const changeProductKitQuantity = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_QUANTITY,
      this.changeProductKitQuantityThunk
    );

    const changeSubKitQuantity = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_QUANTITY,
      this.changeSubKitQuantityThunk
    );

    const changeProductKitActualQuantity = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ACTUAL_QUANTITY,
      this.changeProductKitActualQuantityThunk
    );
    const changeProductKitActualQuantityInventory = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ACTUAL_QUANTITY_INVENTORY,
      this.changeProductKitActualQuantityInventoryThunk
    );

    const changeSubKitActualQuantity = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ACTUAL_QUANTITY,
      this.changeSubKitActualQuantityThunk
    );
    const changeSubKitActualQuantityInventory = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ACTUAL_QUANTITY_INVENTORY,
      this.changeSubKitActualQuantityInventoryThunk
    );

    const changeProductKitItemQuantity = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_QUANTITY,
      this.changeProductKitItemQuantityThunk
    );

    const changeSubKitItemQuantity = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ITEM_QUANTITY,
      this.changeSubKitItemQuantityThunk
    );

    const changeProductKitItemActualQuantity = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_ACTUAL_QUANTITY,
      this.changeProductKitItemActualQuantityThunk
    );
    const changeProductKitItemActualQuantityInventory = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_ACTUAL_QUANTITY_INVENTORY,
      this.changeProductKitItemActualQuantityInventoryThunk
    );

    const changeSubKitItemActualQuantity = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ITEM_ACTUAL_QUANTITY,
      this.changeSubKitItemActualQuantityThunk
    );
    const changeSubKitItemActualQuantityInventory = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ITEM_ACTUAL_QUANTITY_INVENTORY,
      this.changeSubKitItemActualQuantityInventoryThunk
    );

    const changeProductKitDescription = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_DESCRIPTION,
      this.changeProductKitDescriptionThunk
    );

    const changeItemOrderIndex = this.createAction(ACTIONS.CHANGE_ITEM_ORDER_INDEX);
    const changeProductKitItemOrderIndex = this.createAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_ORDER_INDEX
    );
    const changeSubKitItemOrderIndex = this.createAction(ACTIONS.CHANGE_SUB_KIT_ITEM_ORDER_INDEX);
    const changeSubKitOrderIndex = this.createAction(ACTIONS.CHANGE_SUB_KIT_ORDER_INDEX);
    const changeProductName = this.createAction(ACTIONS.CHANGE_PRODUCT_NAME);
    const changeSubKitTitle = this.createAction(ACTIONS.CHANGE_SUB_KIT_TITLE);
    const changeProductNotes = this.createAction(ACTIONS.CHANGE_PRODUCT_NOTES);
    const changeProductDescription = this.createAction(ACTIONS.CHANGE_PRODUCT_DESCRIPTION);
    const changeProductType = this.createAction(ACTIONS.CHANGE_PRODUCT_TYPE);
    const changeProductKitItemName = this.createAction(ACTIONS.CHANGE_PRODUCT_KIT_ITEM_NAME);
    const changeSubKitItemName = this.createAction(ACTIONS.CHANGE_SUB_KIT_ITEM_NAME);
    const changeProductKitItemType = this.createAction(ACTIONS.CHANGE_PRODUCT_KIT_ITEM_TYPE);
    const changeSubKitItemType = this.createAction(ACTIONS.CHANGE_SUB_KIT_ITEM_TYPE);

    const changeProductPriceValue = this.thunkAction(
      ACTIONS.CHANGE_PRICE_VALUE,
      this.changeProductPriceThunk
    );

    const changeProductKitItemPriceValue = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_PRICE_VALUE,
      this.changeProductKitItemPriceThunk
    );
    const changeSubKitItemPriceValue = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ITEM_PRICE_VALUE,
      this.changeSubKitItemPriceThunk
    );
    const changeSubKitPriceValue = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_PRICE_VALUE,
      this.changeSubKitPriceThunk
    );

    const changeProductKitItemNotes = this.createAction(ACTIONS.CHANGE_PRODUCT_KIT_ITEM_NOTES);
    const changeSubKitItemNotes = this.createAction(ACTIONS.CHANGE_SUB_KIT_ITEM_NOTES);
    const changeProductKitItemDescription = this.createAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_DESCRIPTION
    );
    const changeSubKitItemDescription = this.createAction(ACTIONS.CHANGE_SUB_KIT_ITEM_DESCRIPTION);

    const toggleProductIsTaxFree = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_IS_TAX_FREE,
      this.toggleProductIsTaxFree
    );
    const toggleProductKitItemIsTaxFree = this.thunkAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_IS_TAX_FREE,
      this.toggleProductKitItemIsTaxFree
    );
    const toggleSubKitItemIsTaxFree = this.thunkAction(
      ACTIONS.CHANGE_SUB_KIT_ITEM_IS_TAX_FREE,
      this.toggleSubKitItemIsTaxFree
    );

    const toggleProductCompletedStatus = this.createAction(ACTIONS.CHANGE_PRODUCT_COMPLETED_STATUS);

    const toggleProductKitItemCompletedStatus = this.createAction(
      ACTIONS.CHANGE_PRODUCT_KIT_ITEM_COMPLETED_STATUS
    );
    const toggleSubKitItemCompletedStatus = this.createAction(
      ACTIONS.CHANGE_SUB_KIT_ITEM_COMPLETED_STATUS
    );
    const toggleSubKitCompletedStatus = this.createAction(ACTIONS.CHANGE_SUB_KIT_COMPLETED_STATUS);

    const changeProductCustomFormulaValue = this.thunkAction(
      ACTIONS.CHANGE_CUSTOM_FORMULA_VALUES,
      this.changeProductCustomFormulaValueThunk
    );
    const changeProductActualCustomFormulaValue = this.thunkAction(
      ACTIONS.CHANGE_ACTUAL_CUSTOM_FORMULA_VALUES,
      this.changeProductActualCustomFormulaValueThunk
    );
    const addProductCustomFormulaValue = this.thunkAction(
      ACTIONS.ADD_CUSTOM_FORMULA_VALUE,
      this.addProductCustomFormulaValueThunk
    );

    const removeProductCustomFormulaValue = this.thunkAction(
      ACTIONS.REMOVE_CUSTOM_FORMULA_VALUE,
      this.removeProductCustomFormulaValueThunk
    );
    const updateUidOfCustomFormula = this.createAction(ACTIONS.UPDATE_UID_OF_CUSTOM_FORMULA);
    const saveProductToCatalogue = this.thunkAction(
      ACTIONS.SAVE_PRODUCT_TO_CATALOGUE,
      this.saveProductToCatalogueThunk,
      ({ isSavingToProductCatalogue, id }) => ({ isSavingToProductCatalogue, id }),
      false
    );
    const startSavingProductKitToCatalogue = this.thunkAction(
      ACTIONS.SAVE_PRODUCT_KIT_TO_CATALOGUE,
      this.startSavingProductKitToCatalogueThunk,
      ({ isSavingToProductCatalogue, id }) => ({ isSavingToProductCatalogue, id }),
      false
    );
    const saveProductKitToCatalogue = this.thunkAction(
      ACTIONS.SAVE_PRODUCT_KIT_TO_CATALOGUE,
      this.saveProductKitToCatalogueThunk,
      ({ isSavingToProductCatalogue, id }) => ({ isSavingToProductCatalogue, id }),
      false
    );
    const saveKitItemToCatalogue = this.thunkAction(
      ACTIONS.SAVE_KIT_ITEM_TO_CATALOGUE,
      this.saveKitItemToCatalogueThunk,
      ({ isSavingToProductCatalogue, itemIndex, cartIndex }) => ({
        isSavingToProductCatalogue,
        itemIndex,
        cartIndex,
      }),
      false
    );
    const saveSubKitItemToCatalogue = this.thunkAction(
      ACTIONS.SAVE_SUB_KIT_ITEM_TO_CATALOGUE,
      this.saveSubKitItemToCatalogueThunk,
      ({ isSavingToProductCatalogue, itemIndex, subKitIndex, cartIndex }) => ({
        isSavingToProductCatalogue,
        itemIndex,
        subKitIndex,
        cartIndex,
      }),
      false
    );

    const addItemToKit = this.thunkAction(ACTIONS.ADD_ITEM_TO_KIT, this.addItemToKit);
    const addOnTheFlyItemToKit = this.thunkAction(
      ACTIONS.ADD_ON_THE_FLY_ITEM_TO_KIT,
      this.addOnTheFlyItemToKit
    );
    const addLabourLineItemToKit = this.thunkAction(
      ACTIONS.ADD_LABOUR_LINE_ITEM_TO_KIT,
      this.addLabourLineItemToKit
    );
    const addNotesLineItemToKit = this.thunkAction(
      ACTIONS.ADD_NOTES_ITEM_TO_KIT,
      this.addNotesLineItemToKit
    );
    const addItemToSubKit = this.thunkAction(ACTIONS.ADD_ITEM_TO_SUB_KIT, this.addItemToSubKit);
    const addOnTheFlyItemToSubKit = this.thunkAction(
      ACTIONS.ADD_ON_THE_FLY_ITEM_TO_SUB_KIT,
      this.addOnTheFlyItemToSubKit
    );
    const addLabourLineItemToSubKit = this.thunkAction(
      ACTIONS.ADD_LABOUR_LINE_ITEM_TO_SUB_KIT,
      this.addLabourLineItemToSubKit
    );
    const addNotesLineItemToSubKit = this.thunkAction(
      ACTIONS.ADD_NOTES_ITEM_TO_SUB_KIT,
      this.addNotesLineItemToSubKit
    );
    const selectXeroTrackingCategory = this.thunkAction(
      ACTIONS.SELECT_XERO_TRACKING_CATEGORY,
      this.selectXeroTrackingCategory
    );
    const selectXeroTrackingCategoryKitItem = this.thunkAction(
      ACTIONS.SELECT_XERO_TRACKING_CATEGORY_TO_KIT_ITEM,
      this.selectXeroTrackingCategoryKitItem
    );
    const selectXeroTrackingCategorySubKit = this.thunkAction(
      ACTIONS.SELECT_XERO_TRACKING_CATEGORY_TO_SUB_KIT,
      this.selectXeroTrackingCategorySubKit
    );
    const selectXeroTrackingCategorySubKitItem = this.thunkAction(
      ACTIONS.SELECT_XERO_TRACKING_CATEGORY_TO_SUB_KIT_ITEM,
      this.selectXeroTrackingCategorySubKitItem
    );
    const applyXeroTrackingCategoryToAllLineItems = this.thunkAction(
      ACTIONS.APPLY_XERO_TRACKING_CATEGORY_TO_ALL_LINE_ITEMS,
      this.applyXeroTrackingCategoryToAllLineItems
    );
    const selectMyobJob = this.thunkAction(ACTIONS.SELECT_MYOB_JOB, this.selectMyobJob);
    const selectMyobJobSubKit = this.thunkAction(
      ACTIONS.SELECT_MYOB_JOB_TO_SUB_KIT,
      this.selectMyobJobSubKit
    );
    const selectMyobJobKitItem = this.thunkAction(
      ACTIONS.SELECT_MYOB_JOB_TO_KIT_ITEM,
      this.selectMyobJobKitItem
    );
    const selectMyobJobSubKitItem = this.thunkAction(
      ACTIONS.SELECT_MYOB_JOB_TO_SUB_KIT_ITEM,
      this.selectMyobJobSubKitItem
    );
    const applyMyobJobToAllLineItems = this.thunkAction(
      ACTIONS.APPLY_MYOB_JOB_TO_ALL_LINE_ITEMS,
      this.applyMyobJobToAllLineItems
    );
    const selectMyobAccount = this.thunkAction(ACTIONS.SELECT_MYOB_ACCOUNT, this.selectMyobAccount);
    const selectMyobAccountKitItem = this.thunkAction(
      ACTIONS.SELECT_MYOB_ACCOUNT_TO_KIT_ITEM,
      this.selectMyobAccountKitItem
    );
    const selectMyobAccountSubKit = this.thunkAction(
      ACTIONS.SELECT_MYOB_ACCOUNT_TO_SUB_KIT,
      this.selectMyobAccountSubKit
    );
    const selectMyobAccountSubKitItem = this.thunkAction(
      ACTIONS.SELECT_MYOB_ACCOUNT_TO_SUB_KIT_ITEM,
      this.selectMyobAccountSubKitItem
    );
    const applyMyobAccountToAllLineItems = this.thunkAction(
      ACTIONS.APPLY_MYOB_ACCOUNT_TO_ALL_LINE_ITEMS,
      this.applyMyobAccountToAllLineItems
    );
    const updateProductKitCost = this.thunkAction(
      ACTIONS.UPDATE_PRODUCT_KIT_COST,
      this.updateProductKitCost
    );
    return {
      clearActiveOrder,
      createNewOrder,
      setUidOfCustomFormula,
      setUidOfKitItemCustomFormula,
      setUid,
      setKitItemUid,
      setUids,
      setActiveTab,
      setSearchValue,
      searchProducts,
      searchProductsByCategory,
      getProduct,
      openOrder,
      resetOrderChanges,

      setActiveProduct,

      getCategories,

      sortByCategory,
      addItem,
      addProductKit,
      addOnTheFlyProductKit,
      addOnTheFlySubKit,
      addProductKitAsSubKit,
      removeSubKits,
      addOnTheFlyProductItem,
      addLabourLineItem,
      editLabourLineItem,
      addNotesLineItem,
      setIsDuplicatingItem,
      duplicateItem,
      duplicateKit,
      duplicateKitItem,
      duplicateSubKitItem,
      setEditItem,
      saveProductEdit,
      changeQuantity,
      changeActualQuantity,
      changeActualQuantityInventory,
      changeProductKitQuantity,
      changeSubKitQuantity,
      changeProductKitActualQuantity,
      changeProductKitActualQuantityInventory,
      changeSubKitActualQuantity,
      changeSubKitActualQuantityInventory,
      changeProductKitItemQuantity,
      changeSubKitItemQuantity,
      changeProductKitItemActualQuantity,
      changeProductKitItemActualQuantityInventory,
      changeSubKitItemActualQuantity,
      changeSubKitItemActualQuantityInventory,
      changeProductKitDescription,
      deleteItem,
      deleteSubKit,
      deleteKitItem,
      deleteSubKitItem,
      changeItemOrderIndex,
      changeProductKitItemOrderIndex,
      changeSubKitItemOrderIndex,
      changeSubKitOrderIndex,
      changeProductName,
      changeSubKitTitle,
      changeProductNotes,
      changeProductDescription,
      changeProductType,
      changeProductKitItemName,
      changeSubKitItemName,
      changeProductKitItemType,
      changeSubKitItemType,

      changeProductPriceValue,
      changeProductKitItemPriceValue,
      changeSubKitItemPriceValue,
      changeSubKitPriceValue,
      changeProductKitItemNotes,
      changeSubKitItemNotes,
      changeProductKitItemDescription,
      changeSubKitItemDescription,

      toggleProductIsTaxFree,
      toggleProductKitItemIsTaxFree,
      toggleSubKitItemIsTaxFree,

      toggleProductCompletedStatus,
      toggleProductKitItemCompletedStatus,
      toggleSubKitItemCompletedStatus,
      toggleSubKitCompletedStatus,

      changeProductCustomFormulaValue,
      changeProductActualCustomFormulaValue,
      addProductCustomFormulaValue,
      removeProductCustomFormulaValue,
      updateUidOfCustomFormula,
      saveProductToCatalogue,
      startSavingProductKitToCatalogue,
      saveProductKitToCatalogue,
      saveKitItemToCatalogue,
      saveSubKitItemToCatalogue,

      addItemToKit,
      addOnTheFlyItemToKit,
      addLabourLineItemToKit,
      addNotesLineItemToKit,
      addItemToSubKit,
      addOnTheFlyItemToSubKit,
      addLabourLineItemToSubKit,
      addNotesLineItemToSubKit,
      selectXeroTrackingCategory,
      selectXeroTrackingCategoryKitItem,
      selectXeroTrackingCategorySubKit,
      selectXeroTrackingCategorySubKitItem,
      applyXeroTrackingCategoryToAllLineItems,
      selectMyobJob,
      selectMyobJobKitItem,
      selectMyobJobSubKitItem,
      selectMyobJobSubKit,
      applyMyobJobToAllLineItems,
      selectMyobAccount,
      selectMyobAccountKitItem,
      selectMyobAccountSubKit,
      selectMyobAccountSubKitItem,
      applyMyobAccountToAllLineItems,
      updateProductKitCost,
    };
  }

  removeItemFromOrder = (_state, { payload }) => {
    const itemDeleted = _state.getIn(["items", payload.itemIndex]);
    if (isNew(itemDeleted)) {
      const newItems = _state.get("items").delete(payload.itemIndex);
      return _state.set("items", newItems);
    }

    return _state.setIn(["items", payload.itemIndex, "isDeleted"], true);
  };

  removeSubKitFromOrder = (_state, { payload }) => {
    const { itemIndex, subKitIndex } = payload;

    const productKit = _state.getIn(["items", itemIndex]).toJS();
    const remainingSubKits = _state
      .getIn(["items", itemIndex, "subKits"])
      .delete(subKitIndex)
      .toJS()
      .map((subKit, sIndex) => ({
        ...subKit,
        subKitIndex: sIndex,
      }));

    const newProductKit = {
      ...productKit,
      subKits: remainingSubKits,
    };

    // A standard-priced kit derives its total from its items, so removing a whole
    // sub-kit must recompute the parent total here (mirrors removeKitItemFromOrder).
    // We set totalValue DIRECTLY from the exact sum — deriving it from a rounded unit
    // price loses sub-cent changes.
    if (!productKit.customPricing) {
      const kitItemTotals = (newProductKit.productKitItems || []).reduce(
        (prev, current) => prev.plus(current.customValue || current.totalValue),
        Decimal(0)
      );
      const subKitTotals = remainingSubKits.reduce(
        (prev, subKit) => prev.plus(subKit.totalValue),
        Decimal(0)
      );
      const parentKitTotal = kitItemTotals.plus(subKitTotals);
      const parentKitUnitPrice = parentKitTotal.dividedBy(productKit.quantity);
      newProductKit.totalValue = parentKitTotal.toFixed(2);
      newProductKit.value = parentKitUnitPrice.toFixed(4);
    }

    return _state.setIn(["items", itemIndex], fromJS(newProductKit));
  };

  removeKitItemFromOrder = (_state, { payload }) => {
    const { itemIndex, cartIndex } = payload;

    const productKit = _state.getIn(["items", itemIndex]).toJS();
    const deletedKitItemUid = _state.getIn([
      "items",
      itemIndex,
      "productKitItems",
      cartIndex,
      "uid",
    ]);
    const oldDeletedKitItems = _state.getIn(["items", itemIndex, "deletedKitItems"]).toJS();
    const deletedKitItems = [...oldDeletedKitItems, deletedKitItemUid];
    const productKitItems = _state
      .getIn(["items", itemIndex, "productKitItems"])
      .delete(cartIndex)
      .toJS();

    const remainingKitItems = productKitItems.map((kitItem, kitIndex) => ({
      ...kitItem,
      cartIndex: kitIndex,
    }));

    const newProductKit = {
      ...productKit,
      productKitItems: remainingKitItems,
      deletedKitItems,
    };

    // A standard-priced kit derives its total from its items, so removing one must
    // recompute the parent total here (mirrors the changeProductKitItemPriceValue
    // reducer). We set totalValue DIRECTLY from the exact sum — deriving it from a
    // rounded unit price loses sub-cent changes (e.g. a $0.08 item spread across 2200
    // units never moves the 4dp unit price, so the total looked stuck at $1,543.30).
    if (!productKit.customPricing) {
      const kitItemTotals = remainingKitItems.reduce(
        (prev, current) => prev.plus(current.customValue || current.totalValue),
        Decimal(0)
      );
      const subKitTotals = (newProductKit.subKits || []).reduce(
        (prev, subKit) => prev.plus(subKit.totalValue),
        Decimal(0)
      );
      const parentKitTotal = kitItemTotals.plus(subKitTotals);
      const parentKitUnitPrice = parentKitTotal.dividedBy(productKit.quantity);
      newProductKit.totalValue = parentKitTotal.toFixed(2);
      newProductKit.value = parentKitUnitPrice.toFixed(4);
    }

    return _state.setIn(["items", itemIndex], fromJS(newProductKit));
  };

  removeSubKitItemFromOrder = (_state, { payload }) => {
    const { itemIndex, subKitIndex, cartIndex } = payload;

    const productKit = _state.getIn(["items", itemIndex]).toJS();
    const subKit = _state.getIn(["items", itemIndex, "subKits", subKitIndex]).toJS();
    const deletedKitItemUid = _state.getIn([
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "kitProducts",
      cartIndex,
      "uid",
    ]);
    const oldDeletedKitItems = _state
      .getIn(["items", itemIndex, "subKits", subKitIndex, "deletedKitItems"])
      .toJS();
    const deletedKitItems = [...oldDeletedKitItems, deletedKitItemUid];
    const remainingSubKitItems = _state
      .getIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"])
      .delete(cartIndex)
      .toJS()
      .map((subKitItem, index) => ({
        ...subKitItem,
        subKitIndex: index,
      }));

    const newSubKit = {
      ...subKit,
      kitProducts: remainingSubKitItems,
      deletedKitItems,
    };

    // A standard-priced kit derives its totals from its items across two levels, so
    // removing a sub-kit item must recompute both the sub-kit total and the parent
    // total here (mirrors the changeSubKitItemPriceValue reducer). We set each
    // totalValue DIRECTLY from the exact sum — deriving it from a rounded unit price
    // loses sub-cent changes.
    if (!productKit.customPricing) {
      const subKitItemTotals = remainingSubKitItems.reduce(
        (prev, current) => prev.plus(current.customValue || current.totalValue),
        Decimal(0)
      );
      const subKitTotal = subKitItemTotals.toDecimalPlaces(2);
      const subKitUnitPrice = subKitTotal.dividedBy(subKit.quantity);
      newSubKit.totalValue = subKitTotal.toFixed(2);
      newSubKit.value = subKitUnitPrice.toFixed(4);
    }

    const newProductKit = {
      ...productKit,
      subKits: productKit.subKits.map((existingSubKit, index) =>
        index === subKitIndex ? newSubKit : existingSubKit
      ),
    };

    if (!productKit.customPricing) {
      const kitItemTotals = (newProductKit.productKitItems || []).reduce(
        (prev, current) => prev.plus(current.customValue || current.totalValue),
        Decimal(0)
      );
      const subKitTotals = newProductKit.subKits.reduce(
        (prev, current) => prev.plus(current.totalValue),
        Decimal(0)
      );
      const parentKitTotal = kitItemTotals.plus(subKitTotals);
      const parentKitUnitPrice = parentKitTotal.dividedBy(productKit.quantity);
      newProductKit.totalValue = parentKitTotal.toFixed(2);
      newProductKit.value = parentKitUnitPrice.toFixed(4);
    }

    return _state.setIn(["items", itemIndex], fromJS(newProductKit));
  };

  setActiveProduct = (_state, activeProduct) => {
    if (activeProduct) {
      _state.set("activeProduct", fromJS(activeProduct));
    } else {
      _state.set("activeProduct", null);
    }
  };

  changeProductPriceValue = (_state, { itemIndex, type, value, isPercentage = false }) => {
    const item = _state.getIn(["items", itemIndex]);

    const pricingStrategy = item.get("pricingStrategy");
    const productType = item.get("productType");
    const quantityValues = {
      quantity: Decimal(0),
      actualQuantity: Decimal(0),
    };

    if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
      quantityValues.quantity = Decimal(item.get("sumLengths").get("forPrice"))
        .dividedBy(1000)
        .toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(item.get("actualSumLengths").get("forPrice"))
        .dividedBy(1000)
        .toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      const formulaOutput = item.get("customFormulaOutput");
      const actualFormulaOutput = item.get("actualCustomFormulaOutput");
      quantityValues.quantity = Decimal(item.get("quantity"))
        .times(formulaOutput)
        .toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(item.get("actualQuantity"))
        .times(actualFormulaOutput)
        .toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
      quantityValues.quantity = Decimal(item.get("sumLengths").get("total")).toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(
        item.get("actualSumLengths").get("total")
      ).toDecimalPlaces(4);
    } else if (
      pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL ||
      productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL
    ) {
      quantityValues.quantity = Decimal(item.get("quantity")).toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(item.get("actualQuantity")).toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
      quantityValues.quantity = Decimal(convertMmToFeet(item.get("sumLengths").get("forPrice")));
      quantityValues.actualQuantity = Decimal(
        convertMmToFeet(item.get("actualSumLengths").get("forPrice"))
      );
    } else if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
      const subitems = item.get("subitems")?.toJS() ?? [];
      const actualLengthItems = item.get("actualLengthItems")?.toJS() ?? [];
      quantityValues.quantity = Decimal(
        getSquareFeetQuantityInSquareFeet({ items: subitems }).total
      );
      quantityValues.actualQuantity = Decimal(
        getSquareFeetQuantityInSquareFeet({ items: actualLengthItems }).total
      );
    }

    const { quantity, actualQuantity } = quantityValues;
    const markupIsPercentage = item.get("markupIsPercentage");
    const cost = Decimal(item.get("cost")).toDecimalPlaces(4);

    if (type === "totalValue") {
      /* Total Value calculation */
      const newValue = Decimal(value).toDecimalPlaces(2);
      const discount = item.get("discount");
      const itemDiscountIsPercentage = item.get("discountIsPercentage") ?? true;
      let itemTotalWithDiscount;
      if (itemDiscountIsPercentage) {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        const discountRate = Decimal(100).minus(itemDiscount);
        itemTotalWithDiscount = discountRate.isZero()
          ? Decimal(0)
          : newValue.dividedBy(discountRate).times(100).toDecimalPlaces(2);
      } else {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        itemTotalWithDiscount = newValue.plus(itemDiscount).toDecimalPlaces(2);
      }
      const newUnitPrice = itemTotalWithDiscount.dividedBy(quantity).toDecimalPlaces(4);
      _state.setIn(["items", itemIndex, "value"], newUnitPrice.toFixed(4));
      _state.setIn(["items", itemIndex, "customValue"], newValue.toFixed(2));
      if (cost && cost > 0) {
        const newMarkup = markupIsPercentage
          ? Decimal(newUnitPrice).minus(cost).dividedBy(cost).times(100).toDecimalPlaces(22)
          : Decimal(newUnitPrice).minus(cost).toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "markup"], newMarkup.toString());
      }
    } else if (type === "value") {
      /* Unit price calculation */
      const newValue = Decimal(value).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "value"],
        !isNumeric(newValue.toFixed(4)) ? "0.00" : newValue.toFixed(4)
      );
      if (cost && cost > 0) {
        const newMarkup = markupIsPercentage
          ? Decimal(newValue).minus(cost).dividedBy(cost).times(100).toDecimalPlaces(22)
          : Decimal(newValue).minus(cost).toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "markup"], newMarkup.toString());
      }
    } else if (type === "markup") {
      /* Markup price calculation */
      _state.setIn(["items", itemIndex, "markupIsPercentage"], isPercentage);
      if (isPercentage) {
        const newMarkup = Decimal(value);
        // Edit the price with the new provided value
        _state.setIn(["items", itemIndex, "markup"], newMarkup.toString());
        // Cost * Markup = Price for %-based markups
        const newPrice = Decimal(cost)
          .dividedBy(100)
          .times(newMarkup)
          .plus(cost)
          .toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "value"], parseFloat(newPrice.toFixed(4)));
      } else {
        const newMarkup = Decimal(value);
        // Edit the price with the new provided value
        _state.setIn(["items", itemIndex, "markup"], newMarkup.toString());
        // Cost + Markup = Price for $-based markups
        const newPrice = Decimal(cost).plus(newMarkup).toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "value"], parseFloat(newPrice.toFixed(4)));
      }
    } else if (type === "cost") {
      /* Cost price calculation */
      const newCost = value;
      const roundedCost = Decimal(newCost).toDecimalPlaces(4);
      const markup = item.get("markup");
      // Cost * Markup = Price for %-based markups or Cost + Markup = Price for $-based markups
      const newPrice = markupIsPercentage
        ? roundedCost.dividedBy(100).times(markup).plus(roundedCost).toDecimalPlaces(4)
        : roundedCost.plus(Decimal(markup)).toDecimalPlaces(4);
      _state.setIn(["items", itemIndex, "value"], parseFloat(newPrice.toFixed(4)));
      _state.setIn(["items", itemIndex, "cost"], newCost);
    } else if (type === "discount") {
      // Store discount value
      _state.setIn(["items", itemIndex, "discount"], !isPositiveNumber(value) ? "0.00" : value);
      _state.setIn(["items", itemIndex, "discountIsPercentage"], isPercentage);
    }

    // Always apply item level discount
    const discountIsPercentage = _state.getIn(["items", itemIndex, "discountIsPercentage"]) ?? true;
    const discount = Decimal(
      isPositiveNumber(_state.getIn(["items", itemIndex, "discount"]))
        ? _state.getIn(["items", itemIndex, "discount"])
        : 0
    );
    const itemUnitPrice = Decimal(
      isNumeric(_state.getIn(["items", itemIndex, "value"]))
        ? _state.getIn(["items", itemIndex, "value"])
        : 0
    ).toDecimalPlaces(4);
    // Compute item price with discount
    let newTotalWithDiscount;
    if (discountIsPercentage) {
      const discountRate = discount.dividedBy(100);
      newTotalWithDiscount = itemUnitPrice
        .times(quantity)
        .times(Decimal(1).minus(discountRate))
        .toDecimalPlaces(2);
    } else {
      newTotalWithDiscount = itemUnitPrice.times(quantity).minus(discount).toDecimalPlaces(2);
    }

    // Store value
    _state.setIn(["items", itemIndex, "totalValue"], newTotalWithDiscount.toFixed(2));
    _state.setIn(["items", itemIndex, "customValue"], null);

    const updatedCost = Decimal(_state.getIn(["items", itemIndex, "cost"])).toDecimalPlaces(4);
    // Always calculate the margin if cost is greater than 0
    // Item Total Value - (Cost * Qty) = Margin
    if (updatedCost && updatedCost > 0) {
      const itemTotalCost = Decimal(updatedCost).times(actualQuantity).toDecimalPlaces(4);
      const margin = Decimal(newTotalWithDiscount).minus(itemTotalCost).toDecimalPlaces(2);
      _state.setIn(["items", itemIndex, "margin"], margin.toFixed(2));
    } else if (updatedCost?.equals(0)) {
      _state.setIn(["items", itemIndex, "margin"], "0");
    }
  };

  changeProductKitItemPriceValue = (
    _state,
    { itemIndex, cartIndex, type, value, isPercentage = false }
  ) => {
    const parentKitQuantity = _state.getIn(["items", itemIndex, "quantity"]);
    const kitItem = _state.getIn(["items", itemIndex, "productKitItems", cartIndex]);
    const quantityValues = {
      quantity: Decimal(0),
      actualQuantity: Decimal(0),
    };

    const pricingStrategy = kitItem.get("pricingStrategy");

    if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
      quantityValues.quantity = Decimal(kitItem.get("sumLengths").get("forPrice"))
        .dividedBy(1000)
        .toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(kitItem.get("actualSumLengths").get("forPrice"))
        .dividedBy(1000)
        .toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      const formulaOutput = kitItem.get("customFormulaOutput");
      const actualFormulaOutput = kitItem.get("actualCustomFormulaOutput");
      quantityValues.quantity = Decimal(kitItem.get("quantity"))
        .times(formulaOutput)
        .toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(kitItem.get("actualQuantity"))
        .times(actualFormulaOutput)
        .toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
      quantityValues.quantity = Decimal(kitItem.get("sumLengths").get("total")).toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(
        kitItem.get("actualSumLengths").get("total")
      ).toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
      quantityValues.quantity = Decimal(convertMmToFeet(kitItem.get("sumLengths").get("forPrice")));
      quantityValues.actualQuantity = Decimal(
        convertMmToFeet(kitItem.get("actualSumLengths").get("forPrice"))
      );
    } else if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
      quantityValues.quantity = Decimal(
        getSquareFeetQuantityInSquareFeet({ items: kitItem.get("lengthItems")?.toJS() ?? [] }).total
      );
      quantityValues.actualQuantity = Decimal(
        getSquareFeetQuantityInSquareFeet({ items: kitItem.get("actualLengthItems")?.toJS() ?? [] })
          .total
      );
    } else if (pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
      quantityValues.quantity = Decimal(kitItem.get("quantity")).toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(kitItem.get("actualQuantity")).toDecimalPlaces(4);
    }
    const { quantity, actualQuantity } = quantityValues;
    const cost = Decimal(kitItem.get("cost")).toDecimalPlaces(4);
    const markupIsPercentage = kitItem.get("markupIsPercentage");

    if (type === "totalValue") {
      /* Total Value calculation */
      const newValue = Decimal(value).toDecimalPlaces(2);
      const discount = _state.getIn(["items", itemIndex, "productKitItems", cartIndex, "discount"]);
      const kitItemDiscountIsPercentage =
        _state.getIn(["items", itemIndex, "productKitItems", cartIndex, "discountIsPercentage"]) ??
        true;
      let itemTotalWithDiscount;
      if (kitItemDiscountIsPercentage) {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        const discountRate = Decimal(100).minus(itemDiscount);
        itemTotalWithDiscount = discountRate.isZero()
          ? Decimal(0)
          : Decimal(value).dividedBy(discountRate).times(100).toDecimalPlaces(4);
      } else {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        itemTotalWithDiscount = Decimal(value).plus(itemDiscount).toDecimalPlaces(4);
      }
      const newUnitPrice = itemTotalWithDiscount.dividedBy(quantity).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "value"],
        newUnitPrice.toFixed(4)
      );
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "customValue"],
        newValue.toFixed(2)
      );

      if (cost && cost > 0) {
        const newMarkup = markupIsPercentage
          ? Decimal(newUnitPrice).minus(cost).dividedBy(cost).times(100).toDecimalPlaces(22)
          : Decimal(newUnitPrice).minus(cost).toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "markup"],
          newMarkup.toString()
        );
      }
      // update parent kit if standard priced kit
      if (!_state.getIn(["items", itemIndex, "customPricing"])) {
        const kitItems = _state.getIn(["items", itemIndex, "productKitItems"]);
        const kitItemTotals =
          kitItems && kitItems.size > 0
            ? kitItems.reduce((prev, current) => {
                return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
              }, Decimal(0))
            : Decimal(0);
        const subKits = _state.getIn(["items", itemIndex, "subKits"]);
        const subKitTotals =
          subKits && subKits.size > 0
            ? subKits.reduce((prev, current) => {
                return Decimal(prev).plus(current.get("totalValue"));
              }, Decimal(0))
            : Decimal(0);
        const parentKitTotal = kitItemTotals.plus(subKitTotals).toDecimalPlaces(2);
        const parentKitUnitPrice = parentKitTotal.dividedBy(parentKitQuantity).toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "totalValue"], parentKitTotal.toFixed(2));
        _state.setIn(["items", itemIndex, "value"], parentKitUnitPrice.toFixed(4));
        _state.setIn(["items", itemIndex, "markupIsPercentage"], false);
        _state.setIn(["items", itemIndex, "markup"], "0");
        _state.setIn(["items", itemIndex, "cost"], "0");
        _state.setIn(["items", itemIndex, "margin"], "0");
      }
    } else if (type === "value") {
      /* Unit price calculation */
      const newValue = Decimal(value).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, type],
        !isNumeric(newValue.toFixed(4)) ? "0.00" : newValue.toFixed(4)
      );
      if (cost && cost > 0) {
        const newMarkup = markupIsPercentage
          ? Decimal(newValue).minus(cost).dividedBy(cost).times(100).toDecimalPlaces(22)
          : Decimal(newValue).minus(cost).toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "markup"],
          newMarkup.toString()
        );
      }
    } else if (type === "markup") {
      /* Markup price calculation */
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "markupIsPercentage"],
        isPercentage
      );
      if (isPercentage) {
        const newMarkup = Decimal(value);
        // Edit the price with the new provided value
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "markup"],
          newMarkup.toString()
        );
        // Cost * Markup = Price for %-based markups
        const newPrice = Decimal(cost)
          .dividedBy(100)
          .times(newMarkup)
          .plus(cost)
          .toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "value"],
          parseFloat(newPrice.toFixed(4))
        );
      } else {
        const newMarkup = Decimal(value);
        // Edit the price with the new provided value
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "markup"],
          newMarkup.toString()
        );
        // Cost + Markup = Price for $-based markups
        const newPrice = Decimal(cost).plus(newMarkup).toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "value"],
          parseFloat(newPrice.toFixed(4))
        );
      }
    } else if (type === "cost") {
      /* Cost price calculation */
      const newCost = value;
      const roundedCost = Decimal(newCost).toDecimalPlaces(4);
      const markup = kitItem.get("markup");
      // Cost * Markup = Price for %-based markups or Cost + Markup = Price for $-based markups
      const newPrice = markupIsPercentage
        ? roundedCost.dividedBy(100).times(markup).plus(roundedCost).toDecimalPlaces(4)
        : roundedCost.plus(Decimal(markup)).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "value"],
        parseFloat(newPrice.toFixed(4))
      );
      _state.setIn(["items", itemIndex, "productKitItems", cartIndex, "cost"], newCost);
    } else if (type === "discount") {
      // Save discount values
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "discount"],
        !isPositiveNumber(value) ? "0.00" : value
      );
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "discountIsPercentage"],
        isPercentage
      );
    }

    // Always apply the discount
    const discountIsPercentage =
      _state.getIn(["items", itemIndex, "productKitItems", cartIndex, "discountIsPercentage"]) ??
      true;
    const discount = Decimal(
      isPositiveNumber(_state.getIn(["items", itemIndex, "productKitItems", cartIndex, "discount"]))
        ? _state.getIn(["items", itemIndex, "productKitItems", cartIndex, "discount"])
        : 0
    );
    const itemUnitPrice = Decimal(
      isNumeric(_state.getIn(["items", itemIndex, "productKitItems", cartIndex, "value"]))
        ? _state.getIn(["items", itemIndex, "productKitItems", cartIndex, "value"])
        : 0
    ).toDecimalPlaces(4);

    // Compute item price with discount
    let newTotalWithDiscount;
    if (discountIsPercentage) {
      const discountRate = discount.dividedBy(100);
      newTotalWithDiscount = Decimal(itemUnitPrice)
        .times(quantity)
        .times(Decimal(1).minus(discountRate))
        .toDecimalPlaces(2);
    } else {
      newTotalWithDiscount = Decimal(itemUnitPrice)
        .times(quantity)
        .minus(discount)
        .toDecimalPlaces(2);
    }

    // Store value
    _state.setIn(
      ["items", itemIndex, "productKitItems", cartIndex, "totalValue"],
      newTotalWithDiscount.toFixed(2)
    );
    _state.setIn(["items", itemIndex, "productKitItems", cartIndex, "customValue"], null);

    const updatedCost = Decimal(
      _state.getIn(["items", itemIndex, "productKitItems", cartIndex, "cost"])
    ).toDecimalPlaces(4);
    // Always calculate the margin if cost is greater than 0
    if (updatedCost && updatedCost > 0) {
      // Item Total Value - (Cost * Qty) = Margin
      const itemTotalCost = Decimal(updatedCost).times(actualQuantity).toDecimalPlaces(4);
      const margin = Decimal(newTotalWithDiscount).minus(itemTotalCost).toDecimalPlaces(2);
      _state.setIn(["items", itemIndex, "productKitItems", cartIndex, "margin"], margin.toFixed(2));
    } else if (updatedCost?.equals(0)) {
      _state.setIn(["items", itemIndex, "productKitItems", cartIndex, "margin"], "0");
    }

    // update parent kit if standard priced kit
    if (!_state.getIn(["items", itemIndex, "customPricing"])) {
      const kitItems = _state.getIn(["items", itemIndex, "productKitItems"]);
      const kitItemTotals =
        kitItems && kitItems.size > 0
          ? kitItems.reduce((prev, current) => {
              return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
            }, Decimal(0))
          : Decimal(0);
      const subKits = _state.getIn(["items", itemIndex, "subKits"]);
      const subKitTotals =
        subKits && subKits.size > 0
          ? subKits.reduce((prev, current) => {
              return Decimal(prev).plus(current.get("totalValue"));
            }, Decimal(0))
          : Decimal(0);
      const parentKitTotal = kitItemTotals.plus(subKitTotals).toDecimalPlaces(2);
      const parentKitUnitPrice = parentKitTotal.dividedBy(parentKitQuantity).toDecimalPlaces(4);
      _state.setIn(["items", itemIndex, "totalValue"], parentKitTotal.toFixed(2));
      _state.setIn(["items", itemIndex, "value"], parentKitUnitPrice.toFixed(4));
      _state.setIn(["items", itemIndex, "markupIsPercentage"], false);
      _state.setIn(["items", itemIndex, "markup"], "0");
      _state.setIn(["items", itemIndex, "cost"], "0");
      _state.setIn(["items", itemIndex, "margin"], "0");
    }
  };

  changeSubKitItemPriceValue = (
    _state,
    { itemIndex, subKitIndex, cartIndex, type, value, isPercentage = false }
  ) => {
    const parentKitQuantity = _state.getIn(["items", itemIndex, "quantity"]);
    const currentSubKitQuantity = _state.getIn([
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "quantity",
    ]);
    const kitItem = _state.getIn([
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "kitProducts",
      cartIndex,
    ]);

    const quantityValues = {
      quantity: Decimal(0),
      actualQuantity: Decimal(0),
    };

    const pricingStrategy = kitItem.get("pricingStrategy");

    if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
      quantityValues.quantity = Decimal(kitItem.get("sumLengths").get("forPrice"))
        .dividedBy(1000)
        .toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(kitItem.get("actualSumLengths").get("forPrice"))
        .dividedBy(1000)
        .toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      const formulaOutput = kitItem.get("customFormulaOutput");
      const actualFormulaOutput = kitItem.get("actualCustomFormulaOutput");
      quantityValues.quantity = Decimal(kitItem.get("quantity"))
        .times(formulaOutput)
        .toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(kitItem.get("actualQuantity"))
        .times(actualFormulaOutput)
        .toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
      quantityValues.quantity = Decimal(kitItem.get("sumLengths").get("total")).toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(
        kitItem.get("actualSumLengths").get("total")
      ).toDecimalPlaces(4);
    } else if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
      quantityValues.quantity = Decimal(convertMmToFeet(kitItem.get("sumLengths").get("forPrice")));
      quantityValues.actualQuantity = Decimal(
        convertMmToFeet(kitItem.get("actualSumLengths").get("forPrice"))
      );
    } else if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
      quantityValues.quantity = Decimal(
        getSquareFeetQuantityInSquareFeet({ items: kitItem.get("lengthItems")?.toJS() ?? [] }).total
      );
      quantityValues.actualQuantity = Decimal(
        getSquareFeetQuantityInSquareFeet({ items: kitItem.get("actualLengthItems")?.toJS() ?? [] })
          .total
      );
    } else if (pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
      quantityValues.quantity = Decimal(kitItem.get("quantity")).toDecimalPlaces(4);
      quantityValues.actualQuantity = Decimal(kitItem.get("actualQuantity")).toDecimalPlaces(4);
    }
    const { quantity, actualQuantity } = quantityValues;
    const cost = Decimal(kitItem.get("cost")).toDecimalPlaces(4);
    const markupIsPercentage = kitItem.get("markupIsPercentage");

    if (type === "totalValue") {
      /* Total Value calculation */
      const newValue = Decimal(value).toDecimalPlaces(2);
      const discount = _state.getIn([
        "items",
        itemIndex,
        "subKits",
        subKitIndex,
        "kitProducts",
        cartIndex,
        "discount",
      ]);
      const subKitItemDiscountIsPercentage =
        _state.getIn([
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "discountIsPercentage",
        ]) ?? true;
      let itemTotalWithDiscount;
      if (subKitItemDiscountIsPercentage) {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        const discountRate = Decimal(100).minus(itemDiscount);
        itemTotalWithDiscount = discountRate.isZero()
          ? Decimal(0)
          : Decimal(value).dividedBy(discountRate).times(100).toDecimalPlaces(4);
      } else {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        itemTotalWithDiscount = Decimal(value).plus(itemDiscount).toDecimalPlaces(4);
      }
      const newUnitPrice = itemTotalWithDiscount.dividedBy(quantity).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "value"],
        newUnitPrice.toFixed(4)
      );
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "customValue"],
        newValue.toFixed(2)
      );

      if (cost && cost > 0) {
        const newMarkup = markupIsPercentage
          ? Decimal(newUnitPrice).minus(cost).dividedBy(cost).times(100).toDecimalPlaces(22)
          : Decimal(newUnitPrice).minus(cost);
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "markup"],
          newMarkup.toString()
        );
      }

      // update parent kit if standard priced kit
      if (!_state.getIn(["items", itemIndex, "customPricing"])) {
        const currentSubKitItems = _state.getIn([
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
        ]);
        const currentSubKitItemTotals = currentSubKitItems.reduce((prev, current) => {
          return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
        }, Decimal(0));
        const currentSubKitTotal = currentSubKitItemTotals.toDecimalPlaces(2);
        const currentSubKitUnitPrice = currentSubKitTotal
          .dividedBy(currentSubKitQuantity)
          .toDecimalPlaces(4);

        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "totalValue"],
          currentSubKitTotal.toFixed(2)
        );
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "value"],
          currentSubKitUnitPrice.toFixed(4)
        );
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markupIsPercentage"], false);
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markup"], "0");
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "cost"], "0");
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "margin"], "0");

        const kitItems = _state.getIn(["items", itemIndex, "productKitItems"]);
        const kitItemTotals =
          kitItems && kitItems.size > 0
            ? kitItems.reduce((prev, current) => {
                return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
              }, 0)
            : Decimal(0);
        const subKits = _state.getIn(["items", itemIndex, "subKits"]);
        const subKitTotals =
          subKits && subKits.size > 0
            ? subKits.reduce((prev, current) => {
                return Decimal(prev).plus(current.get("totalValue"));
              }, 0)
            : Decimal(0);
        const parentKitTotal = kitItemTotals.plus(subKitTotals).toDecimalPlaces(2);
        const parentKitUnitPrice = parentKitTotal.dividedBy(parentKitQuantity).toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "totalValue"], parentKitTotal.toFixed(2));
        _state.setIn(["items", itemIndex, "value"], parentKitUnitPrice.toFixed(4));
        _state.setIn(["items", itemIndex, "markupIsPercentage"], false);
        _state.setIn(["items", itemIndex, "markup"], "0");
        _state.setIn(["items", itemIndex, "cost"], "0");
        _state.setIn(["items", itemIndex, "margin"], "0");
      }
    } else if (type === "value") {
      /* Unit price calculation */
      const newValue = Decimal(value).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, type],
        !isNumeric(newValue.toFixed(4)) ? "0.00" : newValue.toFixed(4)
      );
      if (cost && cost > 0) {
        const newMarkup = markupIsPercentage
          ? Decimal(newValue).minus(cost).dividedBy(cost).times(100).toDecimalPlaces(22)
          : Decimal(newValue).minus(cost).toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "markup"],
          newMarkup.toString()
        );
      }
    } else if (type === "markup") {
      /* Markup price calculation */
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "markupIsPercentage",
        ],
        isPercentage
      );
      if (isPercentage) {
        const newMarkup = Decimal(value);
        // Edit the price with the new provided value
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "markup"],
          newMarkup.toString()
        );
        // Cost * Markup = Price for %-based markups
        const newPrice = Decimal(cost)
          .dividedBy(100)
          .times(newMarkup)
          .plus(cost)
          .toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "value"],
          parseFloat(newPrice.toFixed(4))
        );
      } else {
        const newMarkup = Decimal(value);
        // Edit the price with the new provided value
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "markup"],
          newMarkup.toString()
        );
        // Cost + Markup = Price for $-based markups
        const newPrice = Decimal(cost).plus(newMarkup).toDecimalPlaces(4);
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "value"],
          parseFloat(newPrice.toFixed(4))
        );
      }
    } else if (type === "cost") {
      /* Cost price calculation */
      const newCost = value;
      const roundedCost = Decimal(newCost).toDecimalPlaces(4);
      const markup = kitItem.get("markup");
      // Cost * Markup = Price for %-based markups or Cost + Markup = Price for $-based markups
      const newPrice = markupIsPercentage
        ? roundedCost.dividedBy(100).times(markup).plus(roundedCost).toDecimalPlaces(4)
        : roundedCost.plus(Decimal(markup)).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "value"],
        parseFloat(newPrice.toFixed(4))
      );
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "cost"],
        newCost
      );
    } else if (type === "discount") {
      // Save discount values
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "discount"],
        !isPositiveNumber(value) ? "0.00" : value
      );
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "discountIsPercentage",
        ],
        isPercentage
      );
    }

    // Always apply the discount
    const subKitDiscountIsPercentage =
      _state.getIn([
        "items",
        itemIndex,
        "subKits",
        subKitIndex,
        "kitProducts",
        cartIndex,
        "discountIsPercentage",
      ]) ?? true;
    const discount = Decimal(
      isPositiveNumber(
        _state.getIn([
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "discount",
        ])
      )
        ? _state.getIn([
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "discount",
          ])
        : 0
    );
    const itemUnitPrice = Decimal(
      isNumeric(
        _state.getIn([
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "value",
        ])
      )
        ? _state.getIn([
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "value",
          ])
        : 0
    ).toDecimalPlaces(4);

    // Compute item price with discount
    let newTotalWithDiscount;
    if (subKitDiscountIsPercentage) {
      const discountRate = discount.dividedBy(100);
      newTotalWithDiscount = Decimal(itemUnitPrice)
        .times(quantity)
        .times(Decimal(1).minus(discountRate))
        .toDecimalPlaces(2);
    } else {
      newTotalWithDiscount = Decimal(itemUnitPrice)
        .times(quantity)
        .minus(discount)
        .toDecimalPlaces(2);
    }

    // Store value
    _state.setIn(
      ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "totalValue"],
      newTotalWithDiscount.toFixed(2)
    );
    _state.setIn(
      ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "customValue"],
      null
    );

    const updatedCost = Decimal(
      _state.getIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "cost"])
    ).toDecimalPlaces(4);
    // Always calculate the margin if cost is greater than 0
    if (updatedCost && updatedCost > 0) {
      // Item Total Value - (Cost * Qty) = Margin
      const itemTotalCost = Decimal(updatedCost).times(actualQuantity).toDecimalPlaces(4);
      const margin = Decimal(newTotalWithDiscount).minus(itemTotalCost).toDecimalPlaces(2);
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "margin"],
        margin.toFixed(2)
      );
    } else if (updatedCost?.equals(0)) {
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "margin"],
        "0"
      );
    }

    // update parent kit if standard priced kit
    if (!_state.getIn(["items", itemIndex, "customPricing"])) {
      const currentSubKitItems = _state.getIn([
        "items",
        itemIndex,
        "subKits",
        subKitIndex,
        "kitProducts",
      ]);
      const currentSubKitItemTotals = currentSubKitItems.reduce((prev, current) => {
        return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
      }, Decimal(0));
      const currentSubKitTotal = currentSubKitItemTotals.toDecimalPlaces(2);
      const currentSubKitUnitPrice = currentSubKitTotal
        .dividedBy(currentSubKitQuantity)
        .toDecimalPlaces(4);

      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "totalValue"],
        currentSubKitTotal.toFixed(2)
      );
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "value"],
        currentSubKitUnitPrice.toFixed(4)
      );
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markupIsPercentage"], false);
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markup"], "0");
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "cost"], "0");
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "margin"], "0");

      const kitItems = _state.getIn(["items", itemIndex, "productKitItems"]);
      const kitItemTotals =
        kitItems && kitItems.size > 0
          ? kitItems.reduce((prev, current) => {
              return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
            }, Decimal(0))
          : Decimal(0);
      const subKits = _state.getIn(["items", itemIndex, "subKits"]);
      const subKitTotals =
        subKits && subKits.size > 0
          ? subKits.reduce((prev, current) => {
              return Decimal(prev).plus(current.get("totalValue"));
            }, Decimal(0))
          : Decimal(0);
      const parentKitTotal = kitItemTotals.plus(subKitTotals).toDecimalPlaces(2);
      const parentKitUnitPrice = parentKitTotal.dividedBy(parentKitQuantity).toDecimalPlaces(4);
      _state.setIn(["items", itemIndex, "totalValue"], parentKitTotal.toFixed(2));
      _state.setIn(["items", itemIndex, "value"], parentKitUnitPrice.toFixed(4));
      _state.setIn(["items", itemIndex, "markupIsPercentage"], false);
      _state.setIn(["items", itemIndex, "markup"], "0");
      _state.setIn(["items", itemIndex, "cost"], "0");
      _state.setIn(["items", itemIndex, "margin"], "0");
    }
  };

  changeSubKitPriceValue = (
    _state,
    { itemIndex, subKitIndex, type, value, isPercentage = false }
  ) => {
    const parentKitQuantity = _state.getIn(["items", itemIndex, "quantity"]);
    const currentSubKitQuantity = _state.getIn([
      "items",
      itemIndex,
      "subKits",
      subKitIndex,
      "quantity",
    ]);
    const subKit = _state.getIn(["items", itemIndex, "subKits", subKitIndex]);

    const quantityValues = {
      quantity: Decimal(0),
    };

    quantityValues.quantity = Decimal(subKit.get("quantity")).toDecimalPlaces(4);

    const { quantity } = quantityValues;

    if (type === "totalValue") {
      /* Total Value calculation */
      const newValue = Decimal(value).toDecimalPlaces(2);
      const discount = _state.getIn(["items", itemIndex, "subKits", subKitIndex, "discount"]);
      const subKitDiscountIsPercentage =
        _state.getIn(["items", itemIndex, "subKits", subKitIndex, "discountIsPercentage"]) ?? true;
      let itemTotalWithDiscount;
      if (subKitDiscountIsPercentage) {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        const discountRate = Decimal(100).minus(itemDiscount);
        itemTotalWithDiscount = discountRate.isZero()
          ? Decimal(0)
          : Decimal(value).dividedBy(discountRate).times(100).toDecimalPlaces(4);
      } else {
        const itemDiscount = Decimal(isPositiveNumber(discount) ? discount : 0);
        itemTotalWithDiscount = Decimal(value).plus(itemDiscount).toDecimalPlaces(4);
      }
      const newUnitPrice = itemTotalWithDiscount.dividedBy(quantity).toDecimalPlaces(4);
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "value"], newUnitPrice.toFixed(4));
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "customValue"],
        newValue.toFixed(2)
      );

      // update parent kit if standard priced kit
      if (!_state.getIn(["items", itemIndex, "customPricing"])) {
        const currentSubKitItems = _state.getIn([
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
        ]);
        const currentSubKitItemTotals = currentSubKitItems.reduce((prev, current) => {
          return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
        }, Decimal(0));
        const currentSubKitTotal = currentSubKitItemTotals.toDecimalPlaces(2);
        const currentSubKitUnitPrice = currentSubKitTotal
          .dividedBy(currentSubKitQuantity)
          .toDecimalPlaces(4);

        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "totalValue"],
          currentSubKitTotal.toFixed(2)
        );
        _state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "value"],
          currentSubKitUnitPrice.toFixed(4)
        );
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markupIsPercentage"], false);
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markup"], "0");
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "cost"], "0");
        _state.setIn(["items", itemIndex, "subKits", subKitIndex, "margin"], "0");

        const kitItems = _state.getIn(["items", itemIndex, "productKitItems"]);
        const kitItemTotals =
          kitItems && kitItems.size > 0
            ? kitItems.reduce((prev, current) => {
                return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
              }, Decimal(0))
            : Decimal(0);
        const subKits = _state.getIn(["items", itemIndex, "subKits"]);
        const subKitTotals =
          subKits && subKits.size > 0
            ? subKits.reduce((prev, current) => {
                return Decimal(prev).plus(current.get("totalValue"));
              }, Decimal(0))
            : Decimal(0);
        const parentKitTotal = kitItemTotals.plus(subKitTotals).toDecimalPlaces(2);
        const parentKitUnitPrice = parentKitTotal.dividedBy(parentKitQuantity).toDecimalPlaces(4);
        _state.setIn(["items", itemIndex, "totalValue"], parentKitTotal.toFixed(2));
        _state.setIn(["items", itemIndex, "value"], parentKitUnitPrice.toFixed(4));
        _state.setIn(["items", itemIndex, "markupIsPercentage"], false);
        _state.setIn(["items", itemIndex, "markup"], "0");
        _state.setIn(["items", itemIndex, "cost"], "0");
        _state.setIn(["items", itemIndex, "margin"], "0");
      }
    } else if (type === "value") {
      /* Unit price calculation */
      const newValue = Decimal(value).toDecimalPlaces(4);
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, type],
        !isNumeric(newValue.toFixed(4)) ? "0.00" : newValue.toFixed(4)
      );
    } else if (type === "discount") {
      // Save discount values
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "discount"],
        !isPositiveNumber(value) ? "0.00" : value
      );
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "discountIsPercentage"],
        isPercentage
      );
    }

    // Always apply the discount
    const subKitDiscountIsPercentage =
      _state.getIn(["items", itemIndex, "subKits", subKitIndex, "discountIsPercentage"]) ?? true;
    const discount = Decimal(
      isPositiveNumber(_state.getIn(["items", itemIndex, "subKits", subKitIndex, "discount"]))
        ? _state.getIn(["items", itemIndex, "subKits", subKitIndex, "discount"])
        : 0
    );
    const itemUnitPrice = Decimal(
      isNumeric(_state.getIn(["items", itemIndex, "subKits", subKitIndex, "value"]))
        ? _state.getIn(["items", itemIndex, "subKits", subKitIndex, "value"])
        : 0
    ).toDecimalPlaces(4);

    // Compute item price with discount
    let newTotalWithDiscount;
    if (subKitDiscountIsPercentage) {
      const discountRate = discount.dividedBy(100);
      newTotalWithDiscount = Decimal(itemUnitPrice)
        .times(quantity)
        .times(Decimal(1).minus(discountRate))
        .toDecimalPlaces(2);
    } else {
      newTotalWithDiscount = Decimal(itemUnitPrice)
        .times(quantity)
        .minus(discount)
        .toDecimalPlaces(2);
    }

    // Store value
    _state.setIn(
      ["items", itemIndex, "subKits", subKitIndex, "totalValue"],
      newTotalWithDiscount.toFixed(2)
    );
    _state.setIn(["items", itemIndex, "subKits", subKitIndex, "customValue"], null);

    // update parent kit if standard priced kit
    if (!_state.getIn(["items", itemIndex, "customPricing"])) {
      const currentSubKitItems = _state.getIn([
        "items",
        itemIndex,
        "subKits",
        subKitIndex,
        "kitProducts",
      ]);
      const currentSubKitItemTotals = currentSubKitItems.reduce((prev, current) => {
        return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
      }, Decimal(0));
      const currentSubKitTotal = currentSubKitItemTotals.toDecimalPlaces(2);
      const currentSubKitUnitPrice = currentSubKitTotal
        .dividedBy(currentSubKitQuantity)
        .toDecimalPlaces(4);

      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "totalValue"],
        currentSubKitTotal.toFixed(2)
      );
      _state.setIn(
        ["items", itemIndex, "subKits", subKitIndex, "value"],
        currentSubKitUnitPrice.toFixed(4)
      );
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markupIsPercentage"], false);
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "markup"], "0");
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "cost"], "0");
      _state.setIn(["items", itemIndex, "subKits", subKitIndex, "margin"], "0");

      const kitItems = _state.getIn(["items", itemIndex, "productKitItems"]);
      const kitItemTotals =
        kitItems && kitItems.size > 0
          ? kitItems.reduce((prev, current) => {
              return Decimal(prev).plus(current.get("customValue") || current.get("totalValue"));
            }, Decimal(0))
          : Decimal(0);
      const subKits = _state.getIn(["items", itemIndex, "subKits"]);
      const subKitTotals =
        subKits && subKits.size > 0
          ? subKits.reduce((prev, current) => {
              return Decimal(prev).plus(current.get("totalValue"));
            }, Decimal(0))
          : Decimal(0);
      const parentKitTotal = kitItemTotals.plus(subKitTotals).toDecimalPlaces(2);
      const parentKitUnitPrice = parentKitTotal.dividedBy(parentKitQuantity).toDecimalPlaces(4);
      _state.setIn(["items", itemIndex, "totalValue"], parentKitTotal.toFixed(2));
      _state.setIn(["items", itemIndex, "value"], parentKitUnitPrice.toFixed(4));
      _state.setIn(["items", itemIndex, "markupIsPercentage"], false);
      _state.setIn(["items", itemIndex, "markup"], "0");
      _state.setIn(["items", itemIndex, "cost"], "0");
      _state.setIn(["items", itemIndex, "margin"], "0");
    }
  };

  searchAllProducts = (_state, { payload: { data } }) => {
    const items = {};

    data.map((item) => {
      const { category } = item;
      if (!items[category]) {
        items[category] = [];
      }

      items[category].push(camelcaseKeys(item, { deep: true }));
    });

    return _state.setIn(["dataServer", "items"], fromJS(items));
  };

  searchByCategory = (_state, { payload: { data } }) => {
    const items = {};
    let categoryName = "";
    data.map((item) => {
      const { category } = item;
      categoryName = category;
      if (!items[category]) {
        items[category] = [];
      }

      items[category].push(camelcaseKeys(item, { deep: true }));
    });

    return _state.setIn(["dataServer", "items", categoryName], fromJS(items[categoryName]));
  };

  updateUidOfCustomFormula = (_state, data) => {
    // For product kits
    _state.updateIn(["items"], (arr) =>
      arr.map((item) => {
        const parsedItem = { ...item.toJS() };
        if (
          parsedItem.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL &&
          parsedItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL &&
          !parsedItem.isDeleted
        ) {
          const product = data.find((a) => a.uid === parsedItem.uid);
          if (product) {
            return fromJS({
              ...parsedItem,
              customFormula: { ...parsedItem.customFormula, uid: product.custom_formula.uid },
            });
          }

          return fromJS({ ...parsedItem });
        }
        if (parsedItem.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL && !parsedItem.isDeleted) {
          const product = data.find((a) => a.uid === parsedItem.uid);
          return fromJS({
            ...parsedItem,
            productKitItems: parsedItem.productKitItems.map((kitItem) => {
              if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
                if (product) {
                  const productKitItem = product.kit_items.find((i) => i.uid === kitItem.uid);
                  if (productKitItem) {
                    return fromJS({
                      ...kitItem,
                      customFormula: {
                        ...kitItem.customFormula,
                        uid: productKitItem.custom_formula.uid,
                      },
                    });
                  }
                }

                return fromJS({ ...kitItem });
              }

              return fromJS({ ...kitItem });
            }),
            subKits: parsedItem.subKits.map((subKit, subKitIndex) => {
              const subKitProducts = subKit.kitProducts.map((kitItem) => {
                if (kitItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
                  if (product) {
                    const subKitItem = product.sub_kits[subKitIndex].kit_products.find(
                      (i) => i.uid === kitItem.uid
                    );

                    if (subKitItem) {
                      return fromJS({
                        ...kitItem,
                        customFormula: {
                          ...kitItem.customFormula,
                          uid: subKitItem.custom_formula.uid,
                        },
                      });
                    }
                  }

                  return fromJS({ ...kitItem });
                }

                return fromJS({ ...kitItem });
              });

              return fromJS({
                ...subKit,
                kitProducts: subKitProducts,
              });
            }),
          });
        }

        return fromJS({ ...parsedItem });
      })
    );

    // For normal product
    _state.updateIn(["items"], (arr) =>
      arr.map((item) => {
        const parsedItem = { ...item.toJS() };
        if (
          parsedItem.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL &&
          !parsedItem.isDeleted
        ) {
          const product = data.find((a) => a.uid === parsedItem.uid);
          if (product) {
            return fromJS({
              ...parsedItem,
              customFormula: { ...parsedItem.customFormula, uid: product.custom_formula.uid },
            });
          }

          return fromJS({ ...parsedItem });
        }

        return fromJS({ ...parsedItem });
      })
    );
  };

  changeCustomFormulaValues = (_state, payload) => {
    const { index, itemIndex, subKitIndex, cartIndex, key, value, isProductKit, isSubKit } =
      payload;

    if (isSubKit) {
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "customFormula",
          "elements",
          index,
          key,
        ],
        value
      );
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "customFormula",
          "appliedElements",
          index,
          key,
        ],
        value
      );

      try {
        const customFormula = _state
          .getIn([
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
          ])
          .toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormulaOutput",
          ],
          formattedFormulaOutput
        );
        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "actualCustomFormulaOutput",
          ],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else if (isProductKit) {
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "elements", index, key],
        value
      );
      _state.setIn(
        [
          "items",
          itemIndex,
          "productKitItems",
          cartIndex,
          "customFormula",
          "appliedElements",
          index,
          key,
        ],
        value
      );

      try {
        const customFormula = _state
          .getIn(["items", itemIndex, "productKitItems", cartIndex, "customFormula"])
          .toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormulaOutput"],
          formattedFormulaOutput
        );
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "actualCustomFormulaOutput"],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else {
      _state.setIn(["items", itemIndex, "customFormula", "elements", index, key], value);
      _state.setIn(["items", itemIndex, "customFormula", "appliedElements", index, key], value);

      try {
        const customFormula = _state.getIn(["items", itemIndex, "customFormula"]).toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(["items", itemIndex, "customFormulaOutput"], formattedFormulaOutput);
        _state.setIn(["items", itemIndex, "actualCustomFormulaOutput"], formattedFormulaOutput);
      } catch (_) {
        /* Do nothing */
      }
    }

    if (isSubKit) {
      const item = _state
        .getIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex])
        .toJS();
      this.changeSubKitItemPriceValue(_state, {
        itemIndex,
        subKitIndex,
        cartIndex,
        type: "value",
        value: item.value,
      });
    } else if (isProductKit) {
      const item = _state.getIn(["items", itemIndex, "productKitItems", cartIndex]).toJS();
      this.changeProductKitItemPriceValue(_state, {
        itemIndex,
        cartIndex,
        type: "value",
        value: item.value,
      });
    } else {
      const item = _state.getIn(["items", itemIndex]).toJS();
      this.changeProductPriceValue(_state, {
        itemIndex: itemIndex,
        type: "value",
        value: item.value,
      });
    }
  };

  changeActualCustomFormulaValues = (_state, payload) => {
    const { index, itemIndex, subKitIndex, cartIndex, key, value, isProductKit, isSubKit } =
      payload;

    if (isSubKit) {
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "customFormula",
          "appliedElements",
          index,
          key,
        ],
        value
      );

      try {
        const customFormula = _state
          .getIn([
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
          ])
          .toJS();
        const formula = customFormula.appliedElements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);
        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "actualCustomFormulaOutput",
          ],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else if (isProductKit) {
      _state.setIn(
        [
          "items",
          itemIndex,
          "productKitItems",
          cartIndex,
          "customFormula",
          "appliedElements",
          index,
          key,
        ],
        value
      );

      try {
        const customFormula = _state
          .getIn(["items", itemIndex, "productKitItems", cartIndex, "customFormula"])
          .toJS();
        const formula = customFormula.appliedElements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "actualCustomFormulaOutput"],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else {
      _state.setIn(["items", itemIndex, "customFormula", "appliedElements", index, key], value);

      try {
        const customFormula = _state.getIn(["items", itemIndex, "customFormula"]).toJS();
        const formula = customFormula.appliedElements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(["items", itemIndex, "actualCustomFormulaOutput"], formattedFormulaOutput);
      } catch (_) {
        /* Do nothing */
      }
    }
  };

  addCustomFormulaValues = (_state, payload) => {
    const { elements, itemIndex, subKitIndex, cartIndex, isProductKit, isSubKit } = payload;
    if (isSubKit) {
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "customFormula",
          "elements",
        ],
        elements
      );
      _state.setIn(
        [
          "items",
          itemIndex,
          "subKits",
          subKitIndex,
          "kitProducts",
          cartIndex,
          "customFormula",
          "appliedElements",
        ],
        elements
      );

      try {
        const customFormula = _state
          .getIn([
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
          ])
          .toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormulaOutput",
          ],
          formattedFormulaOutput
        );
        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "actualCustomFormulaOutput",
          ],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else if (isProductKit) {
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "elements"],
        elements
      );
      _state.setIn(
        ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "appliedElements"],
        elements
      );

      try {
        const customFormula = _state
          .getIn(["items", itemIndex, "productKitItems", cartIndex, "customFormula"])
          .toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormulaOutput"],
          formattedFormulaOutput
        );
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "actualCustomFormulaOutput"],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else {
      _state.setIn(["items", itemIndex, "customFormula", "elements"], elements);
      _state.setIn(["items", itemIndex, "customFormula", "appliedElements"], elements);

      try {
        const customFormula = _state.getIn(["items", itemIndex, "customFormula"]).toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(["items", itemIndex, "customFormulaOutput"], formattedFormulaOutput);
        _state.setIn(["items", itemIndex, "actualCustomFormulaOutput"], formattedFormulaOutput);
      } catch (_) {
        /* Do nothing */
      }
    }

    if (isSubKit) {
      const item = _state
        .getIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex])
        .toJS();
      this.changeSubKitItemPriceValue(_state, {
        itemIndex,
        subKitIndex,
        cartIndex,
        type: "value",
        value: item.value,
      });
    } else if (isProductKit) {
      const item = _state.getIn(["items", itemIndex, "productKitItems", cartIndex]).toJS();
      this.changeProductKitItemPriceValue(_state, {
        itemIndex,
        cartIndex,
        type: "value",
        value: item.value,
      });
    } else {
      const item = _state.getIn(["items", itemIndex]).toJS();
      this.changeProductPriceValue(_state, {
        itemIndex: itemIndex,
        type: "value",
        value: item.value,
      });
    }
  };

  removeCustomFormulaValues = (_state, payload) => {
    const { index, itemIndex, subKitIndex, cartIndex, isProductKit, isSubKit } = payload;

    if (isSubKit) {
      if (index === 0) {
        _state.updateIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
            "elements",
          ],
          (arr) => arr.filter((_, elementIndex) => elementIndex > index + 1)
        );
        _state.updateIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
            "appliedElements",
          ],
          (arr) => arr.filter((_, elementIndex) => elementIndex > index + 1)
        );
      } else {
        _state.updateIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
            "elements",
          ],
          (arr) => arr.filter((_, elementIndex) => elementIndex < index - 1 || elementIndex > index)
        );
        _state.updateIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
            "appliedElements",
          ],
          (arr) => arr.filter((_, elementIndex) => elementIndex < index - 1 || elementIndex > index)
        );
      }

      try {
        const customFormula = _state
          .getIn([
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormula",
          ])
          .toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "customFormulaOutput",
          ],
          formattedFormulaOutput
        );
        _state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "actualCustomFormulaOutput",
          ],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else if (isProductKit) {
      if (index === 0) {
        _state.updateIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "elements"],
          (arr) => arr.filter((_, elementIndex) => elementIndex > index + 1)
        );
        _state.updateIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "appliedElements"],
          (arr) => arr.filter((_, elementIndex) => elementIndex > index + 1)
        );
      } else {
        _state.updateIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "elements"],
          (arr) => arr.filter((_, elementIndex) => elementIndex < index - 1 || elementIndex > index)
        );
        _state.updateIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormula", "appliedElements"],
          (arr) => arr.filter((_, elementIndex) => elementIndex < index - 1 || elementIndex > index)
        );
      }

      try {
        const customFormula = _state
          .getIn(["items", itemIndex, "productKitItems", cartIndex, "customFormula"])
          .toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "customFormulaOutput"],
          formattedFormulaOutput
        );
        _state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "actualCustomFormulaOutput"],
          formattedFormulaOutput
        );
      } catch (_) {
        /* Do nothing */
      }
    } else {
      if (index === 0) {
        _state.updateIn(["items", itemIndex, "customFormula", "elements"], (arr) =>
          arr.filter((_, elementIndex) => elementIndex > index + 1)
        );
        _state.updateIn(["items", itemIndex, "customFormula", "appliedElements"], (arr) =>
          arr.filter((_, elementIndex) => elementIndex > index + 1)
        );
      } else {
        _state.updateIn(["items", itemIndex, "customFormula", "elements"], (arr) =>
          arr.filter((_, elementIndex) => elementIndex < index - 1 || elementIndex > index)
        );
        _state.updateIn(["items", itemIndex, "customFormula", "appliedElements"], (arr) =>
          arr.filter((_, elementIndex) => elementIndex < index - 1 || elementIndex > index)
        );
      }

      try {
        const customFormula = _state.getIn(["items", itemIndex, "customFormula"]).toJS();
        const formula = customFormula.elements.map((e) => e.value).join("");
        const formulaOutput = evaluate(formula);
        const roundedFormulaOutput = mathjsRound(formulaOutput, 4);
        const formattedFormulaOutput = customFormula.isRoundedToNearestWholeNumber
          ? Math.round(roundedFormulaOutput)
          : Decimal(roundedFormulaOutput).toDecimalPlaces(4).toFixed(4);

        _state.setIn(["items", itemIndex, "customFormulaOutput"], formattedFormulaOutput);
        _state.setIn(["items", itemIndex, "actualCustomFormulaOutput"], formattedFormulaOutput);
      } catch (_) {
        /* Do nothing */
      }
    }

    if (isSubKit) {
      const item = _state
        .getIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex])
        .toJS();
      this.changeSubKitItemPriceValue(_state, {
        itemIndex,
        subKitIndex,
        cartIndex,
        type: "value",
        value: item.value,
      });
    } else if (isProductKit) {
      const item = _state.getIn(["items", itemIndex, "productKitItems", cartIndex]).toJS();
      this.changeProductKitItemPriceValue(_state, {
        itemIndex,
        cartIndex,
        type: "value",
        value: item.value,
      });
    } else {
      const item = _state.getIn(["items", itemIndex]).toJS();
      this.changeProductPriceValue(_state, {
        itemIndex: itemIndex,
        type: "value",
        value: item.value,
      });
    }
  };

  saveProductToCatalogue = (_state, payload) => {
    const savedRow = payload.tables?.[0]?.values?.[0];
    const additionalProduct = payload.uid;
    const additionalProductRow = savedRow?.uid ?? null;
    const types = savedRow?.types ?? [];
    const rowPriceLevel = savedRow?.rowPrices?.[0]?.uid ?? null;

    _state.setIn(["items", payload.index, "productDescription"], "");
    _state.setIn(["items", payload.index, "additionalProduct"], additionalProduct);
    _state.setIn(["items", payload.index, "additionalProductRow"], additionalProductRow);
    _state.setIn(["items", payload.index, "rowPriceLevel"], rowPriceLevel);
    _state.setIn(["items", payload.index, "productType"], PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL);
    _state.setIn(["items", payload.index, "types"], types);
  };

  saveProductKitToCatalogue = (_state, payload) => {
    const additionalProduct = payload.uid;
    const additionalProductRow = payload.productKitItem?.productKitRows[0]?.uid ?? null;
    const rowPriceLevel = payload.productKitItem?.productKitRows[0]?.rowPriceLevels[0]?.uid ?? null;

    _state.setIn(["items", payload.index, "additionalProduct"], additionalProduct);
    _state.setIn(["items", payload.index, "additionalProductRow"], additionalProductRow);
    _state.setIn(["items", payload.index, "rowPriceLevel"], rowPriceLevel);
  };

  saveKitItemToCatalogue = (_state, payload) => {
    const savedRow = payload.tables?.[0]?.values?.[0];
    const product = payload.uid;
    const productRow = savedRow?.uid ?? null;
    const types = savedRow?.types ?? [];
    const rowPriceLevel = savedRow?.rowPrices?.[0]?.uid ?? null;

    _state.setIn(
      ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "productDescription"],
      ""
    );
    _state.setIn(
      ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "product", "uid"],
      product
    );
    _state.setIn(
      ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "productRow", "uid"],
      productRow
    );
    _state.setIn(
      ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "rowPriceLevel"],
      rowPriceLevel
    );
    _state.setIn(
      ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "productType"],
      PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL
    );
    _state.setIn(
      ["items", payload.itemIndex, "productKitItems", payload.cartIndex, "types"],
      types
    );
  };

  saveSubKitItemToCatalogue = (_state, payload) => {
    const savedRow = payload.tables?.[0]?.values?.[0];
    const product = payload.uid;
    const productRow = savedRow?.uid ?? null;
    const types = savedRow?.types ?? [];
    const rowPriceLevel = savedRow?.rowPrices?.[0]?.uid ?? null;

    _state.setIn(
      [
        "items",
        payload.itemIndex,
        "subKits",
        payload.subKitIndex,
        "kitProducts",
        payload.cartIndex,
        "productDescription",
      ],
      ""
    );

    _state.setIn(
      [
        "items",
        payload.itemIndex,
        "subKits",
        payload.subKitIndex,
        "kitProducts",
        payload.cartIndex,
        "product",
        "uid",
      ],
      product
    );
    _state.setIn(
      [
        "items",
        payload.itemIndex,
        "subKits",
        payload.subKitIndex,
        "kitProducts",
        payload.cartIndex,
        "productRow",
        "uid",
      ],
      productRow
    );
    _state.setIn(
      [
        "items",
        payload.itemIndex,
        "subKits",
        payload.subKitIndex,
        "kitProducts",
        payload.cartIndex,
        "rowPriceLevel",
      ],
      rowPriceLevel
    );
    _state.setIn(
      [
        "items",
        payload.itemIndex,
        "subKits",
        payload.subKitIndex,
        "kitProducts",
        payload.cartIndex,
        "productType",
      ],
      PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL
    );
    _state.setIn(
      [
        "items",
        payload.itemIndex,
        "subKits",
        payload.subKitIndex,
        "kitProducts",
        payload.cartIndex,
        "types",
      ],
      types
    );
  };

  defineReducers() {
    return {
      [ACTIONS.CREATE_NEW_ORDER]: withMutations(this.createNewOrder),

      [ACTIONS.SET_UID_OF_CUSTOM_FORMULA]: withMutations(this.setUidOfCustomFormula),

      [ACTIONS.SET_UID_OF_KIT_ITEM_CUSTOM_FORMULA]: withMutations(
        this.setUidOfKitItemCustomFormula
      ),

      [ACTIONS.SET_UID]: withMutations(this.setUid),

      [ACTIONS.SET_KIT_ITEM_UID]: withMutations(this.setKitItemUid),

      [ACTIONS.SET_UIDS]: withMutations(this.setUids),

      [`${ACTIONS.SEARCH_PRODUCTS} fulfilled`]: this.searchAllProducts,

      [`${ACTIONS.SEARCH_PRODUCTS} pending`]: this.thunkPendingReducer(["dataServer", "isPending"]),

      [`${ACTIONS.SEARCH_PRODUCTS_BY_CATEGORY} fulfilled`]: this.searchByCategory,

      [`${ACTIONS.SEARCH_PRODUCTS_BY_CATEGORY} pending`]: (state, { payload: { isPending, id } }) =>
        state.setIn(["categories", id, "isPending"], isPending),

      [`${ACTIONS.GET_PRODUCT} fulfilled`]: withMutations(this.setActiveProduct),

      [`${ACTIONS.GET_PRODUCT} pending`]: this.thunkPendingReducer("isPending"),

      [`${ACTIONS.OPEN_ORDER} fulfilled`]: withMutations(this.openOrder),

      [`${ACTIONS.RESET_ORDER_PRODUCTS} fulfilled`]: (state) =>
        state.set("items", state.get("startStateOrderProducts")),

      // actions for product editor

      [`${ACTIONS.GET_CATEGORIES} fulfilled`]: (state, { payload: { data } }) =>
        state.set("categories", fromJS(camelcaseKeys(data))),

      [`${ACTIONS.GET_CATEGORIES} pending`]: this.thunkPendingReducer("isPending"),

      // actions for adding to order

      [ACTIONS.SET_ACTIVE_PRODUCT]: withMutations(this.setActiveProduct),

      [ACTIONS.SET_ACTIVE_TAB]: (state, { payload }) => state.setIn(["activeTabs"], payload || {}),

      [ACTIONS.SORT_BY_CATEGORY]: (state) =>
        state.update("items", (list) => {
          let defaultCategory = fromJS([]);
          let newList = list.filter((item) => {
            if (item.get("categoryName") !== "Miscellaneous") {
              return true;
            }

            defaultCategory = defaultCategory.push(item);
            return false;
          });

          newList = newList.sort((a, b) => {
            if (!a.get("categoryName")) {
              return 1;
            }
            if (!b.get("categoryName")) {
              return -1;
            }

            const catName1 = a.get("categoryName").toLowerCase();
            const catName2 = b.get("categoryName").toLowerCase();
            if (catName1 === catName2) {
              const prodName1 = a.get("name").toLowerCase();
              const prodName2 = b.get("name").toLowerCase();

              if (prodName1 < prodName2) {
                return -1;
              }
              if (prodName1 > prodName2) {
                return 1;
              }
            }
            if (catName1 < catName2) {
              return -1;
            }
            if (catName1 > catName2) {
              return 1;
            }
            return 0;
          });

          const sortedList = newList.concat(defaultCategory);

          return sortedList.map((item, index) => item.set("index", index));
        }),

      [`${ACTIONS.ADD_ADDITIONAL_PRODUCT} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),

      [`${ACTIONS.ADD_ON_THE_FLY_PRODUCT_KIT} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),
      [`${ACTIONS.ADD_ON_THE_FLY_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, subKit } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex], fromJS(subKit)),
      [`${ACTIONS.ADD_PRODUCT_KIT_AS_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, subKits } }
      ) =>
        // FAC-4861: the fan-out produces 1..N sub-kits (preserved + one per catalogue sub-kit);
        // insert each at its slot so promoted siblings aren't dropped.
        subKits.reduce(
          (nextState, subKit, offset) =>
            nextState.setIn(["items", itemIndex, "subKits", subKitIndex + offset], fromJS(subKit)),
          state
        ),
      // FAC-4861: rollback for a rejected save — remove the optimistically spliced sub-kits,
      // otherwise their uid-less rows render a permanent loading overlay.
      [ACTIONS.REMOVE_SUB_KITS]: (state, { payload: { itemIndex, subKitIndex, count } }) =>
        state.getIn(["items", itemIndex, "subKits"])
          ? state.updateIn(["items", itemIndex, "subKits"], (subKits) =>
              subKits.splice(subKitIndex, count)
            )
          : state,
      [`${ACTIONS.ADD_ON_THE_FLY_PRODUCT_ITEM} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),
      [`${ACTIONS.ADD_LABOUR_LINE_ITEM} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),
      [`${ACTIONS.EDIT_LABOUR_LINE_ITEM} fulfilled`]: (state, { payload: { itemIndex, item } }) =>
        state.setIn(["items", itemIndex], item),
      [`${ACTIONS.ADD_NOTES_LINE_ITEM} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),

      [ACTIONS.EDIT_ADDITIONAL_PRODUCT]: (state, { payload }) =>
        state.set("editItem", fromJS(payload) || null),

      [`${ACTIONS.CHANGE_PRODUCT_IS_TAX_FREE} fulfilled`]: (
        state,
        { payload: { itemIndex, status } }
      ) => state.setIn(["items", itemIndex, "isTaxFree"], status),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ITEM_IS_TAX_FREE} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, status } }
      ) => state.setIn(["items", itemIndex, "productKitItems", cartIndex, "isTaxFree"], status),

      [`${ACTIONS.CHANGE_SUB_KIT_ITEM_IS_TAX_FREE} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, status } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "isTaxFree"],
          status
        ),

      [`${ACTIONS.IS_DUPLICATING_PRODUCT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, productType, value } }
      ) => {
        if (productType === PRODUCT_TYPE_PRODUCT_KIT_ITEM_LABEL) {
          return state.setIn(
            ["items", itemIndex, "productKitItems", cartIndex, "isDuplicating"],
            value
          );
        }
        if (productType === PRODUCT_TYPE_PRODUCT_SUB_KIT_ITEM_LABEL) {
          return state.setIn(
            ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "isDuplicating"],
            value
          );
        }
        return state.setIn(["items", itemIndex, "isDuplicating"], value);
      },

      [`${ACTIONS.DUPLICATE_ADDITIONAL_PRODUCT} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),

      [`${ACTIONS.DUPLICATE_KIT} fulfilled`]: (state, { payload: item }) =>
        state.update("items", (list) => list.push(item)),

      [`${ACTIONS.DUPLICATE_KIT_ITEM} fulfilled`]: (state, { payload: { itemIndex, items } }) =>
        state.setIn(["items", itemIndex, "productKitItems"], items),

      [`${ACTIONS.DUPLICATE_SUB_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, items } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], items),

      [`${ACTIONS.SAVE_EDIT_ADDITIONAL_PRODUCT} fulfilled`]: (
        state,
        { payload: { editItem, itemIndex } }
      ) => state.setIn(["items", itemIndex], fromJS(editItem)),

      [`${ACTIONS.CHANGE_PRODUCT_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, value } }
      ) => {
        const item = state.getIn(["items", itemIndex]).toJS();

        if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
          // FAC-5897: a labour row's actual is hours worked, so only the estimate moves.
          return state.setIn(["items", itemIndex, "quantity"], `${value}`);
        }

        const withQuantity = state.setIn(["items", itemIndex, "quantity"], `${value}`);

        return isActualTrackingEstimate(item.quantity, item.actualQuantity)
          ? withQuantity.setIn(["items", itemIndex, "actualQuantity"], `${value}`)
          : withQuantity;
      },

      [`${ACTIONS.CHANGE_PRODUCT_ACTUAL_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, value, customActualQuantity } }
      ) =>
        state
          .setIn(["items", itemIndex, "actualQuantity"], `${value}`)
          .setIn(["items", itemIndex, "customActualQuantity"], `${customActualQuantity}`),

      [`${ACTIONS.CHANGE_PRODUCT_ACTUAL_QUANTITY_INVENTORY} fulfilled`]: (
        state,
        { payload: { itemIndex, value } }
      ) => state.setIn(["items", itemIndex, "actualQuantityInventory"], toStateValue(value)),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, value, newKitItems } }
      ) => {
        const kit = state.getIn(["items", itemIndex]);
        const withQuantity = state
          .setIn(["items", itemIndex, "quantity"], `${value}`)
          .setIn(["items", itemIndex, "productKitItems"], newKitItems);

        return isActualTrackingEstimate(kit.get("quantity"), kit.get("actualQuantity"))
          ? withQuantity.setIn(["items", itemIndex, "actualQuantity"], `${value}`)
          : withQuantity;
      },

      [`${ACTIONS.CHANGE_SUB_KIT_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, value, newSubKitItems } }
      ) => {
        const subKit = state.getIn(["items", itemIndex, "subKits", subKitIndex]);
        const withQuantity = state
          .setIn(["items", itemIndex, "subKits", subKitIndex, "quantity"], `${value}`)
          .setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], newSubKitItems);

        return isActualTrackingEstimate(subKit.get("quantity"), subKit.get("actualQuantity"))
          ? withQuantity.setIn(
              ["items", itemIndex, "subKits", subKitIndex, "actualQuantity"],
              `${value}`
            )
          : withQuantity;
      },

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ACTUAL_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, value, newKitItems } }
      ) =>
        state
          .setIn(["items", itemIndex, "actualQuantity"], `${value}`)
          .setIn(["items", itemIndex, "productKitItems"], newKitItems),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ACTUAL_QUANTITY_INVENTORY} fulfilled`]: (
        state,
        { payload: { itemIndex, value, newKitItems } }
      ) =>
        state
          .setIn(["items", itemIndex, "actualQuantityInventory"], toStateValue(value))
          .setIn(["items", itemIndex, "productKitItems"], newKitItems),

      [`${ACTIONS.CHANGE_SUB_KIT_ACTUAL_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, value, newSubKitItems } }
      ) =>
        state
          .setIn(["items", itemIndex, "subKits", subKitIndex, "actualQuantity"], `${value}`)
          .setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], newSubKitItems),

      [`${ACTIONS.CHANGE_SUB_KIT_ACTUAL_QUANTITY_INVENTORY} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, value, newSubKitItems } }
      ) =>
        state
          .setIn(
            ["items", itemIndex, "subKits", subKitIndex, "actualQuantityInventory"],
            toStateValue(value)
          )
          .setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], newSubKitItems),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ITEM_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, kitQuantity, quantity } }
      ) => {
        const item = state.getIn(["items", itemIndex, "productKitItems", cartIndex]).toJS();
        const path = ["items", itemIndex, "productKitItems", cartIndex];

        if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
          // FAC-5897: a labour row's actual is hours worked, so only the estimate moves.
          return state
            .setIn([...path, "quantity"], `${quantity}`)
            .setIn(
              [...path, "baseQuantity"],
              `${Decimal(quantity).dividedBy(kitQuantity).toDecimalPlaces(4)}`
            );
        }

        const withQuantity = state
          .setIn([...path, "quantity"], `${quantity}`)
          .setIn(
            [...path, "baseQuantity"],
            `${Decimal(quantity).dividedBy(kitQuantity).toDecimalPlaces(4)}`
          );

        return isActualTrackingEstimate(item.quantity, item.actualQuantity)
          ? withQuantity.setIn([...path, "actualQuantity"], `${quantity}`)
          : withQuantity;
      },

      [`${ACTIONS.CHANGE_SUB_KIT_ITEM_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, subKitQuantity, quantity } }
      ) => {
        const item = state
          .getIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex])
          .toJS();

        const path = ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex];
        if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
          // FAC-5897: a labour row's actual is hours worked, so only the estimate moves.
          return state
            .setIn([...path, "quantity"], `${quantity}`)
            .setIn(
              [...path, "baseQuantity"],
              `${Decimal(quantity).dividedBy(subKitQuantity).toDecimalPlaces(4)}`
            );
        }

        const withQuantity = state
          .setIn([...path, "quantity"], `${quantity}`)
          .setIn(
            [...path, "baseQuantity"],
            `${Decimal(quantity).dividedBy(subKitQuantity).toDecimalPlaces(4)}`
          );

        return isActualTrackingEstimate(item.quantity, item.actualQuantity)
          ? withQuantity.setIn([...path, "actualQuantity"], `${quantity}`)
          : withQuantity;
      },

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ITEM_ACTUAL_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, actualQuantity, customActualQuantity = 0 } }
      ) =>
        state
          .setIn(
            ["items", itemIndex, "productKitItems", cartIndex, "actualQuantity"],
            `${actualQuantity}`
          )
          .setIn(
            ["items", itemIndex, "productKitItems", cartIndex, "customActualQuantity"],
            `${customActualQuantity}`
          ),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ITEM_ACTUAL_QUANTITY_INVENTORY} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, actualQuantityInventory } }
      ) =>
        state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "actualQuantityInventory"],
          toStateValue(actualQuantityInventory)
        ),

      [`${ACTIONS.CHANGE_SUB_KIT_ITEM_ACTUAL_QUANTITY} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, actualQuantity, customActualQuantity = 0 } }
      ) =>
        state
          .setIn(
            [
              "items",
              itemIndex,
              "subKits",
              subKitIndex,
              "kitProducts",
              cartIndex,
              "actualQuantity",
            ],
            `${actualQuantity}`
          )
          .setIn(
            [
              "items",
              itemIndex,
              "subKits",
              subKitIndex,
              "kitProducts",
              cartIndex,
              "customActualQuantity",
            ],
            `${customActualQuantity}`
          ),

      [`${ACTIONS.CHANGE_SUB_KIT_ITEM_ACTUAL_QUANTITY_INVENTORY} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, actualQuantityInventory } }
      ) =>
        state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "actualQuantityInventory",
          ],
          toStateValue(actualQuantityInventory)
        ),

      [`${ACTIONS.REMOVE_PRODUCT_FROM_ORDER} fulfilled`]: this.removeItemFromOrder,

      [`${ACTIONS.DELETE_SUB_KIT_FROM_ORDER} fulfilled`]: this.removeSubKitFromOrder,

      [`${ACTIONS.REMOVE_KIT_ITEM_FROM_ORDER} fulfilled`]: this.removeKitItemFromOrder,

      [`${ACTIONS.REMOVE_SUB_KIT_ITEM_FROM_ORDER} fulfilled`]: this.removeSubKitItemFromOrder,

      [ACTIONS.CHANGE_ITEM_ORDER_INDEX]: (state, { payload }) => {
        const orderItems = state.getIn(["items"]).toJS();
        const removed = orderItems.find((_, index) => payload.source.index === index);
        const newList = orderItems.filter((_, index) => payload.source.index !== index);
        newList.splice(payload.destination.index, 0, removed);

        return state.setIn(
          ["items"],
          fromJS(
            newList.map((n, index) => ({
              ...n,
              index,
            }))
          )
        );
      },

      [ACTIONS.CHANGE_PRODUCT_KIT_ITEM_ORDER_INDEX]: (state, { payload }) => {
        const orderItems = state.getIn(["items"]).toJS();
        const [itemPart] = payload.draggableId.split("-");
        const [, itemIndex] = itemPart.split("_");

        const { productKitItems } = orderItems[Number(itemIndex)];

        const removedKitItem = productKitItems.find((_, index) => payload.source.index === index);
        const newList = productKitItems.filter((_, index) => payload.source.index !== index);

        newList.splice(payload.destination.index, 0, removedKitItem);

        orderItems[itemIndex].productKitItems = newList.map((n, index) => ({
          ...n,
          cartIndex: index,
        }));

        return state.setIn(["items"], fromJS(orderItems));
      },

      [ACTIONS.CHANGE_SUB_KIT_ITEM_ORDER_INDEX]: (state, { payload }) => {
        let newState = state;
        const orderItems = state.getIn(["items"]).toJS();
        const [itemPart, subKitPart] = payload.draggableId.split("-");
        const [, itemIndex] = itemPart.split("_");
        const [, subKitIndex] = subKitPart.split("_");
        const { kitProducts } = orderItems[Number(itemIndex)].subKits[Number(subKitIndex)];

        // Remove the dragged item from the list
        const removed = kitProducts[payload.source.index];
        const newList = kitProducts.filter((_, index) => payload.source.index !== index);

        // Insert the dragged item at the new position
        newList.splice(payload.destination.index, 0, removed);

        // Update the orderItems with the new list
        const updatedOrderItems = orderItems.map((item, index) => {
          if (index === Number(itemIndex)) {
            return {
              ...item,
              subKits: item.subKits.map((subKit, subKitIdx) => {
                if (subKitIdx === Number(subKitIndex)) {
                  return {
                    ...subKit,
                    kitProducts: newList.map((n, idx) => ({
                      ...n,
                      cartIndex: idx,
                    })),
                  };
                }
                return subKit;
              }),
            };
          }
          return item;
        });

        // Update state immutably
        newState = newState.setIn(["items"], fromJS(updatedOrderItems));
        return newState;
      },

      [ACTIONS.CHANGE_SUB_KIT_ORDER_INDEX]: (state, { payload }) => {
        const orderItems = state.getIn(["items"]).toJS();
        const [itemPart] = payload.draggableId.split("-");
        const [, itemIndex] = itemPart.split("_");
        const subKits = orderItems[Number(itemIndex)]?.subKits;
        const removed = subKits.find((_, index) => payload.source.index === index);
        const newList = subKits.filter((_, index) => payload.source.index !== index);
        newList.splice(payload.destination.index, 0, removed);

        orderItems[itemIndex].subKits = newList.map((n, index) => ({
          ...n,
          subKitIndex: index,
        }));

        return state.setIn(["items"], fromJS(orderItems));
      },

      [ACTIONS.CHANGE_PRODUCT_NAME]: (state, { payload: { itemIndex, value } }) =>
        state.setIn(["items", itemIndex, "name"], value),

      [ACTIONS.CHANGE_SUB_KIT_TITLE]: (state, { payload: { itemIndex, subKitIndex, value } }) =>
        state.setIn(["items", itemIndex, "subKits", subKitIndex, "title"], value),

      [ACTIONS.CHANGE_PRODUCT_NOTES]: (state, { payload: { itemIndex, value } }) =>
        state.setIn(["items", itemIndex, "notes"], value),

      [ACTIONS.CHANGE_PRODUCT_DESCRIPTION]: (state, { payload: { itemIndex, value } }) =>
        state.setIn(["items", itemIndex, "productDescription"], value),

      [ACTIONS.CHANGE_PRODUCT_KIT_ITEM_NAME]: (
        state,
        { payload: { itemIndex, cartIndex, value } }
      ) => state.setIn(["items", itemIndex, "productKitItems", cartIndex, "productName"], value),

      [ACTIONS.CHANGE_SUB_KIT_ITEM_NAME]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, value } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "productName"],
          value
        ),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_DESCRIPTION} fulfilled`]: (
        state,
        { payload: { itemIndex, value } }
      ) => state.setIn(["items", itemIndex, "productKitDescription"], value),

      [ACTIONS.CHANGE_PRODUCT_TYPE]: (state, { payload }) => {
        const { itemIndex, id, type, value } = payload;
        return state.setIn(["items", itemIndex, "types", id, type], value);
      },

      [ACTIONS.CHANGE_PRODUCT_KIT_ITEM_TYPE]: (state, { payload }) => {
        const { itemIndex, cartIndex, id, type, value } = payload;
        return state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "types", id, type],
          value
        );
      },

      [ACTIONS.CHANGE_SUB_KIT_ITEM_TYPE]: (state, { payload }) => {
        const { itemIndex, subKitIndex, cartIndex, id, type, value } = payload;
        return state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "types", id, type],
          value
        );
      },

      [ACTIONS.CHANGE_PRODUCT_KIT_ITEM_NOTES]: (
        state,
        { payload: { itemIndex, cartIndex, value } }
      ) => state.setIn(["items", itemIndex, "productKitItems", cartIndex, "notes"], value),

      [ACTIONS.CHANGE_SUB_KIT_ITEM_NOTES]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, value } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "notes"],
          value
        ),

      [ACTIONS.CHANGE_PRODUCT_KIT_ITEM_DESCRIPTION]: (
        state,
        { payload: { itemIndex, cartIndex, value } }
      ) =>
        state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "productDescription"],
          value
        ),

      [ACTIONS.CHANGE_SUB_KIT_ITEM_DESCRIPTION]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, value } }
      ) =>
        state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "productDescription",
          ],
          value
        ),

      [ACTIONS.CHANGE_PRODUCT_COMPLETED_STATUS]: (state, { payload: { itemIndex, value } }) =>
        state.setIn(["items", itemIndex, "isCompleted"], value),

      [ACTIONS.CHANGE_PRODUCT_KIT_ITEM_COMPLETED_STATUS]: (
        state,
        { payload: { itemIndex, cartIndex, status } }
      ) => state.setIn(["items", itemIndex, "productKitItems", cartIndex, "isCompleted"], status),

      [ACTIONS.CHANGE_SUB_KIT_ITEM_COMPLETED_STATUS]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, status } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "isCompleted"],
          status
        ),

      [ACTIONS.CHANGE_SUB_KIT_COMPLETED_STATUS]: (
        state,
        { payload: { itemIndex, subKitIndex, status } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "isCompleted"], status),

      [`${ACTIONS.CHANGE_PRICE_VALUE} fulfilled`]: withMutations(this.changeProductPriceValue),

      [`${ACTIONS.CHANGE_PRODUCT_KIT_ITEM_PRICE_VALUE} fulfilled`]: withMutations(
        this.changeProductKitItemPriceValue
      ),

      [`${ACTIONS.CHANGE_SUB_KIT_ITEM_PRICE_VALUE} fulfilled`]: withMutations(
        this.changeSubKitItemPriceValue
      ),

      [`${ACTIONS.CHANGE_SUB_KIT_PRICE_VALUE} fulfilled`]: withMutations(
        this.changeSubKitPriceValue
      ),

      [`${ACTIONS.CHANGE_CUSTOM_FORMULA_VALUES} fulfilled`]: withMutations(
        this.changeCustomFormulaValues
      ),
      [`${ACTIONS.CHANGE_ACTUAL_CUSTOM_FORMULA_VALUES} fulfilled`]: withMutations(
        this.changeActualCustomFormulaValues
      ),
      [`${ACTIONS.ADD_CUSTOM_FORMULA_VALUE} fulfilled`]: withMutations(this.addCustomFormulaValues),
      [`${ACTIONS.REMOVE_CUSTOM_FORMULA_VALUE} fulfilled`]: withMutations(
        this.removeCustomFormulaValues
      ),
      [ACTIONS.UPDATE_UID_OF_CUSTOM_FORMULA]: withMutations(this.updateUidOfCustomFormula),

      [`${ACTIONS.SAVE_PRODUCT_TO_CATALOGUE} fulfilled`]: withMutations(
        this.saveProductToCatalogue
      ),
      [`${ACTIONS.SAVE_PRODUCT_TO_CATALOGUE} pending`]: (
        state,
        { payload: { isSavingToProductCatalogue, id } }
      ) => state.setIn(["items", id, "isSavingToProductCatalogue"], isSavingToProductCatalogue),

      [`${ACTIONS.SAVE_PRODUCT_KIT_TO_CATALOGUE} fulfilled`]: withMutations(
        this.saveProductKitToCatalogue
      ),
      [`${ACTIONS.SAVE_PRODUCT_KIT_TO_CATALOGUE} pending`]: (
        state,
        { payload: { isSavingToProductCatalogue, id } }
      ) => state.setIn(["items", id, "isSavingToProductCatalogue"], isSavingToProductCatalogue),

      [`${ACTIONS.SAVE_KIT_ITEM_TO_CATALOGUE} fulfilled`]: withMutations(
        this.saveKitItemToCatalogue
      ),
      [`${ACTIONS.SAVE_KIT_ITEM_TO_CATALOGUE} pending`]: (
        state,
        { payload: { isSavingToProductCatalogue, itemIndex, cartIndex } }
      ) =>
        state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "isSavingToProductCatalogue"],
          isSavingToProductCatalogue
        ),

      [`${ACTIONS.SAVE_SUB_KIT_ITEM_TO_CATALOGUE} fulfilled`]: withMutations(
        this.saveSubKitItemToCatalogue
      ),
      [`${ACTIONS.SAVE_SUB_KIT_ITEM_TO_CATALOGUE} pending`]: (
        state,
        { payload: { isSavingToProductCatalogue, itemIndex, subKitIndex, cartIndex } }
      ) =>
        state.setIn(
          [
            "items",
            itemIndex,
            "subKits",
            subKitIndex,
            "kitProducts",
            cartIndex,
            "isSavingToProductCatalogue",
          ],
          isSavingToProductCatalogue
        ),

      [`${ACTIONS.ADD_ITEM_TO_KIT} fulfilled`]: (state, { payload: { itemIndex, items } }) =>
        state.setIn(["items", itemIndex, "productKitItems"], items),

      [`${ACTIONS.ADD_ON_THE_FLY_ITEM_TO_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, items } }
      ) => state.setIn(["items", itemIndex, "productKitItems"], items),

      [`${ACTIONS.ADD_LABOUR_LINE_ITEM_TO_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, items } }
      ) => state.setIn(["items", itemIndex, "productKitItems"], items),

      [`${ACTIONS.ADD_NOTES_ITEM_TO_KIT} fulfilled`]: (state, { payload: { itemIndex, items } }) =>
        state.setIn(["items", itemIndex, "productKitItems"], items),

      [`${ACTIONS.ADD_ITEM_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, items } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], items),

      [`${ACTIONS.ADD_ON_THE_FLY_ITEM_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, items } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], items),

      [`${ACTIONS.ADD_LABOUR_LINE_ITEM_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, items } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], items),

      [`${ACTIONS.ADD_NOTES_ITEM_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, items } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "kitProducts"], items),

      [`${ACTIONS.SELECT_XERO_TRACKING_CATEGORY} fulfilled`]: (
        state,
        { payload: { itemIndex, data } }
      ) => state.setIn(["items", itemIndex, "job"], data.id ? data : null),

      [`${ACTIONS.SELECT_XERO_TRACKING_CATEGORY_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, data } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "job"], data.id ? data : null),

      [`${ACTIONS.SELECT_XERO_TRACKING_CATEGORY_TO_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, data } }
      ) =>
        state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "job"],
          data.id ? data : null
        ),
      [`${ACTIONS.SELECT_XERO_TRACKING_CATEGORY_TO_SUB_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, data } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "job"],
          data.id ? data : null
        ),

      [`${ACTIONS.SELECT_MYOB_JOB} fulfilled`]: (state, { payload: { itemIndex, data } }) =>
        state.setIn(["items", itemIndex, "job"], data.id ? data : null),
      [`${ACTIONS.SELECT_MYOB_JOB_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, data } }
      ) => state.setIn(["items", itemIndex, "subKits", subKitIndex, "job"], data.id ? data : null),

      [`${ACTIONS.SELECT_MYOB_JOB_TO_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, data } }
      ) =>
        state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "job"],
          data.id ? data : null
        ),
      [`${ACTIONS.SELECT_MYOB_JOB_TO_SUB_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, data } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "job"],
          data.id ? data : null
        ),
      [`${ACTIONS.SELECT_MYOB_ACCOUNT} fulfilled`]: (state, { payload: { itemIndex, data } }) =>
        state.setIn(["items", itemIndex, "account"], data.id ? data : null),
      [`${ACTIONS.SELECT_MYOB_ACCOUNT_TO_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, cartIndex, data } }
      ) =>
        state.setIn(
          ["items", itemIndex, "productKitItems", cartIndex, "account"],
          data.id ? data : null
        ),
      [`${ACTIONS.SELECT_MYOB_ACCOUNT_TO_SUB_KIT} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, data } }
      ) =>
        state.setIn(["items", itemIndex, "subKits", subKitIndex, "account"], data.id ? data : null),
      [`${ACTIONS.SELECT_MYOB_ACCOUNT_TO_SUB_KIT_ITEM} fulfilled`]: (
        state,
        { payload: { itemIndex, subKitIndex, cartIndex, data } }
      ) =>
        state.setIn(
          ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex, "account"],
          data.id ? data : null
        ),
      [`${ACTIONS.UPDATE_PRODUCT_KIT_COST} fulfilled`]: (state, { payload: { itemIndex, cost } }) =>
        state.setIn(["items", itemIndex, "cost"], cost),
    };
  }
}

const instance = new AdditionalProducts();
instance.init();

export default instance;
